// TODO STUDIO MIGRATION (Phase 5 prep): middleware now returns { jobId, room } for TTS avatar dispatches. Migrate from setInterval polling to useJobSocket(jobId, onEvent) from _editor/utils/jobSocket.js. See aidea-middleware spec docs/superpowers/specs/2026-05-25-producer-worker-discipline-design.md §7.
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  LuMic,
  LuUser,
  LuPlay,
  LuPause,
  LuSparkles,
  LuVideo,
  LuImage,
  LuChevronDown,
  LuChevronUp,
  LuX,
  LuCheck,
  LuPlus,
  LuShuffle,
  LuTrash2,
} from "react-icons/lu";
import { usePopupPrefill } from "../ai-chat/aiPopupGateway";
import { randomAvatarIdentity } from "./avatarPresets";
import DeleteAvatarConfirmModal from "./DeleteAvatarConfirmModal";
import { isDeletableAvatar, deleteSavedAvatar } from "./avatarDelete";
// "Doğal konuşan video" (Beta) — second generation method, see talking_avatar/.
import TalkingMethodSelector from "./talking_avatar/TalkingMethodSelector";
import TalkingVideoPanel from "./talking_avatar/TalkingVideoPanel";
import TalkingJobView from "./talking_avatar/TalkingJobView";
import { resolveTalkingAvatarSource } from "./talking_avatar/talkingAvatarSource";
import { useTalkingAvatarJobForScene } from "./talking_avatar/useTalkingAvatarJob";
import { selectJobForScene } from "./talking_avatar/talkingAvatarJobStore";

// AI ile avatar üretme bölümündeki input/select için tek tip stil.
// Native select'in çirkin OS-temalı görünümünü gizleyip custom chevron koyuyoruz.
const AI_FIELD_BASE = {
  width: "100%",
  padding: "8px 12px",
  fontSize: "13px",
  lineHeight: "1.4",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  background: "#fff",
  color: "#1f2937",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
  transition: "border-color 0.15s, box-shadow 0.15s",
};
const AI_INPUT_STYLE = { ...AI_FIELD_BASE };
const AI_TEXTAREA_STYLE = { ...AI_FIELD_BASE, padding: "10px 12px", resize: "vertical" };
const focusField = (e) => {
  e.target.style.borderColor = "#7c3aed";
  e.target.style.boxShadow = "0 0 0 3px rgba(124, 58, 237, 0.12)";
};
const blurField = (e) => {
  e.target.style.borderColor = "#e2e8f0";
  e.target.style.boxShadow = "none";
};

// AI avatar properties dropdown opsiyonları. value alanı API'ye olduğu gibi
// gider (İngilizce); label kullanıcının diline göre i18n ile çevrilir.
const AI_PROVIDER_OPTIONS = [
  { value: "gemini-fast", labelKey: "AvatarAiProviderGeminiFast", fallback: "Gemini Fast" },
  { value: "grok", labelKey: "AvatarAiProviderGrok", fallback: "Grok" },
  { value: "gemini", labelKey: "AvatarAiProviderGemini", fallback: "Gemini" },
];
// Görsel stil — hem "prompt" hem "özel" modunda request payload'ına `style`
// olarak gider. "realistic" güvenli default.
// Değerler backend AiAvatarStyle enum'u ile birebir aynı olmalı.
const AI_STYLE_OPTIONS = [
  { value: "realistic", labelKey: "AvatarAiStyleRealistic", fallback: "Realistic" },
  { value: "cartoon", labelKey: "AvatarAiStyleCartoon", fallback: "Cartoon" },
  { value: "illustration", labelKey: "AvatarAiStyleIllustration", fallback: "Illustration" },
  { value: "animation", labelKey: "AvatarAiStyleAnimation", fallback: "Animated" },
  { value: "3d-animation", labelKey: "AvatarAiStyle3D", fallback: "3D Animation" },
  { value: "anime", labelKey: "AvatarAiStyleAnime", fallback: "Anime" },
];
// Avatar videosu en/boy oranı VARSAYILANI. Kullanıcı Gelişmiş Ayarlar'dan
// değiştirebilir (avatarAspectRatio state). Bu değer tüm üretim (azure / tavus)
// ve lipsync (processLipsync/v2) isteklerine gönderilir. Backend YALNIZCA
// "1:1" gelirse final videoyu merkezden kareye kırpar; "16:9" gelince crop
// atlanır ve provider'ın native (yatay) oranı korunur.
const AVATAR_ASPECT_RATIO = "1:1";
// Gelişmiş Ayarlar'daki en/boy oranı seçenekleri. value'lar backend'in beklediği
// oran string'leridir ("1:1" | "16:9").
const AVATAR_ASPECT_OPTIONS = [
  { value: "1:1", labelKey: "AvatarAspectSquare", fallback: "Kare (1:1)" },
  { value: "16:9", labelKey: "AvatarAspectWide", fallback: "Yatay (16:9)" },
];
// Poz — TOP-LEVEL payload alanı (parameters değil), hem prompt hem guide modunda
// geçerli. Geçerli değerler yalnızca "seated" | "standing"; başka değer backend'de
// 400 döner. Değer yoksa / "seated" → varsayılan oturan davranış.
const AI_POSE_OPTIONS = [
  { value: "seated", labelKey: "AvatarAiPoseSeated", fallback: "Seated" },
  { value: "standing", labelKey: "AvatarAiPoseStanding", fallback: "Standing" },
];
const AI_CATEGORY_OPTIONS = [
  { value: "educational", labelKey: "AvatarAiCategoryEducational", fallback: "Educational" },
  {
    value: "professional",
    labelKey: "AvatarAiCategoryProfessional",
    fallback: "Professional",
  },
  { value: "marketing", labelKey: "AvatarAiCategoryMarketing", fallback: "Marketing" },
  { value: "training", labelKey: "AvatarAiCategoryTraining", fallback: "Training" },
  {
    value: "presentation",
    labelKey: "AvatarAiCategoryPresentation",
    fallback: "Presentation",
  },
];
// Not: "teen" ve "young-adult" seçenekleri bilinçli olarak listede yok —
// provider'lar genç ifadelerde content filter'a takılıp üretimi reddediyor.
// Liste güvenli minimumdan ("adult") başlıyor.
const AI_AGE_OPTIONS = [
  { value: "adult", labelKey: "AvatarAiAgeAdult", fallback: "Adult (30–49)" },
  { value: "middle-aged", labelKey: "AvatarAiAgeMiddleAged", fallback: "Middle-aged (50–65)" },
  { value: "senior", labelKey: "AvatarAiAgeSenior", fallback: "Senior (65+)" },
];
// `color` alanı yalnızca UI'daki renk kutucuğu (swatch) için; payload'a value gider.
const AI_HAIR_COLOR_OPTIONS = [
  { value: "black", labelKey: "AvatarAiHairColorBlack", fallback: "Black", color: "#1c1c1c" },
  { value: "brown", labelKey: "AvatarAiHairColorBrown", fallback: "Brown", color: "#6b4423" },
  { value: "blonde", labelKey: "AvatarAiHairColorBlonde", fallback: "Blonde", color: "#e0bc6e" },
  { value: "red", labelKey: "AvatarAiHairColorRed", fallback: "Red / Auburn", color: "#9b3b1b" },
  { value: "gray", labelKey: "AvatarAiHairColorGray", fallback: "Gray", color: "#9ca3af" },
  { value: "white", labelKey: "AvatarAiHairColorWhite", fallback: "White", color: "#e5e7eb" },
];
const AI_HAIR_STYLE_OPTIONS = [
  { value: "short", labelKey: "AvatarAiHairStyleShort", fallback: "Short" },
  { value: "medium", labelKey: "AvatarAiHairStyleMedium", fallback: "Medium" },
  { value: "long", labelKey: "AvatarAiHairStyleLong", fallback: "Long" },
  { value: "curly", labelKey: "AvatarAiHairStyleCurly", fallback: "Curly" },
  { value: "wavy", labelKey: "AvatarAiHairStyleWavy", fallback: "Wavy" },
  { value: "straight", labelKey: "AvatarAiHairStyleStraight", fallback: "Straight" },
  { value: "ponytail", labelKey: "AvatarAiHairStylePonytail", fallback: "Ponytail" },
  { value: "bald", labelKey: "AvatarAiHairStyleBald", fallback: "Bald" },
];
const AI_EYE_COLOR_OPTIONS = [
  { value: "brown", labelKey: "AvatarAiEyeColorBrown", fallback: "Brown", color: "#6b4423" },
  { value: "blue", labelKey: "AvatarAiEyeColorBlue", fallback: "Blue", color: "#3b7dd8" },
  { value: "green", labelKey: "AvatarAiEyeColorGreen", fallback: "Green", color: "#4a8c5f" },
  { value: "hazel", labelKey: "AvatarAiEyeColorHazel", fallback: "Hazel", color: "#a6783f" },
  { value: "gray", labelKey: "AvatarAiEyeColorGray", fallback: "Gray", color: "#94a3b8" },
];
const AI_SKIN_TONE_OPTIONS = [
  { value: "fair", labelKey: "AvatarAiSkinFair", fallback: "Fair", color: "#f7d9bf" },
  { value: "light", labelKey: "AvatarAiSkinLight", fallback: "Light", color: "#f0c8a0" },
  { value: "medium", labelKey: "AvatarAiSkinMedium", fallback: "Medium", color: "#e0ac69" },
  { value: "olive", labelKey: "AvatarAiSkinOlive", fallback: "Olive", color: "#c99a5b" },
  { value: "tan", labelKey: "AvatarAiSkinTan", fallback: "Tan", color: "#a9744a" },
  { value: "dark", labelKey: "AvatarAiSkinDark", fallback: "Dark", color: "#80543a" },
  { value: "deep", labelKey: "AvatarAiSkinDeep", fallback: "Deep", color: "#563226" },
];
const AI_FACE_SHAPE_OPTIONS = [
  { value: "oval", labelKey: "AvatarAiFaceOval", fallback: "Oval" },
  { value: "round", labelKey: "AvatarAiFaceRound", fallback: "Round" },
  { value: "square", labelKey: "AvatarAiFaceSquare", fallback: "Square" },
  { value: "heart", labelKey: "AvatarAiFaceHeart", fallback: "Heart" },
  { value: "oblong", labelKey: "AvatarAiFaceOblong", fallback: "Oblong" },
  { value: "diamond", labelKey: "AvatarAiFaceDiamond", fallback: "Diamond" },
];
const AI_BODY_TYPE_OPTIONS = [
  { value: "slim", labelKey: "AvatarAiBodySlim", fallback: "Slim" },
  { value: "athletic", labelKey: "AvatarAiBodyAthletic", fallback: "Athletic" },
  { value: "average", labelKey: "AvatarAiBodyAverage", fallback: "Average" },
  { value: "curvy", labelKey: "AvatarAiBodyCurvy", fallback: "Curvy" },
  { value: "heavy", labelKey: "AvatarAiBodyHeavy", fallback: "Heavy" },
];
const AI_EXPRESSION_OPTIONS = [
  { value: "smile", labelKey: "AvatarAiExpressionSmile", fallback: "Smile" },
  { value: "neutral", labelKey: "AvatarAiExpressionNeutral", fallback: "Neutral" },
  { value: "serious", labelKey: "AvatarAiExpressionSerious", fallback: "Serious" },
  { value: "friendly", labelKey: "AvatarAiExpressionFriendly", fallback: "Friendly" },
  {
    value: "professional",
    labelKey: "AvatarAiExpressionProfessional",
    fallback: "Professional",
  },
];
// outfitStyle serbest/açıklayıcı metin olarak gider (style gibi katı enum değil),
// bu yüzden mockup'taki medical / safety-industrial / academic seçenekleri eklendi.
const AI_CLOTHING_OPTIONS = [
  { value: "business-suit", labelKey: "AvatarAiClothingBusinessSuit", fallback: "Business" },
  {
    value: "business-casual",
    labelKey: "AvatarAiClothingBusinessCasual",
    fallback: "Business casual",
  },
  { value: "casual", labelKey: "AvatarAiClothingCasual", fallback: "Casual" },
  { value: "medical", labelKey: "AvatarAiClothingMedical", fallback: "Medical" },
  {
    value: "safety-industrial",
    labelKey: "AvatarAiClothingSafetyIndustrial",
    fallback: "Safety / industrial",
  },
  { value: "academic", labelKey: "AvatarAiClothingAcademic", fallback: "Academic" },
  { value: "smart-casual", labelKey: "AvatarAiClothingSmartCasual", fallback: "Smart casual" },
  { value: "formal", labelKey: "AvatarAiClothingFormal", fallback: "Formal" },
  { value: "traditional", labelKey: "AvatarAiClothingTraditional", fallback: "Traditional" },
  { value: "athletic", labelKey: "AvatarAiClothingAthletic", fallback: "Athletic" },
];
// Aksesuarlar — çoklu seçim ("pick any"). Seçilenler payload'da parameters.accessories
// içine virgülle birleştirilerek gider. value alanları doğal kelimeler olduğundan
// payload'a olduğu gibi (label yerine value) yazılabilir.
const AI_ACCESSORY_OPTIONS = [
  { value: "glasses", labelKey: "AvatarAiAccessoryGlasses", fallback: "Glasses" },
  { value: "watch", labelKey: "AvatarAiAccessoryWatch", fallback: "Watch" },
  { value: "earrings", labelKey: "AvatarAiAccessoryEarrings", fallback: "Earrings" },
  { value: "tie", labelKey: "AvatarAiAccessoryTie", fallback: "Tie" },
  { value: "headscarf", labelKey: "AvatarAiAccessoryHeadscarf", fallback: "Headscarf" },
];
const AI_BACKGROUND_OPTIONS = [
  { value: "studio", labelKey: "AvatarAiBackgroundStudio", fallback: "Studio" },
  { value: "office", labelKey: "AvatarAiBackgroundOffice", fallback: "Office" },
  { value: "classroom", labelKey: "AvatarAiBackgroundClassroom", fallback: "Classroom" },
  { value: "gradient", labelKey: "AvatarAiBackgroundGradient", fallback: "Soft gradient" },
  { value: "outdoor", labelKey: "AvatarAiBackgroundOutdoor", fallback: "Outdoor" },
  { value: "plain", labelKey: "AvatarAiBackgroundPlain", fallback: "Plain / neutral" },
];
const AI_ENVIRONMENT_OPTIONS = [
  {
    value: "modern minimalist studio office",
    labelKey: "AvatarAiEnvModernStudio",
    fallback: "Modern minimalist studio",
  },
  {
    value: "corporate office",
    labelKey: "AvatarAiEnvCorporateOffice",
    fallback: "Corporate office",
  },
  { value: "home office", labelKey: "AvatarAiEnvHomeOffice", fallback: "Home office" },
  { value: "classroom", labelKey: "AvatarAiEnvClassroom", fallback: "Classroom" },
  { value: "library", labelKey: "AvatarAiEnvLibrary", fallback: "Library" },
  { value: "warehouse", labelKey: "AvatarAiEnvWarehouse", fallback: "Warehouse" },
  { value: "lab", labelKey: "AvatarAiEnvLab", fallback: "Lab" },
  { value: "cafe", labelKey: "AvatarAiEnvCafe", fallback: "Cafe" },
  {
    value: "outdoor urban",
    labelKey: "AvatarAiEnvOutdoorUrban",
    fallback: "Outdoor — urban",
  },
  {
    value: "outdoor nature",
    labelKey: "AvatarAiEnvOutdoorNature",
    fallback: "Outdoor — nature",
  },
];
// "Diğer" (Other) seçeneği için sentinel. Dropdown'da bu değer seçilince enum dışı
// serbest metin girişi açılır; gerçek değer aiCustom[field]'da tutulur. Payload
// kurulurken resolveAiField bu sentinel'i kullanıcının girdiği metinle değiştirir.
const AI_OTHER_VALUE = "__other__";
const resolveAiField = (value, custom) =>
  value === AI_OTHER_VALUE ? (custom || "").trim() : value;
// "Ses etiketleri" (Audio tags / SSML) toggle butonu şimdilik gizli. Kod ve
// SSML mantığı korunuyor; tekrar açmak için bu bayrağı true yapmak yeterli.
const SHOW_AUDIO_TAGS_BUTTON = false;
import i18n from "i18next";
import SceneAudioService from "../../services/sceneAudioService";
import { pickSampleLanguage } from "../../utils/sampleVoiceLanguage";

import { ID_PREFIX_FILE } from "../../../common/constants.es6";
import { withSidecarSuffix, credentialsFor } from "../../../common/utils.es6";
import { getToken, getOrganizationId } from "../../../common/environment_config.es6";
import { waitForTtsJob } from "../../../common/tts_job_poll.es6";
import {
  classifyAiProviderError,
  mapAiProviderErrorMessage,
} from "../../../common/ai_provider_errors.es6";
import { approveJob } from "../../../common/server_utils.es6";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";
import { getMediaDuration } from "../../utils/mediaDuration";
import getMediaBoxSize from "../../utils/getMediaBoxSize";
import { spinForSceneAdd } from "../../utils/sceneAddSpinner";
import { isCreditRefusal } from "../../utils/insufficientBalance";
import { pickContentLocale, getContentLanguage } from "../../../common/content_language";

const PROVIDER_OPTIONS = [
  { value: "aidea", label: "Aidea" },
  { value: "azure-video", label: "Azure" },
];

// "Otomatik" modunda kullanılan varsayılan model (AddImageModal'daki AUTO_PROVIDER karşılığı).
const AUTO_MODEL = "aidea";

const TTS_PROVIDERS = [
  { value: "gemini", label: "Google Gemini" },
  { value: "elevenlabs", label: "ElevenLabs" },
  { value: "azure", label: "Azure (Microsoft)" },
];

// "Metinden Oluştur" altındaki zengin ses seçici (AIVoiceTab ile aynı yapı):
// sağlayıcı/dil/cinsiyet filtreleri + örnekli (sample) ses kartları.
// Avatar tarafındaki sağlayıcı değerleri (azure | elevenlabs | gemini) `api/course/voices`
// endpoint'inin beklediği `service` paramına burada eşleniyor.
const COURSE_VOICES_SERVICE_MAP = {
  azure: "microsoft",
  elevenlabs: "elevenlabs",
  gemini: "gemini",
};
const VALID_TTS_PROVIDERS = ["azure", "elevenlabs", "gemini"];
// Seçili anlatıcı kartındaki sağlayıcı rozetinin etiketi.
const TTS_PROVIDER_LABELS = { azure: "Azure", elevenlabs: "ElevenLabs", gemini: "Google" };
// ElevenLabs model ailesi (AIVoiceTab ile aynı): v2_5 → turbo v2.5, v3 → eleven_v3.
// Seçim lipsync/önizleme isteğinde `elevenLabsModel` olarak backend'e gider.
const DEFAULT_ELEVENLABS_MODEL = "v3";
const normalizeElevenLabsModel = (m) => (m === "v2_5" ? "v2_5" : DEFAULT_ELEVENLABS_MODEL);
const ttsProviderLabel = (provider, model) =>
  provider === "elevenlabs"
    ? `ElevenLabs ${normalizeElevenLabsModel(model) === "v2_5" ? "2.5" : "3"}`
    : TTS_PROVIDER_LABELS[provider] || provider;

const normalizeLanguageCode = (lang) => {
  if (!lang) return "tr-TR";
  const l = lang.toLowerCase();
  if (l.startsWith("tr")) return "tr-TR";
  if (l === "en-gb" || l === "en-uk") return "en-GB";
  if (l.startsWith("en")) return "en-US";
  if (l.startsWith("es")) return "es-ES";
  if (l.startsWith("de")) return "de-DE";
  if (l.startsWith("fr")) return "fr-FR";
  if (l.startsWith("ja")) return "ja-JP";
  if (l.startsWith("zh")) return "zh-CN";
  if (l.startsWith("az")) return "az-AZ";
  if (l.startsWith("pt")) return "pt-BR";
  if (l.startsWith("ar")) return "ar-SA";
  return lang;
};

// Sağlayıcıya göre dil seçenekleri (AIVoiceTab getLanguageOptions ile aynı tablo).
const getTtsLanguageOptions = (provider) => {
  const languagesByProvider = {
    gemini: [
      { value: "ar-SA", label: i18n.t("LanguageNames.Arabic") || "Arabic" },
      { value: "de-DE", label: i18n.t("LanguageNames.German") || "German" },
      { value: "en-US", label: i18n.t("LanguageNames.English") || "English (US)" },
      { value: "es-ES", label: i18n.t("LanguageNames.Spanish") || "Spanish" },
      { value: "fr-FR", label: i18n.t("LanguageNames.French") || "French" },
      { value: "ja-JP", label: i18n.t("LanguageNames.Japanese") || "Japanese" },
      { value: "tr-TR", label: i18n.t("LanguageNames.Turkish") || "Turkish" },
      { value: "az-AZ", label: i18n.t("LanguageNames.Azerbaijani") || "Azerbaijani" },
    ],
    elevenlabs: [
      { value: "multilingual", label: i18n.t("LanguageNames.Multilingual") || "Multilingual" },
    ],
    azure: [
      { value: "de-DE", label: i18n.t("LanguageNames.German") || "German" },
      { value: "en-GB", label: i18n.t("LanguageNames.EnglishUK") || "English (UK)" },
      { value: "en-US", label: i18n.t("LanguageNames.English") || "English (US)" },
      { value: "es-ES", label: i18n.t("LanguageNames.Spanish") || "Spanish" },
      { value: "fr-FR", label: i18n.t("LanguageNames.French") || "French" },
      { value: "ja-JP", label: i18n.t("LanguageNames.Japanese") || "Japanese" },
      { value: "multilingual", label: i18n.t("LanguageNames.Multilingual") || "Multilingual" },
      { value: "pt-BR", label: i18n.t("LanguageNames.Portuguese") || "Portuguese (BR)" },
      { value: "tr-TR", label: i18n.t("LanguageNames.Turkish") || "Turkish" },
      { value: "az-AZ", label: i18n.t("LanguageNames.Azerbaijani") || "Azerbaijani" },
    ],
  };
  return languagesByProvider[provider] || [];
};

const PHOTO_ANIMATE_PROVIDERS = [
  { value: "gemini", label: "Google Gemini" },
  { value: "grok", label: "xAI Grok" },
];

// Aidea modeli için lipsync engine seçenekleri. UI iki seviyeli:
// 1) Otomatik / Özel butonları (lipsyncMode state'i)
// 2) Özel seçilirse pixverse / fal-ai / sync-lipsync açılır (lipsyncProvider state'i)
// Otomatik modda request'e lipsyncProvider gönderilmez → backend default (fal-ai).
// Backend ApiQuery enum (processLipsync/v2): 'fal-ai' | 'pixverse' | 'sync-lipsync' | 'heygen-lipsync'.
const LIPSYNC_PROVIDERS = [
  { value: "pixverse", label: "pixverse" },
  { value: "fal-ai", label: "fal-ai" },
  { value: "sync-lipsync", label: "Sync v3" },
];

const SERVICE_MAP = {
  microsoft: "azure",
  azure: "azure",
  elevenlabs: "elevenlabs",
  gemini: "google",
  google: "google",
};

// Kaydedilmiş ses / voiceProfile sağlayıcısını zengin ses seçicinin beklediği
// ttsProvider değerine (azure | elevenlabs | gemini) indirger. Numeric enum
// (0/1/2) ve "microsoft"/"google" gibi alias'ları da kapsar.
const normalizeTtsProvider = (provider) => {
  const v = String(provider ?? "")
    .toLowerCase()
    .trim();
  if (v === "azure" || v === "microsoft" || v === "0") return "azure";
  if (v === "elevenlabs" || v === "1") return "elevenlabs";
  if (v === "gemini" || v === "google" || v === "2") return "gemini";
  // Klonlanmış ses profilinin sağlayıcısı "private" olarak kaydedilmiş olabilir;
  // gerçekte ElevenLabs olduğundan "sesi koru" akışında da elevenlabs'a indir.
  if (v === "private" || v === "cloned") return "elevenlabs";
  return "";
};

// Ses cinsiyetini ("Male"/"female"/"M"/0 vb.) avatar gender butonlarının
// beklediği "male" | "female" değerine indirger; tanınmazsa "" döner.
const normalizeGender = (g) => {
  const v = String(g ?? "")
    .toLowerCase()
    .trim();
  if (v === "male" || v === "m" || v === "0" || v === "erkek") return "male";
  if (v === "female" || v === "f" || v === "1" || v === "kadın" || v === "kadin") return "female";
  return "";
};

// Ses kartı / seçili anlatıcı avatarının cinsiyete göre rengi. Önceki tasarımda
// her zaman pembe (kadın) gösteriliyordu; artık erkek için mavi, kadın için pembe,
// bilinmeyen için nötr mor döner. bg = kutucuk zemini, color = harf/ikon rengi.
const getGenderColors = (gender) => {
  const g = normalizeGender(gender);
  if (g === "male") return { bg: "#e0f2fe", color: "#2563eb" };
  if (g === "female") return { bg: "#ffe4f0", color: "#db2777" };
  return { bg: "#ede9fe", color: "#7c3aed" };
};
const genderAvatarStyle = (gender) => {
  const c = getGenderColors(gender);
  return { background: c.bg, color: c.color };
};

// Bir ses objesinin sağlayıcısını (azure | elevenlabs | gemini) service/servicename
// alanından türetir. "Tümü" (All) filtresinde seçilen sesin gerçek sağlayıcısını
// ttsProvider'a yazmak için kullanılır — üretim bu provider ile yapıldığından şart.
const voiceToProvider = (voice) => {
  const raw = String(voice?.service ?? voice?.provider ?? voice?.servicename ?? "").toLowerCase();
  if (raw.includes("eleven")) return "elevenlabs";
  if (raw.includes("google") || raw.includes("gemini")) return "gemini";
  if (raw.includes("azure") || raw.includes("microsoft")) return "azure";
  // Klonlanmış (private) sesler ElevenLabs ile üretilir; backend bu sesleri
  // providerName "private" olarak sakladığından servicename "private" gelir.
  // "Tümü" filtresinde bu sesi seçince ttsProvider'ı doğru sağlayıcıya (elevenlabs)
  // ayarlamak şart — aksi halde ttsProvider boş kalır ve "Avatar Oluştur" pasif olur.
  if (raw.includes("private") || raw.includes("clone")) return "elevenlabs";
  return "";
};

// Tahmini konuşma süresi (AIVoiceTab ile aynı model): sağlayıcıya göre karakter/sn
// hızı + cümle başına duraklama. "Yeni ses üret" modunda footer'daki ~süre için.
const PROVIDER_SPEECH_RATES = {
  azure: { minCps: 13.5, maxCps: 16.5, pausePerSentence: 0.35 },
  elevenlabs: { minCps: 12.0, maxCps: 15.0, pausePerSentence: 0.5 },
  gemini: { minCps: 12.5, maxCps: 15.5, pausePerSentence: 0.45 },
};
// "5 sn üret" önizlemesi için sağlayıcıya göre hedef karakter sayısı. Her
// sağlayıcının gerçek konuşma hızı farklı olduğundan ortak bir formül (avgCps)
// farklı süreler üretiyordu: aynı ~84-94 karakter azure'da ~7 sn, gemini'de
// ~6 sn, elevenlabs'te ~5 sn oluyordu. Aşağıdaki değerler ampirik olarak üçünü
// de ~5 sn'ye getirir (elevenlabs zaten doğruydu, isteğe göre +8 karakter).
const SHORT_PREVIEW_TARGET_CHARS = {
  azure: 67,
  elevenlabs: 92,
  gemini: 73,
};
const SHORT_PREVIEW_DEFAULT_CHARS = 84;
const estimateSpeechDuration = (text, providerKey) => {
  if (!text || !text.trim()) return 0;
  const conf = PROVIDER_SPEECH_RATES[providerKey] || PROVIDER_SPEECH_RATES.azure;
  const cleaned = text.replace(/<[^>]+>/g, "").trim();
  const charCount = cleaned.length;
  if (charCount === 0) return 0;
  const sentenceCount = (cleaned.match(/[.!?…]+/g) || []).length || 1;
  const pauseTotal = sentenceCount * conf.pausePerSentence;
  const avgCps = (conf.minCps + conf.maxCps) / 2;
  const estSec = charCount / avgCps + pauseTotal * 0.8;
  return Math.max(1, Math.round(estSec));
};

const AvatarNarrationTab = React.forwardRef(
  (
    {
      onElementSelected,
      onServerModal,
      navItemSelected,
      navItemsIds,
      voiceProfile,
      soundList,
      boxes,
      navItems,
      containedViews,
      containedViewSelected,
      boxSelected,
      pluginToolbars,
      onBoxAdded,
      onClose,
      onViewChange,
      onProcessingChange,
      onInsufficientBalance,
      onRenderSecondsChange,
      onCanCreateChange,
      onCreditCheckReady,
      onNavItemSelected,
    },
    ref
  ) => {
    // Detects a credit refusal (org balance or team budget) in any avatar/TTS API error body.
    // Returns true when handled so the caller can short-circuit (skip throw / generic error modal).
    const checkInsufficientBalance = useCallback(
      (errData) => {
        if (!isCreditRefusal(errData)) return false;
        if (typeof onInsufficientBalance === "function") {
          onInsufficientBalance(errData);
        }
        return true;
      },
      [onInsufficientBalance]
    );
    // VoiceProfile bilgilerini normalize et (useMemo ile stabilize et)
    const profileVoiceName = useMemo(
      () => voiceProfile?.voiceDetail?.name || voiceProfile?.voiceName || "",
      [voiceProfile]
    );
    const profileVoiceId = useMemo(
      () => voiceProfile?.voiceDetail?.id || voiceProfile?.voiceName || "",
      [voiceProfile]
    );
    const profileService = useMemo(
      () =>
        SERVICE_MAP[voiceProfile?.voiceServiceProvider] || voiceProfile?.voiceServiceProvider || "",
      [voiceProfile]
    );
    const profileLanguage = useMemo(() => voiceProfile?.voiceLanguage || "", [voiceProfile]);

    // ── Orijinal UI state ─────────────────────────────────────────────────────
    const [selectedVoice, setSelectedVoice] = useState("different"); // Azure başlangıç modeli için "different"
    const [selectedAvatarType, setSelectedAvatarType] = useState("stock");
    // Provider artık her zaman Aidea; Auto/Custom toggle kaldırıldı (Azure UI'dan çıktı).
    const [selectedModel] = useState("aidea");
    // Üretim yöntemi: "lipsync" (mevcut akış) | "talking" (Doğal konuşan video, Beta).
    // Talking modunda Adım 2 = TalkingVideoPanel; footer raporları onun durumundan.
    // Sahnede bir iş varsa (yeniden açılış / reload) doğrudan talking ile başla.
    const [generationMethod, setGenerationMethod] = useState(() =>
      selectJobForScene(navItemSelected) ? "talking" : "lipsync"
    );
    const isTalking = generationMethod === "talking";
    const talkingPanelRef = useRef(null);
    const [talkingStatus, setTalkingStatus] = useState({
      canCreate: false,
      seconds: null,
      submitting: false,
      checkCredit: null,
    });
    const handleTalkingStatus = useCallback(
      (next) =>
        setTalkingStatus((prev) =>
          Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next
        ),
      []
    );
    // ── Aşamalı (accordion) akış bayrakları ───────────────────────────────────
    // Kaynak (Library / Create-your-own) aktif olarak seçildi mi → seçilince diğer
    // kart gizlenir, alttaki içerik açılır.
    const [sourceChosen, setSourceChosen] = useState(false);
    // Kullanıcı cinsiyete ELLE bastı mı → basana kadar "Üretim yöntemi" kilitli.
    // (genderTouchedRef bir ref olduğu için re-render tetiklemez; gating için state şart.)
    const [genderChosen, setGenderChosen] = useState(false);
    // Bir üretim yöntemi (Prompt/Guide/Kendi medyan) seçildi mi → seçilene kadar
    // alt panel (prompt/properties/upload) açılmaz.
    const [methodChosen, setMethodChosen] = useState(false);
    // Galeri kartında inline oynatılan avatarın id'si (sağ üst play butonu).
    const [playingAvatarId, setPlayingAvatarId] = useState(null);
    // İsim kullanıcı tarafından elle düzenlendi mi → düzenlendiyse gender değişince
    // otomatik re-roll ile ezmeyiz.
    const nameEditedRef = useRef(false);
    // Persona üretilince "Bu avatarı kullan" ile Adım 2'ye kaydırmak için ref.
    const narrationStepRef = useRef(null);
    const [avatarName, setAvatarName] = useState("");
    const [avatarRole, setAvatarRole] = useState("");
    const [gender, setGender] = useState("male");
    // Kullanıcı cinsiyet butonuna elle dokunduğunda true olur; sonrasında sahne
    // sesinden gelen otomatik ön-seçim onun tercihini ezmez.
    const genderTouchedRef = useRef(false);
    // /api/tts-avatar/ai-create payload'unda gönderilecek `category` alanı.
    // Sadece AI üretim akışında kullanılıyor; from-video/from-image için ignore.
    const [avatarCategory, setAvatarCategory] = useState("educational");
    // AI üretim sağlayıcısı — payload.provider olarak gider
    const [aiProvider, setAiProvider] = useState("gemini-fast"); // "gemini-fast" | "grok" | "gemini"
    // ai-create artık her zaman 202 + jobId döndürüyor. İş tamamlanana kadar
    // aiJobInfo set; tamamlanınca aiResult'a geçilip preview gösteriliyor.
    const [aiJobInfo, setAiJobInfo] = useState(null);
    // { jobId, progress, stage, placeholderUrl, message }
    const [aiResult, setAiResult] = useState(null);
    // { videoUrl, refinedPrompt, provider, lipsyncAvatarId, metadata, threadId }
    const [sourcePreview, setSourcePreview] = useState("");
    const [sourceSize, setSourceSize] = useState("");
    const [narrationText, setNarrationText] = useState("");
    // Metin sekmesi ilk açılışta sahneden anlatım metnini asenkron çekiyor; bu
    // sürede "AI Sesinden metin kullan" butonu yanıp sönmesin diye gizli tutulur.
    const [sceneTextLoading, setSceneTextLoading] = useState(false);
    const [transparentBg, setTransparentBg] = useState(true);
    // Avatar videosu en/boy oranı — Gelişmiş Ayarlar'dan seçilir. Varsayılan kare
    // (1:1); "16:9" seçilirse backend crop'u atlar, native yatay oran korunur.
    const [avatarAspectRatio, setAvatarAspectRatio] = useState(AVATAR_ASPECT_RATIO);
    // "Sahne Sesinden Oluştur" modunda yalnızca GÖSTERİM amaçlı (read-only) metin.
    // selectedSceneAudio alanları boşsa loadSceneNarrationText ile API'den çekilir.
    const [sceneAudioText, setSceneAudioText] = useState("");

    // ── Anlatım kaynağı seçimi ────────────────────────────────────────────────
    // İlk açılış varsayılanı "sceneAudio" → "Sahne Sesinden Oluştur" seçili gelir.
    const [narrationSource, setNarrationSource] = useState("sceneAudio"); // "text" | "sceneAudio"
    // Aşamalı akış: kullanıcı Adım 2'de bir anlatım kaynağı kartına (Sahne sesi /
    // Yeni ses üret) ELLE bastı mı → basana kadar hiçbiri seçili görünmez ve alt
    // içerik açılmaz. (narrationSource fonksiyonel default'u "sceneAudio" kalır;
    // bu bayrak yalnızca highlight + reveal'ı gate eder.)
    const [narrationChosen, setNarrationChosen] = useState(false);
    const [selectedSceneAudio, setSelectedSceneAudio] = useState(null);

    // Chat agent → "createAvatar" / "suggestAvatarFeatures": prefill the narration
    // text (the avatar speaks it) and, when the suggest-features card supplies a
    // `settings` envelope, flip to AI-generate mode and pre-fill the structured
    // "properties" form. Each setter applies only when its value is present
    // (forward-compatible; unknown settings ignored). The user still reviews +
    // clicks Generate. Best-effort.
    // VERIFY-LIVE: confirm sourceType:'ai' + aiMode:'properties' + aiProperties prefill renders the structured form filled
    const applyAvatarIntent = useCallback(({ prompt, settings }) => {
      if (typeof prompt === "string" && prompt.trim()) {
        setNarrationSource("text");
        setNarrationChosen(true);
        setNarrationText(prompt);
      }
      if (settings && settings.method === "talking") setGenerationMethod("talking");
      if (settings && typeof settings === "object") {
        setSourceType("ai"); // ensure AI-generate mode
        if (settings.provider) setAiProvider(settings.provider);
        if (settings.style) setAiStyle(settings.style);
        if (settings.category) setAvatarCategory(settings.category);
        if (settings.gender) setGender(settings.gender);
        if (settings.mode) setAiMode(settings.mode); // 'properties'
        if (settings.appearance) setAiAppearance((p) => ({ ...p, ...settings.appearance }));
        if (settings.properties) setAiProperties((p) => ({ ...p, ...settings.properties }));
      }
    }, []);
    usePopupPrefill("avatar", applyAvatarIntent);

    // ── Gerçek avatar state ───────────────────────────────────────────────────
    const [avatars, setAvatars] = useState([]);
    const [isLoadingAvatars, setIsLoadingAvatars] = useState(false);
    const [selectedStockAvatar, setSelectedStockAvatar] = useState(null);
    // Galeri kartındaki çöp kutusu → onay penceresi açık olan avatar + silme sürüyor mu.
    const [avatarToDelete, setAvatarToDelete] = useState(null);
    const [isDeletingAvatar, setIsDeletingAvatar] = useState(false);
    const deletingAvatarRef = useRef(false); // çift submit kilidi (state render'ı beklemeden)
    const [showAllAvatars, setShowAllAvatars] = useState(false);
    // Stock galerisi için kategori filtresi (mockup'taki All / Business / Casual / Coach
    // çipleri). avatarCategory (AI üretim alanı) ile karıştırılmamalı.
    const [avatarFilter, setAvatarFilter] = useState("all");
    // Kendi medyanla oluşturulan avatar — liste yenilenince otomatik seçilir.
    const pendingNewAvatarRef = useRef(null);

    // ── Azure ses state ───────────────────────────────────────────────────────
    const [allVoices, setAllVoices] = useState([]);
    const [voices, setVoices] = useState([]);
    const [selectedVoiceId, setSelectedVoiceId] = useState("");
    const [selectedLanguage, setSelectedLanguage] = useState(() =>
      pickContentLocale(["tr-TR", "en-US"], "tr-TR")
    );
    const [isLoadingVoices, setIsLoadingVoices] = useState(false);

    // ── Aidea TTS ses state ───────────────────────────────────────────────────
    const [ttsProvider, setTtsProvider] = useState("");
    // ElevenLabs model seçimi ('v2_5' | 'v3'); kaydedilmiş ses / course voice-profile'dan başlar.
    const [ttsElevenLabsModel, setTtsElevenLabsModel] = useState(() =>
      normalizeElevenLabsModel(voiceProfile?.elevenLabsModel)
    );
    const [ttsVoices, setTtsVoices] = useState([]);
    const [ttsSelectedVoice, setTtsSelectedVoice] = useState(null);
    const [isLoadingTTSVoices, setIsLoadingTTSVoices] = useState(false);
    // Zengin ses seçici (AIVoiceTab yapısı): dil/cinsiyet filtreleri + sayfalama + sample.
    const [ttsLanguage, setTtsLanguage] = useState("");
    const [ttsGender, setTtsGender] = useState("");
    const [ttsCurrentPage, setTtsCurrentPage] = useState(1);
    const [ttsTotalPages, setTtsTotalPages] = useState(1);
    // Toplam ses sayısı ("9 / 48 ses" göstergesi için). Bilinmiyorsa null.
    const [ttsTotalVoices, setTtsTotalVoices] = useState(null);
    const [ttsSamplePlayingId, setTtsSamplePlayingId] = useState(null);
    const ttsSampleAudioRef = useRef(null);
    // Anlatıcı seçim panelinin açık/kapalı durumu (AIVoiceTab'daki profileOpen gibi).
    const [voiceSelectOpen, setVoiceSelectOpen] = useState(false);

    // ── Video yükleme (custom avatar) ─────────────────────────────────────────
    const [uploadedVideo, setUploadedVideo] = useState(null);
    const [uploadedVideoUrl, setUploadedVideoUrl] = useState(null);
    const [uploadedVideoFileUrl, setUploadedVideoFileUrl] = useState(null);
    const [uploadingVideo, setUploadingVideo] = useState(false);

    // ── Video kayıt (webcam) ──────────────────────────────────────────────────
    const [showVideoCamera, setShowVideoCamera] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const videoRecordRef = useRef(null);
    const videoStreamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const pendingVideoRef = useRef(null); // closure race condition için

    // ── Kaynak tipi seçimi (video / photo / ai) ──────────────────────────────
    const [sourceType, setSourceType] = useState("video"); // "video" | "photo" | "ai"
    const [photoAnimateProvider, setPhotoAnimateProvider] = useState("gemini");

    // ── AI ile avatar oluşturma ──────────────────────────────────────────────
    // İki bağımsız alt-sekme: "prompt" doğrudan metinle, "properties" yapılandırılmış
    // alanlarla. Aktif sekmedeki veri request'e gider (diğeri yok sayılır).
    const [aiMode, setAiMode] = useState("prompt"); // "prompt" | "properties"
    const [aiStyle, setAiStyle] = useState("realistic"); // bkz. AI_STYLE_OPTIONS
    // Poz — top-level payload alanı, iki modda da geçerli. "seated" | "standing".
    const [aiPose, setAiPose] = useState("seated");
    const [aiPrompt, setAiPrompt] = useState("");
    // Anahtarlar /api/tts-avatar/ai-create endpoint'inin "parameters" şemasıyla
    // birebir aynı; payload doğrudan bu objeden türetiliyor.
    // Not: faceShape / bodyType / background / environment formdan kaldırıldı
    // (sensitivity filter ve gereksizlik nedeniyle).
    const [aiProperties, setAiProperties] = useState({
      ageRange: "",
      hairColor: "",
      hairStyle: "",
      eyeColor: "",
      expression: "",
      outfitStyle: "",
      background: "",
      additionalDetails: "",
    });
    // Aksesuarlar çoklu seçim olduğu için aiProperties dışında ayrı bir dizide
    // tutulur (enum value'ları). Payload kurulurken aiCustom.accessories serbest
    // metniyle birleştirilip parameters.accessories'e yazılır.
    const [aiAccessories, setAiAccessories] = useState([]);
    const toggleAiAccessory = useCallback((val) => {
      setAiAccessories((prev) =>
        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
      );
    }, []);
    // ai-create payload'unda TOP-LEVEL (parameters değil) gönderilen görünüm
    // alanları. Hem "prompt" hem "parameters" modunda geçerli; boş olanlar
    // payload'dan elenir. skinTone enum (AI_SKIN_TONE_OPTIONS), ethnicity ve
    // facialFeatures serbest metin.
    const [aiAppearance, setAiAppearance] = useState({
      ethnicity: "",
      skinTone: "",
      facialFeatures: "",
    });
    // Dropdown'larda "Diğer" seçilince girilen serbest metinler. Anahtarlar ilgili
    // alan adıyla birebir aynı (aiProperties alanları + aiAppearance.skinTone).
    // resolveAiField payload'ı kurarken enum değerinin yerine bu metni koyar.
    const [aiCustom, setAiCustom] = useState({
      style: "",
      skinTone: "",
      ageRange: "",
      hairColor: "",
      hairStyle: "",
      eyeColor: "",
      expression: "",
      outfitStyle: "",
      background: "",
      accessories: "",
    });
    // Aidea lipsync engine — iki seviyeli kontrol:
    //   lipsyncMode: "auto" | "custom" (default "auto" → request'e gönderme)
    //   lipsyncProvider: "pixverse" | "fal-ai" (sadece "custom" modunda anlamlı)
    const [lipsyncMode, setLipsyncMode] = useState("auto");
    const [lipsyncProvider, setLipsyncProvider] = useState("pixverse");

    // ── Fotoğraf yükleme (custom avatar) ──────────────────────────────────────
    const [uploadedPhoto, setUploadedPhoto] = useState(null);
    const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState(null);
    const [uploadedPhotoFileUrl, setUploadedPhotoFileUrl] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [sourcePhotoPreview, setSourcePhotoPreview] = useState("");
    const [sourcePhotoSize, setSourcePhotoSize] = useState("");
    const photoInputRef = useRef(null);
    const [showCamera, setShowCamera] = useState(false);
    const cameraVideoRef = useRef(null);
    const cameraStreamRef = useRef(null);

    // ── Ses yükleme (keep voice modu) ────────────────────────────────────────
    const [uploadedAudio, setUploadedAudio] = useState(null);
    const [uploadedAudioUrl, setUploadedAudioUrl] = useState(null);
    const [uploadingAudio, setUploadingAudio] = useState(false);

    // ── Job / polling ─────────────────────────────────────────────────────────
    const [processing, setProcessing] = useState(false);
    const [polling, setPolling] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [videoUrl, setVideoUrl] = useState("");
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    const [currentView, setCurrentView] = useState("form"); // "form" | "result"
    const [generatedVideoFileName, setGeneratedVideoFileName] = useState("");
    const [generatedVideoFileId, setGeneratedVideoFileId] = useState("");

    const pollInterval = useRef(null);
    const avatarPollInterval = useRef(null);
    const [avatarCreating, setAvatarCreating] = useState(false);
    const [avatarJobId, setAvatarJobId] = useState(null);

    // ── SSML toggle state ─────────────────────────────────────────────────────
    const [showSSML, setShowSSML] = useState(false);

    // ── Advanced settings (lipsync / engine / azure ses / keep ses bilgisi) açık mı ──
    const [advancedOpen, setAdvancedOpen] = useState(false);

    // ── Audio player state ────────────────────────────────────────────────────
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);

    // ── "Ses önizlemesi üret" state'i (Yeni ses üret modu) ─────────────────────
    // Buton gerçek TTS API'sine (api/course/text-to-speech) gider; iş async
    // tamamlandığında buton yerini oynat/ilerleme çubuklu mini bir oynatıcıya bırakır.
    const previewAudioRef = useRef(null);
    const previewMenuRef = useRef(null);
    const [previewGenerating, setPreviewGenerating] = useState(false);
    // "Ses önizlemesi üret" butonuna tıklayınca açılan "Tamamını üret / 5 sn üret" menüsü.
    const [previewMenuOpen, setPreviewMenuOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    // Üretilen önizlemenin modu: "full" → metnin tamamı, avatar bu sesle oluşturulur;
    // "short" → ~5 sn'lik kısaltılmış önizleme, SADECE dinleme amaçlı (avatar üretiminde
    // kullanılmaz, sunucu metnin tamamından yeniden üretir).
    const [previewMode, setPreviewMode] = useState("full");
    const [previewPlaying, setPreviewPlaying] = useState(false);
    const [previewProgress, setPreviewProgress] = useState(0);
    const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
    const [previewDuration, setPreviewDuration] = useState(0);

    // Resolves the selected scene audio's length in seconds — preferring the
    // audio metadata object, then the live <audio> element, then local state.
    // Used to attach `durationSeconds` to backend requests so the API doesn't
    // need to probe the file itself.
    const getSelectedSceneAudioDuration = useCallback(() => {
      const fromObj = selectedSceneAudio?.durationSeconds ?? selectedSceneAudio?.duration ?? null;
      if (fromObj != null && !isNaN(Number(fromObj)) && Number(fromObj) > 0) {
        return Math.round(Number(fromObj));
      }
      const fromAudio = audioRef.current?.duration;
      if (fromAudio && !isNaN(fromAudio) && fromAudio > 0) return Math.round(fromAudio);
      if (duration > 0) return Math.round(duration);
      return null;
    }, [selectedSceneAudio, duration]);

    // Footer'daki "~süre" için tahmini/gerçek saniye:
    //  • Sahne sesi modunda  → seçili sahne sesinin gerçek süresi
    //  • Yeni ses üret modunda → "full" önizleme üretildiyse onun gerçek süresi,
    //    yoksa (veya "short"/5 sn önizlemesi varsa) metnin tamamından tahmin.
    //    "short" önizleme avatar üretiminde kullanılmadığı için süresi de gösterilmez.
    const renderSeconds = useMemo(() => {
      if (narrationSource === "sceneAudio") {
        return getSelectedSceneAudioDuration();
      }
      if (previewMode === "full" && previewDuration > 0) return Math.round(previewDuration);
      const est = estimateSpeechDuration(narrationText, ttsProvider || "azure");
      return est > 0 ? est : null;
    }, [
      narrationSource,
      getSelectedSceneAudioDuration,
      previewMode,
      previewDuration,
      narrationText,
      ttsProvider,
    ]);

    // ── Doğal konuşan video: adım-1 seçimi → avatar payload'u; sahnenin işi ──
    const talkingSource = useMemo(
      () =>
        resolveTalkingAvatarSource({
          selectedAvatarType,
          selectedStockAvatar,
          aiResult,
          sourceType,
          uploadedVideo,
          uploadedVideoFileUrl,
          uploadingVideo,
          uploadedPhoto,
          uploadedPhotoFileUrl,
          uploadingPhoto,
        }),
      [
        selectedAvatarType,
        selectedStockAvatar,
        aiResult,
        sourceType,
        uploadedVideo,
        uploadedVideoFileUrl,
        uploadingVideo,
        uploadedPhoto,
        uploadedPhotoFileUrl,
        uploadingPhoto,
      ]
    );
    const { job: talkingJob } = useTalkingAvatarJobForScene(navItemSelected);
    // "Test üretim (kredisiz)": the fake result shows the chosen avatar's own video.
    const talkingTestVideoUrl =
      (talkingSource.payload && talkingSource.payload.videoUrl) ||
      aiResult?.videoUrl ||
      selectedStockAvatar?.videoUrl ||
      uploadedVideoFileUrl ||
      null;
    // Sahnede süren/biten bir iş varsa (modal yeniden açıldı, sayfa yenilendi)
    // doğrudan o işi göster.
    const talkingJobId = talkingJob ? talkingJob.jobId : null;
    useEffect(() => {
      if (talkingJobId) setGenerationMethod("talking");
    }, [talkingJobId]);

    // Süren doğal konuşan video işi: lip-sync gibi footer görünür kalır
    // ("Oluşturuluyor…" pasif); sonuç/hata ekranında footer gizlenir.
    const talkingRunning =
      isTalking &&
      !!talkingJob &&
      talkingJob.status !== "completed" &&
      talkingJob.status !== "failed";

    // currentView değiştiğinde parent'ı bilgilendir
    useEffect(() => {
      onViewChange?.(isTalking && talkingJob && !talkingRunning ? "talking-job" : currentView);
    }, [currentView, onViewChange, isTalking, talkingJob, talkingRunning]);

    // polling/processing değiştiğinde parent'ı bilgilendir
    useEffect(() => {
      onProcessingChange?.(
        polling || processing || (isTalking && talkingStatus.submitting) || talkingRunning
      );
    }, [
      polling,
      processing,
      isTalking,
      talkingStatus.submitting,
      talkingRunning,
      onProcessingChange,
    ]);

    // Hesaplanan süreyi footer için parent'a bildir
    useEffect(() => {
      onRenderSecondsChange?.(isTalking ? talkingStatus.seconds : renderSeconds);
    }, [renderSeconds, isTalking, talkingStatus.seconds, onRenderSecondsChange]);

    // Avatar seçildi/üretildi mi? Hem accordion 2. adımını (Ses & Anlatım) açmak
    // için hem de canCreate içinde kullanılır — TEK tanım, drift olmasın.
    // Custom'da: aiResult effect'i (aşağıda) üretilen avatarı selectedStockAvatar'a
    // yazar; yüklenen video/foto da geçerli sayılır.
    const avatarReady = useMemo(() => {
      if (selectedAvatarType === "stock") return !!selectedStockAvatar;
      if (selectedAvatarType === "custom")
        return !!selectedStockAvatar?.videoUrl || !!uploadedVideo || !!uploadedPhoto;
      return false;
    }, [selectedAvatarType, selectedStockAvatar, uploadedVideo, uploadedPhoto]);

    // Footer "Avatar Oluştur" butonu için: tüm gerekli seçimler tamam mı?
    // handleCreate'teki erken-dönüş (uyarı) koşullarının birebir karşılığıdır;
    // böylece buton tam olarak handleCreate'in başarılı olacağı durumda aktifleşir.
    const canCreate = useMemo(() => {
      // Anlatım kaynağı
      if (narrationSource === "text") {
        if (!narrationText.trim()) return false;
        if (selectedModel === "tavus" && narrationText.trim().length < 45) return false;
      }
      if (narrationSource === "sceneAudio" && !selectedSceneAudio) return false;

      // Avatar seçimi (avatarReady = stock seçili || custom üretildi/yüklendi)
      if (!avatarReady) return false;

      // Ses / voice gereksinimleri
      if (selectedModel === "azure-video") {
        if (!selectedVoiceId) return false;
      } else if (selectedModel === "aidea" || selectedModel === "tavus") {
        // Sahne sesi ya da manuel yüklenen ses varsa ayrıca anlatıcı seçimi gerekmez
        if (narrationSource !== "sceneAudio" && !uploadedAudio) {
          if (selectedVoice === "keep") {
            if (!voiceProfile || !profileService || !profileVoiceName) return false;
          } else if (!ttsProvider || !ttsSelectedVoice) {
            return false;
          }
        }
      }
      return true;
    }, [
      narrationSource,
      narrationText,
      selectedModel,
      selectedSceneAudio,
      avatarReady,
      selectedVoiceId,
      uploadedAudio,
      selectedVoice,
      voiceProfile,
      profileService,
      profileVoiceName,
      ttsProvider,
      ttsSelectedVoice,
    ]);

    // canCreate durumunu footer butonu için parent'a bildir
    useEffect(() => {
      onCanCreateChange?.(isTalking ? talkingStatus.canCreate : canCreate);
    }, [canCreate, isTalking, talkingStatus.canCreate, onCanCreateChange]);

    // Builds the credit-estimate request using current form state and registers
    // the function with the parent modal via onCreditCheckReady.
    const checkCredit = useCallback(async () => {
      const resolvedProvider = lipsyncMode === "custom" ? lipsyncProvider : "pixverse";
      const sceneAudioDuration = getSelectedSceneAudioDuration();
      const apiUrl = process.env.MW_API_URL;
      const body = {
        lipsyncProvider: resolvedProvider,
        removeBackground: transparentBg ? "true" : "false",
        ...(sceneAudioDuration > 0 ? { durationSeconds: sceneAudioDuration } : {}),
      };
      const res = await fetch(`${apiUrl}api/tts-avatar/processLipsync/credit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Credit check failed");
      }
      const data = await res.json();
      return {
        requiredCredits: data.requiredCredits,
        availableCredits: data.availableCredits,
        hasSufficientBalance: data.hasSufficientBalance,
        providers: [
          {
            label:
              LIPSYNC_PROVIDERS.find((p) => p.value === resolvedProvider)?.label ||
              resolvedProvider,
            cost: data.requiredCredits,
          },
        ],
      };
    }, [lipsyncMode, lipsyncProvider, transparentBg, getSelectedSceneAudioDuration]);

    useEffect(() => {
      onCreditCheckReady?.(isTalking ? talkingStatus.checkCredit : checkCredit);
    }, [checkCredit, isTalking, talkingStatus.checkCredit, onCreditCheckReady]);

    // ── SSML etiketlerini temizleyen fonksiyon ────────────────────────────────
    const stripSSML = (text) => {
      if (!text) return "";
      return text.replace(/<[^>]*>/g, "");
    };

    // Görüntülenecek metin (SSML göster/gizle durumuna göre)
    const displayedText = showSSML ? narrationText : stripSSML(narrationText);

    // "Custom" (AI_OTHER_VALUE) seçiliyken çip/swatch grubunun altında açılan
    // serbest metin girişi. fieldKey aiCustom anahtarıyla aynı; sağdaki × seçimi
    // geri alır (onBack çağrısı value'yu "" yapar).
    const renderAiCustomInput = (fieldKey, onBack) => {
      // Yaş aralığı "Özel" girişinde alt sınır 25: provider'lar genç ifadelerde
      // content filter'a takıldığından (bkz. AI_AGE_OPTIONS üstündeki not) 25 yaş
      // altı serbest değer girilemez. Sadece rakam kabul edilir; çoklu basamak
      // yazımı bozulmasın diye clamp her tuşta değil, blur'da uygulanır.
      const isAge = fieldKey === "ageRange";
      const handleChange = (e) => {
        const val = isAge ? e.target.value.replace(/[^\d]/g, "") : e.target.value;
        setAiCustom((prev) => ({ ...prev, [fieldKey]: val }));
      };
      const handleBlur = (e) => {
        blurField(e);
        if (!isAge) return;
        setAiCustom((prev) => {
          const cur = prev.ageRange;
          if (cur === "" || cur == null) return prev;
          const n = parseInt(cur, 10);
          if (isNaN(n)) return { ...prev, ageRange: "" };
          if (n < 25) return { ...prev, ageRange: "25" };
          return prev;
        });
      };
      return (
        <div style={{ position: "relative", marginTop: "8px" }}>
          <input
            type={isAge ? "number" : "text"}
            {...(isAge ? { min: 25, inputMode: "numeric" } : {})}
            autoFocus
            value={aiCustom[fieldKey] || ""}
            onChange={handleChange}
            placeholder={
              isAge
                ? i18n.t("AvatarAiAgeCustomPlaceholder") || "e.g. 30 (min 25)"
                : i18n.t("AvatarAiOptionOtherPlaceholder") || "Type your own…"
            }
            style={{ ...AI_INPUT_STYLE, paddingRight: "32px" }}
            onFocus={focusField}
            onBlur={handleBlur}
          />
          <button
            type="button"
            title={i18n.t("Back") || "Back"}
            aria-label={i18n.t("Back") || "Back"}
            onClick={onBack}
            style={{
              position: "absolute",
              top: "50%",
              right: "8px",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "18px",
              height: "18px",
              padding: 0,
              border: "none",
              borderRadius: "4px",
              background: "transparent",
              color: "#64748b",
              cursor: "pointer",
            }}
          >
            <LuX size={14} />
          </button>
        </div>
      );
    };

    // Tek seçimli çip grubu (yaş, saç stili, ifade, kıyafet, arka plan). "Custom"
    // çipi AI_OTHER_VALUE'ya geçirip altında serbest metin girişi açar; seçili çipe
    // tekrar tıklamak seçimi kaldırır. fieldKey aiCustom anahtarıyla aynı.
    const renderAiChips = ({ value, onChange, options, fieldKey }) => {
      const isCustom = value === AI_OTHER_VALUE;
      return (
        <div>
          <div className="asm-av__chips">
            {options.map((o) => {
              const on = value === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  className={`asm-av__chip${on ? " asm-av__chip--on" : ""}`}
                  onClick={() => onChange(on ? "" : o.value)}
                >
                  {on && <LuCheck className="asm-av__chip-check" size={13} />}
                  {i18n.t(o.labelKey) || o.fallback}
                </button>
              );
            })}
            <button
              type="button"
              className={`asm-av__chip asm-av__chip--add${isCustom ? " asm-av__chip--on" : ""}`}
              onClick={() => onChange(isCustom ? "" : AI_OTHER_VALUE)}
            >
              <LuPlus size={13} />
              {i18n.t("Custom") || "Custom"}
            </button>
          </div>
          {isCustom && renderAiCustomInput(fieldKey, () => onChange(""))}
        </div>
      );
    };

    // Renk kutucuğu (swatch) grubu (cilt tonu, saç rengi, göz rengi). Seçili swatch
    // mor halka alır; "+" kutucuğu serbest metin girişini açar. Renk yalnızca
    // görseldir — payload'a value (enum) gider.
    const renderAiSwatches = ({ value, onChange, options, fieldKey }) => {
      const isCustom = value === AI_OTHER_VALUE;
      return (
        <div>
          <div className="asm-av__swatches">
            {options.map((o) => {
              const on = value === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  title={i18n.t(o.labelKey) || o.fallback}
                  aria-label={i18n.t(o.labelKey) || o.fallback}
                  className={`asm-av__swatch${on ? " asm-av__swatch--on" : ""}`}
                  style={{ background: o.color }}
                  onClick={() => onChange(on ? "" : o.value)}
                />
              );
            })}
            <button
              type="button"
              title={i18n.t("Custom") || "Custom"}
              aria-label={i18n.t("Custom") || "Custom"}
              className={`asm-av__swatch asm-av__swatch--add${
                isCustom ? " asm-av__swatch--on" : ""
              }`}
              onClick={() => onChange(isCustom ? "" : AI_OTHER_VALUE)}
            >
              <LuPlus size={13} />
            </button>
          </div>
          {isCustom && renderAiCustomInput(fieldKey, () => onChange(""))}
        </div>
      );
    };

    // Çoklu seçimli aksesuar çipleri ("pick any"). Seçim aiAccessories dizisinde
    // tutulur; "Custom" çipi aiCustom.accessories serbest metnini açar.
    const renderAiAccessoryChips = () => {
      const customOpen = aiAccessories.includes(AI_OTHER_VALUE);
      return (
        <div>
          <div className="asm-av__chips">
            {AI_ACCESSORY_OPTIONS.map((o) => {
              const on = aiAccessories.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  className={`asm-av__chip${on ? " asm-av__chip--on" : ""}`}
                  onClick={() => toggleAiAccessory(o.value)}
                >
                  {on && <LuCheck className="asm-av__chip-check" size={13} />}
                  {i18n.t(o.labelKey) || o.fallback}
                </button>
              );
            })}
            <button
              type="button"
              className={`asm-av__chip asm-av__chip--add${customOpen ? " asm-av__chip--on" : ""}`}
              onClick={() => toggleAiAccessory(AI_OTHER_VALUE)}
            >
              <LuPlus size={13} />
              {i18n.t("Custom") || "Custom"}
            </button>
          </div>
          {customOpen &&
            renderAiCustomInput("accessories", () => toggleAiAccessory(AI_OTHER_VALUE))}
        </div>
      );
    };

    // ── Stock galeri kategori çipleri ─────────────────────────────────────────
    // avatar.category / tags / group alanlarından türetilir. Veri yoksa boş döner
    // ve UI yalnızca "All" çipini gösterir. Filtreleme tamamen istemci tarafında.
    const avatarCategoryOf = useCallback((a) => {
      const raw = a?.category ?? a?.categories ?? a?.tags ?? a?.group ?? a?.role;
      if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
      return raw ? [String(raw)] : [];
    }, []);
    const avatarCategories = useMemo(() => {
      const set = new Set();
      (avatars || []).forEach((a) => avatarCategoryOf(a).forEach((c) => set.add(c)));
      return Array.from(set);
    }, [avatars, avatarCategoryOf]);
    const filteredAvatars = useMemo(() => {
      if (avatarFilter === "all") return avatars || [];
      const f = avatarFilter.toLowerCase();
      return (avatars || []).filter((a) => avatarCategoryOf(a).some((c) => c.toLowerCase() === f));
    }, [avatars, avatarFilter, avatarCategoryOf]);
    // Avatar kartının altında gösterilecek rol/başlık etiketi (varsa).
    const avatarRoleLabel = (a) => a?.role || a?.title || a?.subtitle || a?.description || "";

    // "Sahne Sesinden Oluştur" read-only metni: önce sahne sesinin kendi alanları,
    // yoksa API'den çekilen sceneAudioText (narrationTextOriginal) kullanılır.
    const sceneAudioResolvedText =
      selectedSceneAudio?.text ||
      selectedSceneAudio?.narrationText ||
      selectedSceneAudio?.content ||
      sceneAudioText ||
      "";

    // ── Sahneye ekle fonksiyonu (VideoModal'daki handleAddToScene'den adapte edildi) ──────────
    const handleAddToScene = useCallback(
      async (url, name) => {
        if (!onBoxAdded || !url) {
          return;
        }

        const IdealStudio = require("../../../core/editor/main").default;
        const { isSlide, isContainedView } = require("../../../common/utils");
        const { ID_PREFIX_BOX } = require("../../../common/constants");
        const { randomPositionGenerator } = require("../clipboard/clipboard.utils");
        const { createBox } = require("../../../common/common_tools");

        const pluginName = "EnrichedPlayer";
        const plugin = IdealStudio.Plugins.get(pluginName);
        if (!plugin) {
          return;
        }

        const navItem =
          containedViewSelected && containedViews && containedViews[containedViewSelected]
            ? containedViews[containedViewSelected]
            : navItems && navItems[navItemSelected];

        if (!navItem) {
          return;
        }

        const cv =
          containedViewSelected &&
          containedViews &&
          containedViews[containedViewSelected] &&
          isContainedView(containedViewSelected);
        const cvslide = cv && isSlide(navItem.type);

        const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
        const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

        // Pozisyon + id await gerektirmez → hemen üret ve spinner'ı TIKLAMA ANINDA
        // göster; kutu render olunca üstüne geçip video karesi gelince kapanır.
        const position = {
          x: randomPositionGenerator(10, 30),
          y: randomPositionGenerator(10, 30),
          type: "absolute",
        };
        const boxId = ID_PREFIX_BOX + Date.now();
        spinForSceneAdd({ boxId, x: position.x, y: position.y, contained: cv });

        const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(url, "video");

        const initialParams = {
          parent,
          container: 0,
          name: pluginName,
          position,
          id: boxId,
          page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
          url,
          width,
          height,
          widthUnit,
          heightUnit,
        };

        createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
        onClose?.();
      },
      [
        onBoxAdded,
        boxes,
        navItems,
        navItemSelected,
        containedViews,
        containedViewSelected,
        boxSelected,
        pluginToolbars,
        onClose,
      ]
    );

    // ── Audio player fonksiyonları ────────────────────────────────────────────
    const handlePlayPause = useCallback(() => {
      if (!audioRef.current) return;

      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, [isPlaying]);

    const handleTimeUpdate = useCallback(() => {
      if (!audioRef.current) return;
      const current = audioRef.current.currentTime;
      const dur = audioRef.current.duration || 0;
      setCurrentTime(current);
      setProgress(dur > 0 ? (current / dur) * 100 : 0);
    }, []);

    const handleLoadedMetadata = useCallback(() => {
      if (!audioRef.current) return;
      setDuration(audioRef.current.duration || 0);
    }, []);

    const handleProgressBarClick = useCallback((e) => {
      if (!audioRef.current) return;
      const bar = e.currentTarget;
      const rect = bar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setProgress(percentage * 100);
    }, []);

    const handleAudioEnded = useCallback(() => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    }, []);

    // ── Önizleme oynatıcısı handler'ları (preview audio) ───────────────────────
    const handlePreviewPlayPause = useCallback(() => {
      if (!previewAudioRef.current) return;
      if (previewPlaying) {
        previewAudioRef.current.pause();
        setPreviewPlaying(false);
      } else {
        previewAudioRef.current.play();
        setPreviewPlaying(true);
      }
    }, [previewPlaying]);

    const handlePreviewTimeUpdate = useCallback(() => {
      if (!previewAudioRef.current) return;
      const current = previewAudioRef.current.currentTime;
      const dur = previewAudioRef.current.duration || 0;
      setPreviewCurrentTime(current);
      setPreviewProgress(dur > 0 ? (current / dur) * 100 : 0);
    }, []);

    const handlePreviewLoadedMetadata = useCallback(() => {
      if (!previewAudioRef.current) return;
      setPreviewDuration(previewAudioRef.current.duration || 0);
    }, []);

    const handlePreviewBarClick = useCallback((e) => {
      if (!previewAudioRef.current) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const percentage = (e.clientX - rect.left) / rect.width;
      const newTime = percentage * previewAudioRef.current.duration;
      previewAudioRef.current.currentTime = newTime;
      setPreviewCurrentTime(newTime);
      setPreviewProgress(percentage * 100);
    }, []);

    const handlePreviewEnded = useCallback(() => {
      setPreviewPlaying(false);
      setPreviewCurrentTime(0);
      setPreviewProgress(0);
    }, []);

    // Üretilen önizleme sesini temizle (× butonu). Oynatıcıyı kapatıp tekrar
    // "üret" menüsüne döner.
    const handleClearPreview = useCallback(() => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
      }
      setPreviewUrl("");
      setPreviewPlaying(false);
      setPreviewProgress(0);
      setPreviewCurrentTime(0);
      setPreviewDuration(0);
    }, []);

    // Seçilen ses/dil/metin değişince üretilmiş önizleme artık güncel değil → sıfırla.
    useEffect(() => {
      setPreviewUrl("");
      setPreviewPlaying(false);
      setPreviewProgress(0);
      setPreviewCurrentTime(0);
      setPreviewMenuOpen(false);
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
      }
    }, [ttsSelectedVoice, ttsProvider, ttsLanguage, narrationText]);

    // Önizleme üret menüsü açıkken dışarı tıklayınca kapat.
    useEffect(() => {
      if (!previewMenuOpen) return undefined;
      const onDocClick = (e) => {
        if (previewMenuRef.current && !previewMenuRef.current.contains(e.target)) {
          setPreviewMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }, [previewMenuOpen]);

    // "Ses önizlemesi üret" — seçili sağlayıcı/ses/dil ile gerçek TTS API'sine gider.
    const handleGeneratePreview = useCallback(
      async (mode = "full") => {
        if (previewGenerating || !narrationText.trim() || !ttsSelectedVoice?.id || !ttsProvider) {
          return;
        }
        // "short" modunda ~5 sn'lik bir önizleme üret: metni sağlayıcının konuşma
        // hızına göre ~5 saniyelik karakter sayısına, kelime sınırından kırpıyoruz.
        // (SSML etiketleri atılır; ortadan kesilince bozuk etiket göndermemek için.)
        const buildPreviewText = () => {
          const full = narrationText.trim();
          if (mode !== "short") return full;
          const plain = full
            .replace(/<[^>]*>/g, "")
            .replace(/\s+/g, " ")
            .trim();
          // Hedef ~5 sn. Sağlayıcıya göre ampirik karakter sayısı (bkz.
          // SHORT_PREVIEW_TARGET_CHARS) — ortak formül üçünde farklı süreler üretiyordu.
          const targetChars =
            SHORT_PREVIEW_TARGET_CHARS[ttsProvider] || SHORT_PREVIEW_DEFAULT_CHARS;
          // 5 sn'yi dolduracak kadar metin yoksa null dön → çağıran uyarı gösterip iptal eder.
          if (plain.length < targetChars) return null;
          const slice = plain.slice(0, targetChars);
          const lastSpace = slice.lastIndexOf(" ");
          return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trim();
        };
        const previewText = buildPreviewText();
        if (mode === "short" && !previewText) {
          setPreviewMenuOpen(false);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("VoicePreviewNotEnoughText") ||
                "Not enough text for a 5-second preview. Please enter more text.") +
              "</span>",
            false
          );
          return;
        }
        setPreviewMenuOpen(false);
        setPreviewGenerating(true);
        setPreviewUrl("");
        try {
          const requestBody = {
            idealStudioTrainingId: window.idealStudioTrainingId,
            provider: ttsProvider, // azure | elevenlabs | gemini
            text: previewText,
            language: ttsProvider === "elevenlabs" ? "multilingual" : ttsLanguage || "",
            voice: ttsSelectedVoice?.id || profileVoiceName || "",
            ...(ttsProvider === "elevenlabs" ? { elevenLabsModel: ttsElevenLabsModel } : {}),
            // Routing hints for the async result / activity-widget drop-in.
            navId: navItemSelected,
            sceneIndex: String((navItemsIds?.indexOf(navItemSelected) ?? -1) + 1),
          };
          // The endpoint is async: 202 { jobId } now, audio later. Submit, then
          // poll the job-status endpoint until it completes.
          const response = await fetch(`${process.env.MW_API_URL}api/course/text-to-speech`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify(requestBody),
          });
          if (response.ok) {
            const data = await response.json().catch(() => ({}));
            const jobId = data.jobId;
            if (!jobId) {
              onServerModal?.(
                i18n.t("FileModal.APIProviders.error"),
                "<span>" + (i18n.t("error.serviceError") || "Service Error") + "</span>",
                false
              );
              return;
            }
            const result = await waitForTtsJob(jobId);
            // Önizleme sesi bu sekmenin ara adımı (dinleme / avatar sesi) — widget'ta
            // ayrı bir "sahneye ekle" kartı olarak beklememeli.
            if (result?.audioUrl) approveJob(jobId);
            setPreviewUrl(result.audioUrl);
            // Hangi modda üretildiğini sakla: "short" önizleme avatar üretiminde
            // kullanılmaz; sadece "full" önizleme avatarın sesi olur.
            setPreviewMode(mode);
            setPreviewDuration(result.duration || 0);
            setPreviewPlaying(false);
            setPreviewProgress(0);
            setPreviewCurrentTime(0);
          } else {
            const errData = await response.json().catch(() => ({}));
            if (checkInsufficientBalance(errData)) return;
            onServerModal?.(
              i18n.t("FileModal.APIProviders.error"),
              "<span>" + (i18n.t("error.systemError") || "System Error") + "</span>",
              false
            );
          }
        } catch (err) {
          console.error("Voice preview generate error:", err);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("error.networkError") || "Network error. Please try again.") +
              "</span>",
            false
          );
        } finally {
          setPreviewGenerating(false);
        }
      },
      [
        previewGenerating,
        narrationText,
        ttsSelectedVoice,
        ttsProvider,
        ttsElevenLabsModel,
        ttsLanguage,
        profileVoiceName,
        onServerModal,
        checkInsufficientBalance,
        navItemSelected,
        navItemsIds,
      ]
    );

    const formatTime = useCallback((seconds) => {
      if (!seconds || isNaN(seconds)) return "00:00";
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }, []);

    // selectedSceneAudio değiştiğinde audio'yu resetle
    useEffect(() => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }, [selectedSceneAudio]);

    // narrationSource değiştiğinde audio'yu durdur
    useEffect(() => {
      if (narrationSource !== "sceneAudio") {
        setIsPlaying(false);
        if (audioRef.current) {
          audioRef.current.pause();
        }
      }
    }, [narrationSource]);

    // ── selectedVoice'u anlatım kaynağıyla senkronize et ─────────────────────
    // "Farklı ses kullan" kartı kaldırıldı; ses koru vs. farklı ses ayrımı artık
    // tamamen anlatım kaynağı radyolarıyla yönetiliyor:
    //   sceneAudio → "keep" (anlatım sesini koru)
    //   text       → "different" (yeni anlatım / farklı ses)
    // Save akışındaki selectedVoice === "keep" kontrolleri bu sayede korunuyor.
    useEffect(() => {
      setSelectedVoice(narrationSource === "sceneAudio" ? "keep" : "different");
    }, [narrationSource]);

    // ── Azure sahne sesini desteklemiyor → her zaman "Metinden Oluştur" ───────
    // Azure modeli seçiliyken (ilk açılış dâhil) anlatım kaynağını "text"e zorla;
    // böylece doğrudan "yeni anlatım sesi oluştur" görünümünde açılır.
    useEffect(() => {
      if (selectedModel === "azure-video" && narrationSource !== "text") {
        setNarrationSource("text");
      }
    }, [selectedModel, narrationSource]);

    // ── API: Avatar listesi ────────────────────────────────────────────────────
    const loadAvatars = useCallback(
      async (provider) => {
        setIsLoadingAvatars(true);
        setAvatars([]);
        setSelectedStockAvatar(null);
        try {
          const organizationId = getOrganizationId();

          let url = `${process.env.MW_API_URL}api/tts-avatar/provider/${provider}`;
          if (provider === "aidea" && organizationId) {
            url += `?organizationId=${organizationId}`;
          }

          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (response.ok) {
            const data = await response.json();
            const list = Array.isArray(data) ? data.filter((a) => a.isActive) : [];
            setAvatars(list);
            return list;
          } else {
            throw new Error("Failed to fetch avatars");
          }
        } catch (error) {
          console.error("Error loading avatars:", error);
          setAvatars([]);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.FailedToLoadAvatars") + "</span>",
            false
          );
          return [];
        } finally {
          setIsLoadingAvatars(false);
        }
      },
      [onServerModal]
    );

    // ── API: Kayıtlı (org'a ait) avatarı sil ───────────────────────────────────
    const confirmDeleteAvatar = useCallback(async () => {
      const target = avatarToDelete;
      if (!target || deletingAvatarRef.current) return;
      deletingAvatarRef.current = true;
      setIsDeletingAvatar(true);
      const result = await deleteSavedAvatar(target.id);
      deletingAvatarRef.current = false;
      setIsDeletingAvatar(false);
      setAvatarToDelete(null);
      // 404 = zaten yok / başka org → silinmiş say, listeden de düş.
      if (result.status === "deleted" || result.status === "gone") {
        setAvatars((prev) => (prev || []).filter((a) => a.id !== target.id));
        setSelectedStockAvatar((prev) => (prev?.id === target.id ? null : prev));
        setPlayingAvatarId((prev) => (prev === target.id ? null : prev));
        return;
      }
      const message =
        result.code === "AVATAR_NOT_DELETABLE"
          ? i18n.t("AvatarDelete.ErrorNotDeletable") || "Bu avatar silinemez."
          : i18n.t("AvatarDelete.ErrorGeneric") || "Avatar silinemedi, lütfen tekrar deneyin.";
      onServerModal?.(i18n.t("error.warning"), "<span>" + message + "</span>", false);
    }, [avatarToDelete, onServerModal]);

    // ── API: Ses listesi (Azure) ───────────────────────────────────────────────
    const loadVoices = useCallback(async () => {
      setIsLoadingVoices(true);
      try {
        const response = await fetch(
          `${process.env.MW_API_URL}api/course/available-voices?service=microsoft`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setAllVoices(Array.isArray(data?.voices) ? data.voices : []);
        }
      } catch (error) {
        console.error("Error loading voices:", error);
      } finally {
        setIsLoadingVoices(false);
      }
    }, []);

    // ── API: Zengin ses listesi (AIVoiceTab ile aynı: course/voices, sayfalı + sample) ──
    const loadCourseVoices = useCallback(
      async (provider, language, gender = "", page = 1, autoSelectName = "") => {
        // provider === "" → "Tümü" (All): service paramı gönderilmez, tüm sağlayıcıların
        // sesleri gelir. Sadece null/undefined'da no-op (yanlışlıkla çağrıyı engelle).
        if (provider == null) return [];
        setIsLoadingTTSVoices(true);
        const serviceName = COURSE_VOICES_SERVICE_MAP[provider] || provider;
        try {
          const params = new URLSearchParams();
          if (serviceName) params.append("service", serviceName);
          if (language) params.append("language", language);
          if (gender) params.append("gender", gender);
          params.append("page", page.toString());
          params.append("pageSize", "12");

          const response = await fetch(
            `${process.env.MW_API_URL}api/course/voices?${params.toString()}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          if (response.ok) {
            const data = await response.json();
            const list = Array.isArray(data?.voices) ? data.voices : [];
            setTtsVoices(list);

            const totalCount = data.pagination?.total ?? data.totalCount ?? data.total ?? null;
            setTtsTotalVoices(typeof totalCount === "number" ? totalCount : null);

            if (data.pagination?.totalPages) {
              setTtsTotalPages(data.pagination.totalPages);
            } else if (data.pagination?.total) {
              setTtsTotalPages(Math.ceil(data.pagination.total / 12));
            } else if (data.totalPages) {
              setTtsTotalPages(data.totalPages);
            } else if (data.totalCount) {
              setTtsTotalPages(Math.ceil(data.totalCount / 12));
            } else {
              setTtsTotalPages(list.length >= 12 ? page + 1 : page);
            }

            if (autoSelectName) {
              // Önceki sesi listede bulup seçili getir. Tam eşitlik bazen tutmuyordu
              // (büyük/küçük harf, ad/ID formatı farkı), bu yüzden esnek eşleştirme.
              const target = String(autoSelectName).toLowerCase().trim();
              const match =
                list.find((v) => {
                  const vName = String(v.name || "")
                    .toLowerCase()
                    .trim();
                  const vId = String(v.id || "")
                    .toLowerCase()
                    .trim();
                  return vName === target || vId === target;
                }) ||
                list.find((v) => {
                  const vName = String(v.name || "")
                    .toLowerCase()
                    .trim();
                  const vId = String(v.id || "")
                    .toLowerCase()
                    .trim();
                  return (
                    (vName && (vName.includes(target) || target.includes(vName))) ||
                    (vId && (vId.includes(target) || target.includes(vId)))
                  );
                });
              if (match) setTtsSelectedVoice(match);
            }
            return list;
          }
        } catch (error) {
          console.error("Error loading course voices:", error);
        } finally {
          setIsLoadingTTSVoices(false);
        }
        return [];
      },
      []
    );

    // Sağlayıcı değişince filtreleri sıfırla ve sesleri yeniden yükle (AIVoiceTab pattern).
    const handleTtsProviderChange = useCallback(
      (newProvider) => {
        setTtsProvider(newProvider);
        setTtsVoices([]);
        setTtsSelectedVoice(null);
        setTtsCurrentPage(1);
        setTtsTotalPages(1);
        setTtsTotalVoices(null);
        if (newProvider === "elevenlabs") {
          setTtsLanguage("multilingual");
          setTtsGender("");
          loadCourseVoices("elevenlabs", "multilingual", "", 1, "");
        } else if (newProvider) {
          setTtsLanguage("");
          setTtsGender("");
          loadCourseVoices(newProvider, "", "", 1, "");
        } else {
          // "Tümü" (All) — tüm sağlayıcıların seslerini yükle (service paramı yok).
          setTtsLanguage("");
          setTtsGender("");
          loadCourseVoices("", "", "", 1, "");
        }
      },
      [loadCourseVoices]
    );

    // Ses kartındaki örnek (sample) oynatma — dil önceliği: manuel > TR > EN > ilk.
    const handleTtsSamplePlay = useCallback(
      (voice) => {
        let sampleUrl = null;
        if (voice.sampleUrlsByLanguage) {
          const availableLanguages = Object.keys(voice.sampleUrlsByLanguage);
          // Örnek dil önceliği AIVoiceTab ile aynı: açık olan sayfa/editör dili >
          // TR > EN > seçili üretim dili > ilk mevcut dil.
          const langSamples =
            voice.sampleUrlsByLanguage[
              pickSampleLanguage(availableLanguages, { selectedLanguage: ttsLanguage })
            ];
          if (langSamples && langSamples.v1) sampleUrl = langSamples.v1;
        }
        if (!sampleUrl) {
          sampleUrl = voice.sampleUrl || voice.audioSampleUrl || voice.previewUrl;
        }
        if (!sampleUrl) return;

        if (ttsSamplePlayingId === voice.id && ttsSampleAudioRef.current) {
          ttsSampleAudioRef.current.pause();
          ttsSampleAudioRef.current = null;
          setTtsSamplePlayingId(null);
          return;
        }
        if (ttsSampleAudioRef.current) ttsSampleAudioRef.current.pause();

        const audio = new Audio(sampleUrl);
        ttsSampleAudioRef.current = audio;
        setTtsSamplePlayingId(voice.id);
        audio.onended = () => {
          setTtsSamplePlayingId(null);
          ttsSampleAudioRef.current = null;
        };
        audio.play().catch(() => {
          setTtsSamplePlayingId(null);
          ttsSampleAudioRef.current = null;
        });
      },
      [ttsLanguage, ttsSamplePlayingId]
    );

    // "Metinden Oluştur" (farklı ses) moduna girince seçiciyi AIVoiceTab gibi hazır getir.
    // AIVoiceTab davranışı: sahnenin/eğitimin aktif anlatıcısını (kaydedilmiş ses ya da
    // voiceProfile) sağlayıcı + dil olarak ayarlayıp ses adını autoSelect ile seçili getir.
    // Saved voice (soundList[0].voiceData) öncelikli; yoksa voiceProfile'a düşer; o da yoksa
    // ElevenLabs/multilingual default. Her giriş/model değişiminde yeniden yükleriz; çünkü
    // "sesi koru" modundaki loadSavedVoiceData aynı state'leri farklı doldurabiliyor.
    useEffect(() => {
      // Hem stock hem custom (kendin oluştur) modunda çalışır; her ikisinde de
      // "Yeni ses üret" altındaki seçici önceki/aktif sesi seçili getirir.
      if (narrationSource === "text" && (selectedModel === "aidea" || selectedModel === "tavus")) {
        const savedVoice = soundList?.[0]?.voiceData;
        const provider =
          normalizeTtsProvider(savedVoice?.provider) ||
          normalizeTtsProvider(profileService) ||
          normalizeTtsProvider(voiceProfile?.voiceServiceProvider) ||
          "elevenlabs";
        const autoSelectName =
          savedVoice?.voiceName || savedVoice?.voiceId || profileVoiceName || profileVoiceId || "";
        // İlk yüklemede DİL FİLTRESİ UYGULAMA (ElevenLabs hariç): kaydedilmiş sesin
        // dili yanlış/eksik olduğunda filtre o sesi listeden eliyor ve autoSelect
        // başarısız oluyordu. Filtresiz yüklersek aktif ses (örn. azure "Alessio")
        // listede gelir ve seçili olarak işaretlenir. Dili kullanıcı isterse
        // aşağıdaki dropdown'dan kendisi daraltabilir.
        const language = provider === "elevenlabs" ? "multilingual" : "";
        setTtsProvider(provider);
        setTtsElevenLabsModel(
          normalizeElevenLabsModel(savedVoice?.elevenLabsModel || voiceProfile?.elevenLabsModel)
        );
        // Liste asenkron yüklenene kadar önceki sesi hemen seçili göster: kaydedilmiş
        // ses / voiceProfile verisinden sentetik bir seçim objesi kur. Liste gelince
        // loadCourseVoices'taki autoSelect, tam veriyi içeren eşleşmeyle değiştirir.
        const preselectedVoice = autoSelectName
          ? {
              id: savedVoice?.voiceId || savedVoice?.voiceName || profileVoiceId || autoSelectName,
              name: savedVoice?.voiceName || profileVoiceName || autoSelectName,
              language: savedVoice?.language || profileLanguage || "",
              gender: savedVoice?.gender || "",
            }
          : null;
        setTtsSelectedVoice(preselectedVoice);
        setTtsLanguage(language);
        setTtsGender("");
        setTtsCurrentPage(1);
        loadCourseVoices(provider, language, "", 1, autoSelectName);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [narrationSource, selectedAvatarType, selectedModel]);

    // ── Effect: Başlangıçta avatar ve ses yükle ───────────────────────────────
    useEffect(() => {
      loadAvatars("aidea");
      loadVoices();
      return () => {
        if (pollInterval.current) clearInterval(pollInterval.current);
        if (avatarPollInterval.current) clearTimeout(avatarPollInterval.current);
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach((t) => t.stop());
          videoStreamRef.current = null;
        }
        if (uploadedVideoUrl) URL.revokeObjectURL(uploadedVideoUrl);
        if (uploadedAudioUrl) URL.revokeObjectURL(uploadedAudioUrl);
        if (uploadedPhotoUrl) URL.revokeObjectURL(uploadedPhotoUrl);
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach((t) => t.stop());
          cameraStreamRef.current = null;
        }
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        if (ttsSampleAudioRef.current) {
          ttsSampleAudioRef.current.pause();
          ttsSampleAudioRef.current = null;
        }
        if (previewAudioRef.current) {
          previewAudioRef.current.pause();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Effect: soundList değiştiğinde ilk sesi otomatik seç ──────────────────
    useEffect(() => {
      if (soundList && soundList.length > 0) {
        setSelectedSceneAudio(soundList[0]);
      } else {
        setSelectedSceneAudio(null);
      }
    }, [soundList]);

    // ── Sahnede kullanılan anlatım sesinin cinsiyeti ──────────────────────────
    // Kaydedilen voiceData çoğu zaman gender içermez; asıl güvenilir kaynak
    // available-voices listesinden eşleştirilip ttsSelectedVoice'a yazılan sestir
    // (hem "sesi koru" hem "yeni ses" modunda autoSelect ile dolar). O da yoksa
    // kaydedilmiş voiceData.gender'a düşeriz.
    const sceneVoiceGender = useMemo(
      () =>
        normalizeGender(
          ttsSelectedVoice?.gender ||
            selectedSceneAudio?.voiceData?.gender ||
            soundList?.[0]?.voiceData?.gender
        ),
      [ttsSelectedVoice, selectedSceneAudio, soundList]
    );

    // ── Effect: Avatar cinsiyetini sahne sesinin cinsiyetine göre ön-seç ──────
    // Erkek seslendirme → "male", kadın → "female". Kullanıcı butona elle
    // dokunduysa (genderTouchedRef) tercihini ezmeyiz.
    useEffect(() => {
      if (genderTouchedRef.current) return;
      if (sceneVoiceGender && sceneVoiceGender !== gender) {
        setGender(sceneVoiceGender);
      }
    }, [sceneVoiceGender, gender]);

    // ── Effect: Persona (AI) üretimi bitince üretilen avatarı otomatik seç ─────
    // Böylece kullanıcı Aidea kütüphanesine dönmeden, custom ekranından doğrudan
    // footer'daki "Avatar Oluştur" ile videoyu üretebilir. handleCreate'in Aidea
    // lipsync dalı kaynak videoyu selectedStockAvatar.videoUrl'den alır.
    useEffect(() => {
      if (aiResult?.videoUrl) {
        setSelectedStockAvatar((prev) => ({
          id: aiResult.lipsyncAvatarId || prev?.id || `ai-${Date.now()}`,
          name: avatarName?.trim() || prev?.name || "AI Avatar",
          gender,
          videoUrl: aiResult.videoUrl,
          thumbnailUrl: aiResult.thumbnailUrl || aiResult.imageUrl || prev?.thumbnailUrl || "",
          isAiGenerated: true,
        }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiResult]);

    // ── Effect: Avatar tipi değişince önceki avatar seçimini temizle ───────────
    // Kütüphaneden (stock) seçilen avatar "kendin oluştur" (custom) moduna
    // taşınınca selectedStockAvatar dolu kalıyor ve "Avatar Oluştur" yanlışlıkla
    // aktifleşiyordu. Tip değişince seçimi sıfırlıyoruz; custom'da persona
    // üretilince yukarıdaki aiResult effect'i tekrar dolduruyor.
    useEffect(() => {
      setSelectedStockAvatar(null);
    }, [selectedAvatarType]);

    // ── Effect: Kendi medyanla oluşturulan avatarı listede seçip öne getir ─────
    // checkAvatarJobStatus tamamlanınca yeni avatarı pendingNewAvatarRef'e yazar.
    // Seçimi burada (yukarıdaki temizleme effect'inden SONRA) uyguluyoruz; aksi
    // halde "custom" → "stock" geçişi seçimi hemen null'a çekiyordu ve kullanıcı
    // avatarı "Tümü" sekmesinde elle aramak zorunda kalıyordu.
    useEffect(() => {
      const pending = pendingNewAvatarRef.current;
      if (!pending || selectedAvatarType !== "stock") return;
      pendingNewAvatarRef.current = null;
      setSelectedStockAvatar(pending);
      // Kart DOM'a girdikten sonra görünür alana kaydır.
      setTimeout(() => {
        const el = document.querySelector(
          `.asm-av__avatar[data-avatar-id="${String(pending.id).replace(/"/g, '\\"')}"]`
        );
        el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      }, 50);
    }, [avatars, selectedAvatarType]);

    // ── Effect: Avatar seçilince sesi filtrele ─────────────────────────────────
    useEffect(() => {
      if (selectedModel === "azure-video" && selectedStockAvatar && allVoices.length > 0) {
        const filtered = allVoices.filter((v) => v.gender === selectedStockAvatar.gender);
        setVoices(filtered);
        setSelectedVoiceId(filtered.length > 0 ? filtered[0].id : "");
      } else {
        setVoices([]);
        setSelectedVoiceId("");
      }
    }, [selectedStockAvatar, allVoices, selectedModel]);

    // ── Effect: soundList'ten kaydedilmiş ses bilgilerini yükle (AIVoiceTab pattern) ───
    useEffect(() => {
      const loadSavedVoiceData = async () => {
        // "Keep voice" modunda değilse çık
        if (selectedVoice !== "keep") return;

        // Model Aidea veya Tavus değilse çık
        if (selectedModel !== "aidea" && selectedModel !== "tavus") return;

        // soundList'te kaydedilmiş ses var mı kontrol et
        if (!soundList || !Array.isArray(soundList) || soundList.length === 0) {
          await loadFromVoiceProfile();
          return;
        }

        const firstSound = soundList[0];
        if (!firstSound || !firstSound.file) {
          await loadFromVoiceProfile();
          return;
        }

        // soundList'te voiceData varsa kullan
        let metadata = null;
        if (firstSound.voiceData) {
          metadata = {
            VoiceServiceType: firstSound.voiceData.provider || "",
            VoiceName: firstSound.voiceData.voiceName || "",
            VoiceId: firstSound.voiceData.voiceId || "",
            VoiceLanguage: firstSound.voiceData.language || "",
            VoiceGender: firstSound.voiceData.gender || "",
          };
        }

        // Metadata dosyasını dene
        if (!metadata) {
          try {
            const metaUrl = withSidecarSuffix(firstSound.file, ".txt");
            const response = await fetch(metaUrl, { credentials: "include" });
            if (response.ok) {
              const text = await response.text();
              if (text.indexOf("{") === 0) {
                metadata = JSON.parse(text);
              }
            }
          } catch (error) {
            // Metadata dosyası yoksa soundList veya voiceProfile'dan bilgileri kullan
          }
        }

        // Metadata varsa işle
        if (metadata && metadata.VoiceName) {
          // Provider normalization
          let provider = metadata.VoiceServiceType;
          if (provider === "Microsoft" || provider === 0) {
            provider = "azure";
          } else if (provider === 1) {
            provider = "elevenlabs";
          } else if (provider === 2 || provider === "gemini") {
            provider = "google";
          }

          setTtsProvider(provider);

          // TTS seslerini yükle
          try {
            const serviceMap = { azure: "microsoft", elevenlabs: "elevenlabs", google: "gemini" };
            const serviceName = serviceMap[provider] || provider;

            setIsLoadingTTSVoices(true);
            const response = await fetch(
              `${process.env.MW_API_URL}api/course/available-voices?service=${serviceName}`,
              { headers: { Authorization: `Bearer ${getToken()}` } }
            );

            if (response.ok) {
              const data = await response.json();
              const voices = data.voices || [];
              setTtsVoices(voices);

              // Voice'u bul ve seç
              const voiceIdToMatch = metadata.VoiceId || metadata.VoiceName;

              // Daha esnek matching - ID veya name ile eşleş
              const matchedVoice = voices.find((v) => {
                // Case-insensitive ve trim ile karşılaştır
                const vId = String(v.id || "")
                  .toLowerCase()
                  .trim();
                const vName = String(v.name || "")
                  .toLowerCase()
                  .trim();
                const searchId = String(voiceIdToMatch || "")
                  .toLowerCase()
                  .trim();
                const searchName = String(metadata.VoiceName || "")
                  .toLowerCase()
                  .trim();

                const match =
                  vId === searchId ||
                  vName === searchName ||
                  vId === searchName ||
                  vName === searchId ||
                  vId.includes(searchId) ||
                  vName.includes(searchId);

                return match;
              });

              if (matchedVoice) {
                setTtsSelectedVoice(matchedVoice);
              } else {
                await loadFromVoiceProfile();
              }
            }
          } catch (error) {
            console.error("Error loading voices from metadata:", error);
            await loadFromVoiceProfile();
          } finally {
            setIsLoadingTTSVoices(false);
          }
        } else {
          await loadFromVoiceProfile();
        }
      };

      const loadFromVoiceProfile = async () => {
        if (!voiceProfile) return;

        const provider = profileService || voiceProfile.voiceServiceProvider;
        if (!provider) return;

        setTtsProvider(provider);

        try {
          const serviceMap = {
            azure: "microsoft",
            elevenlabs: "elevenlabs",
            gemini: "gemini",
            google: "gemini",
          };
          const serviceName = serviceMap[provider] || provider;

          setIsLoadingTTSVoices(true);
          const response = await fetch(
            `${process.env.MW_API_URL}api/course/available-voices?service=${serviceName}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );

          if (response.ok) {
            const data = await response.json();
            const voices = data.voices || [];

            // Daha esnek matching - ID, full name, veya short name ile eşleş
            const matchedVoice = voices.find((v) => {
              // Case-insensitive ve trim ile karşılaştır
              const vId = String(v.id || "")
                .toLowerCase()
                .trim();
              const vName = String(v.name || "")
                .toLowerCase()
                .trim();
              const searchId = String(voiceIdToMatch || "")
                .toLowerCase()
                .trim();
              const searchName = String(profileVoiceName || "")
                .toLowerCase()
                .trim();

              const match =
                vId === searchId ||
                vId === searchName ||
                vName === searchId ||
                vName === searchName ||
                vId.includes(searchName) ||
                vName.includes(searchName);

              return match;
            });

            if (matchedVoice) {
              setTtsSelectedVoice(matchedVoice);
            } else {
            }
          }
        } catch (error) {
          console.error("Error loading voice profile settings:", error);
        } finally {
          setIsLoadingTTSVoices(false);
        }
      };

      loadSavedVoiceData();
    }, [
      selectedVoice,
      selectedModel,
      soundList,
      voiceProfile,
      profileService,
      profileVoiceName,
      profileLanguage,
    ]);

    // Provider (Aidea/Azure) seçimi kaldırıldı → model her zaman "aidea". Avatar
    // listesi mount effect'inde loadAvatars("aidea") ile bir kez yükleniyor.

    // ── Video yükleme (custom avatar için) ────────────────────────────────────
    const handleVideoUpload = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // 500 MB üst sınırı: aşan dosyayı kabul etme
      if (isFileTooLarge(file)) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + fileTooLargeMessage() + "</span>",
          false
        );
        event.target.value = ""; // Input'u sıfırla
        return;
      }

      // Video formatı kontrolü
      const allowedVideoFormats = [".mp4", ".webm", ".mov", ".avi", ".mkv", ".flv", ".wmv", ".m4v"];
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf("."));

      if (!allowedVideoFormats.includes(fileExtension)) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("InvalidVideoFormat") ||
              "Please upload a valid video file (mp4, webm, mov, etc.)") +
            "</span>",
          false
        );
        event.target.value = ""; // Input'u sıfırla
        return;
      }

      setSourcePreview(file.name);
      setSourceSize(`${(file.size / (1024 * 1024)).toFixed(1)} MB`);
      const objectUrl = URL.createObjectURL(file);
      setUploadedVideo(file);
      setUploadedVideoUrl(objectUrl);
      setUploadingVideo(true);
      setUploadedVideoFileUrl(null);
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const trainingId = searchParams?.get("id");
        const formData = new FormData();
        formData.append("file", file);
        const queryParams = new URLSearchParams();
        if (trainingId) queryParams.set("trainingId", trainingId);
        queryParams.set("fileDescription", "Aidea Avatar Video");
        const response = await fetch(
          `${process.env.MW_API_URL}api/tts-avatar/upload-media?${queryParams.toString()}`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${getToken()}` },
            body: formData,
          }
        );
        if (response.ok) {
          const data = await response.json();
          setUploadedVideoFileUrl(data.fileUrl);
        } else {
          throw new Error(i18n.t("FailedToUploadVideo") || "Failed to upload video");
        }
      } catch (error) {
        console.error("Error uploading video:", error);
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("FailedToUploadVideoRetry") || "Failed to upload video. Please try again.") +
            "</span>",
          false
        );
      } finally {
        setUploadingVideo(false);
      }
    };

    const handleStartVideoCamera = async () => {
      setShowVideoCamera(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        videoStreamRef.current = stream;
        if (videoRecordRef.current) {
          videoRecordRef.current.srcObject = stream;
          videoRecordRef.current.play();
        }
      } catch (err) {
        console.error("Video camera error:", err);
        setShowVideoCamera(false);
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + (i18n.t("CameraAccessDenied") || "Camera access denied.") + "</span>",
          false
        );
      }
    };

    const handleStopVideoCamera = () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach((t) => t.stop());
        videoStreamRef.current = null;
      }
      setShowVideoCamera(false);
      setIsRecording(false);
    };

    const handleStartRecording = () => {
      if (!videoStreamRef.current) return;
      recordedChunksRef.current = [];

      // MP4 codec desteğini kontrol et, yoksa WebM kullan
      let mimeType = "video/webm";
      let fileExtension = "mp4";
      let fileMimeType = "video/mp4";

      if (MediaRecorder.isTypeSupported("video/mp4")) {
        mimeType = "video/mp4";
      } else if (MediaRecorder.isTypeSupported("video/webm;codecs=h264")) {
        mimeType = "video/webm;codecs=h264";
      } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
        mimeType = "video/webm;codecs=vp9";
      }

      const recorder = new MediaRecorder(videoStreamRef.current, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        pendingVideoRef.current = true;
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        // Dosya adını ve type'ı mp4 olarak ayarla
        const file = new File([blob], `recorded-video.${fileExtension}`, { type: fileMimeType });
        const objectUrl = URL.createObjectURL(blob);

        // State'leri hemen set et (buton enable olsun)
        setSourcePreview(`recorded-video.${fileExtension}`);
        setSourceSize(`${(blob.size / (1024 * 1024)).toFixed(1)} MB`);
        setUploadedVideo(file);
        setUploadedVideoUrl(objectUrl);
        setShowVideoCamera(false);
        setIsRecording(false);

        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach((t) => t.stop());
          videoStreamRef.current = null;
        }
        // Sunucuya yükle
        setUploadingVideo(true);
        setUploadedVideoFileUrl(null);
        try {
          const searchParams = new URLSearchParams(window.location.search);
          const trainingId = searchParams?.get("id");
          const formData = new FormData();
          formData.append("file", file);
          const queryParams = new URLSearchParams();
          if (trainingId) queryParams.set("trainingId", trainingId);
          queryParams.set("fileDescription", "Aidea Avatar Video");
          const response = await fetch(
            `${process.env.MW_API_URL}api/tts-avatar/upload-media?${queryParams.toString()}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${getToken()}` },
              body: formData,
            }
          );
          if (response.ok) {
            const data = await response.json();
            setUploadedVideoFileUrl(data.fileUrl);
          }
        } catch (err) {
          console.error("Error uploading recorded video:", err);
        } finally {
          setUploadingVideo(false);
          pendingVideoRef.current = false;
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    };

    const handleStopRecording = () => {
      mediaRecorderRef.current?.stop();
    };

    const handleRemoveSource = () => {
      if (uploadedVideoUrl) URL.revokeObjectURL(uploadedVideoUrl);
      setSourcePreview("");
      setSourceSize("");
      setUploadedVideo(null);
      setUploadedVideoUrl(null);
      setUploadedVideoFileUrl(null);
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach((t) => t.stop());
        videoStreamRef.current = null;
      }
      setShowVideoCamera(false);
      setIsRecording(false);
    };

    // ── Fotoğraf yükleme handler'ları ─────────────────────────────────────────
    const handlePhotoUpload = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // 500 MB üst sınırı: aşan dosyayı kabul etme
      if (isFileTooLarge(file)) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + fileTooLargeMessage() + "</span>",
          false
        );
        event.target.value = ""; // Input'u sıfırla
        return;
      }

      setSourcePhotoPreview(file.name);
      setSourcePhotoSize(`${(file.size / (1024 * 1024)).toFixed(1)} MB`);
      const objectUrl = URL.createObjectURL(file);
      setUploadedPhoto(file);
      setUploadedPhotoUrl(objectUrl);
      setUploadingPhoto(true);
      setUploadedPhotoFileUrl(null);
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const trainingId = searchParams?.get("id");
        const formData = new FormData();
        formData.append("file", file);
        const queryParams = new URLSearchParams();
        if (trainingId) queryParams.set("trainingId", trainingId);
        queryParams.set("fileDescription", "Aidea Avatar Photo");
        const response = await fetch(
          `${process.env.MW_API_URL}api/tts-avatar/upload-image?${queryParams.toString()}`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${getToken()}` },
            body: formData,
          }
        );
        if (response.ok) {
          const data = await response.json();
          setUploadedPhotoFileUrl(data.fileUrl);
        } else {
          throw new Error(i18n.t("FailedToUploadPhoto") || "Failed to upload photo");
        }
      } catch (error) {
        console.error("Error uploading photo:", error);
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("FailedToUploadPhotoRetry") || "Failed to upload photo. Please try again.") +
            "</span>",
          false
        );
      } finally {
        setUploadingPhoto(false);
      }
    };

    const handleStartCamera = async () => {
      setShowCamera(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          cameraVideoRef.current.play();
        }
      } catch (err) {
        console.error("Camera error:", err);
        setShowCamera(false);
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + (i18n.t("CameraAccessDenied") || "Camera access denied.") + "</span>",
          false
        );
      }
    };

    const handleCapturePhoto = () => {
      if (!cameraVideoRef.current) return;
      const canvas = document.createElement("canvas");
      canvas.width = cameraVideoRef.current.videoWidth;
      canvas.height = cameraVideoRef.current.videoHeight;
      canvas.getContext("2d").drawImage(cameraVideoRef.current, 0, 0);
      canvas.toBlob(async (blob) => {
        const file = new File([blob], "captured-photo.jpg", { type: "image/jpeg" });
        setSourcePhotoPreview("captured-photo.jpg");
        setSourcePhotoSize(`${(blob.size / (1024 * 1024)).toFixed(1)} MB`);
        const objectUrl = URL.createObjectURL(blob);
        setUploadedPhoto(file);
        setUploadedPhotoUrl(objectUrl);
        handleStopCamera();
        // Upload
        setUploadingPhoto(true);
        try {
          const searchParams = new URLSearchParams(window.location.search);
          const trainingId = searchParams?.get("id");
          const formData = new FormData();
          formData.append("file", file);
          const queryParams = new URLSearchParams();
          if (trainingId) queryParams.set("trainingId", trainingId);
          queryParams.set("fileDescription", "Aidea Avatar Photo");
          const response = await fetch(
            `${process.env.MW_API_URL}api/tts-avatar/upload-image?${queryParams.toString()}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${getToken()}` },
              body: formData,
            }
          );
          if (response.ok) {
            const data = await response.json();
            setUploadedPhotoFileUrl(data.fileUrl);
          }
        } catch (err) {
          console.error("Error uploading captured photo:", err);
        } finally {
          setUploadingPhoto(false);
        }
      }, "image/jpeg");
    };

    const handleStopCamera = () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((t) => t.stop());
        cameraStreamRef.current = null;
      }
      setShowCamera(false);
    };

    const handleRemovePhoto = () => {
      if (uploadedPhotoUrl) URL.revokeObjectURL(uploadedPhotoUrl);
      setSourcePhotoPreview("");
      setSourcePhotoSize("");
      setUploadedPhoto(null);
      setUploadedPhotoUrl(null);
      setUploadedPhotoFileUrl(null);
    };

    // ── Avatar oluşturma job polling ──────────────────────────────────────────
    const checkAvatarJobStatus = useCallback(
      async (jId) => {
        try {
          const res = await fetch(`${process.env.MW_API_URL}api/tts-avatar/jobs/${jId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (!res.ok) throw new Error("Job status fetch failed");
          const data = await res.json();

          if (data.state === "completed") {
            clearTimeout(avatarPollInterval.current);
            avatarPollInterval.current = null;
            setAvatarCreating(false);
            setAvatarJobId(null);
            const list = (await loadAvatars(selectedModel)) || [];
            // Yeni avatarı bul: önce job sonucundaki id, yoksa aynı isimli en
            // yeni kayıt. Bulunursa stock listesinde seçili + görünür hale gelir.
            const result = data.result || data.returnvalue || {};
            const newId =
              result.avatarId || result.lipsyncAvatarId || result.id || data.avatarId || null;
            const createdName = avatarName.trim().toLowerCase();
            const created =
              (newId != null && list.find((a) => String(a.id) === String(newId))) ||
              [...list]
                .reverse()
                .find((a) => (a.name || "").trim().toLowerCase() === createdName) ||
              null;
            pendingNewAvatarRef.current = created;
            setAvatarFilter("all");
            setShowAllAvatars(true);
            setSourceChosen(true);
            setSelectedAvatarType("stock");
            onServerModal?.(
              i18n.t("Success"),
              "<span>" +
                (i18n.t("AvatarCreatedSuccess") || "Avatar created successfully.") +
                "</span>",
              false
            );
          } else if (data.state === "failed") {
            clearTimeout(avatarPollInterval.current);
            avatarPollInterval.current = null;
            setAvatarCreating(false);
            // failedReason varsa onu göster (ör. "Failed to download video: 404"),
            // yoksa genel hata metnine düş.
            onServerModal?.(
              i18n.t("error.warning"),
              "<span>" +
                (mapAiProviderErrorMessage(data.failedReason) ||
                  data.failedReason ||
                  i18n.t("AvatarCreationFailed") ||
                  "Avatar creation failed.") +
                "</span>",
              false
            );
          } else {
            avatarPollInterval.current = setTimeout(() => checkAvatarJobStatus(jId), 3000);
          }
        } catch {
          clearTimeout(avatarPollInterval.current);
          avatarPollInterval.current = null;
          setAvatarCreating(false);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("AvatarCreationFailed") || "Avatar creation failed.") + "</span>",
            false
          );
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedModel, loadAvatars, onServerModal, avatarName]
    );

    // ── AI avatar job poller ──────────────────────────────────────────────────
    // POST /api/tts-avatar/ai-create artık 202 + jobId + placeholder döner.
    // Bu poll worker tarafından progress=5/15/85/100 ile ilerletilen job'u takip
    // edip COMPLETED olunca final videoUrl'i aiResult'a yazar.
    const pollAiAvatarJob = useCallback(
      async (jId) => {
        try {
          const res = await fetch(`${process.env.MW_API_URL}api/tts-avatar/jobs/${jId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (!res.ok) throw new Error("AI job status fetch failed");
          const data = await res.json();
          const state = (data.state || data.status || "").toLowerCase();
          const progress = Number(data.progress) || 0;
          // Worker stage etiketleri progress'e gömülü değil; varsa data.stage'i kullan,
          // yoksa progress aralığına göre okunabilir bir etiket türet.
          const stage =
            data.stage ||
            data.message ||
            (progress < 15
              ? "queued"
              : progress < 50
                ? "prompt-refinement"
                : progress < 85
                  ? "generating-video"
                  : "finalizing");

          if (state === "completed" || state === "success") {
            clearTimeout(avatarPollInterval.current);
            avatarPollInterval.current = null;
            setAvatarCreating(false);
            setAvatarJobId(null);
            setAiJobInfo(null);
            const result = data.result || data || {};
            setAiResult({
              // ai-avatar job'u: bu avatarla üretilen video sahneye eklenince approve edilir.
              jobId: jId,
              videoUrl: result.videoUrl || result.finalVideoUrl || null,
              refinedPrompt: result.refinedPrompt || "",
              provider: result.provider || aiProvider,
              lipsyncAvatarId: result.lipsyncAvatarId ?? null,
              metadata: result.metadata || null,
              threadId: result.threadId || null,
            });
          } else if (state === "failed" || state === "error") {
            clearTimeout(avatarPollInterval.current);
            avatarPollInterval.current = null;
            setAvatarCreating(false);
            setAvatarJobId(null);
            setAiJobInfo(null);
            // Job failure: backend `failedReason` veya `error.message` döndürebilir.
            // İçerik politikası / content filter hatalarını teknik mesaj yerine
            // okunabilir bir uyarıya çevir.
            const rawReason = (
              data.failedReason ||
              data.error?.message ||
              data.error ||
              ""
            ).toString();
            const lower = rawReason.toLowerCase();
            const isContentFilter =
              !!classifyAiProviderError(rawReason) ||
              lower.includes("content management policy") ||
              lower.includes("content filter") ||
              lower.includes("content policy") ||
              lower.includes("filtered due to") ||
              lower.includes("policy violation") ||
              lower.includes("safety");
            const friendly = isContentFilter
              ? i18n.t("AvatarAiContentFilterError") ||
                "İçerik uygun değil. Lütfen prompt veya seçimlerinizi yumuşatıp tekrar deneyin."
              : rawReason || i18n.t("AvatarCreationFailed") || "Avatar creation failed.";
            onServerModal?.(i18n.t("error.warning"), "<span>" + friendly + "</span>", false);
          } else {
            // Hâlâ devam ediyor — UI'daki progress bar'ı güncelle ve 3sn sonra tekrar dene
            setAiJobInfo((prev) => (prev ? { ...prev, progress, stage } : prev));
            avatarPollInterval.current = setTimeout(() => pollAiAvatarJob(jId), 3000);
          }
        } catch {
          // Geçici hata; bir sonraki tick'te tekrar dene
          avatarPollInterval.current = setTimeout(() => pollAiAvatarJob(jId), 3000);
        }
      },
      [aiProvider, onServerModal]
    );

    // ── Avatar kaydet ─────────────────────────────────────────────────────────
    const handleSaveAvatar = async () => {
      if (!avatarName.trim()) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + (i18n.t("AvatarNameRequired") || "Please enter an avatar name.") + "</span>",
          false
        );
        return;
      }

      const organizationId = getOrganizationId();
      setAvatarCreating(true);

      // ── AI ile üretim ─────────────────────────────────────────────────────
      // POST /api/tts-avatar/ai-create — payload şeması:
      //   mode = "prompt"     → { prompt }
      //   mode = "parameters" → { parameters: {...} }
      // Yanıt 202: { jobId, videoUrl (placeholder), metadataUrl, trainingId }.
      // pollAiAvatarJob ile takip; tamamlanınca final videoUrl aiResult'a yazılır.
      if (sourceType === "ai") {
        // "Diğer" seçilen alanlarda enum yerine kullanıcının girdiği serbest metni
        // koy, ardından boş alanları parameters objesinden ele.
        const cleanParams = Object.fromEntries(
          Object.entries(aiProperties)
            .map(([k, v]) => [k, resolveAiField(v, aiCustom[k])])
            .filter(([, v]) => v && v.trim())
        );
        // Aksesuarlar çoklu seçim (aiAccessories) + "Custom" serbest metni
        // (aiCustom.accessories) → virgülle birleşip parameters.accessories'e yazılır.
        const accessoryList = [
          ...aiAccessories.filter((v) => v !== AI_OTHER_VALUE),
          ...(aiAccessories.includes(AI_OTHER_VALUE) ? [(aiCustom.accessories || "").trim()] : []),
        ].filter(Boolean);
        if (accessoryList.length) {
          cleanParams.accessories = accessoryList.join(", ");
        }
        // DTO'da `background` alanı YOK — sahne/arka plan seçimi additionalDetails'e
        // yazılmalı, aksi halde backend tarafından sessizce yok sayılır. Seçilen arka
        // planı okunabilir bir ibareye çevirip additionalDetails'e katıyoruz.
        if (cleanParams.background) {
          const bgText = `${cleanParams.background} background`;
          cleanParams.additionalDetails = [cleanParams.additionalDetails, bgText]
            .filter(Boolean)
            .join(" · ");
          delete cleanParams.background;
        }
        // organizationId JWT'den geliyor (getOrganizationId → decodeJWT → claim);
        // from-video/from-image akışlarındaki ile aynı kaynak. Her iki modda da
        // basePayload üzerinden eklendiği için tek noktada görünür.
        // trainingId URL query param'ından (`?id=...`) okunuyor — aynı dosyadaki
        // upload-image / staged-video akışlarıyla tutarlı.
        const trainingIdFromUrl =
          new URLSearchParams(window.location.search).get("id") || undefined;
        const basePayload = {
          provider: aiProvider, // "gemini" | "gemini-fast" | "grok"
          // Language of the avatar's fixed line; backend maps unsupported codes to tr.
          language: getContentLanguage(),
          name: avatarName.trim(),
          // description = "Title / Role" input'undaki ünvan
          description: avatarRole.trim() || undefined,
          gender,
          category: avatarCategory,
          // "+ Özel" seçiliyse aiStyle === AI_OTHER_VALUE; bu durumda serbest metin
          // (aiCustom.style) gider, boşsa güvenli default "realistic".
          style: resolveAiField(aiStyle, aiCustom.style) || "realistic",
          // Poz top-level; geçerli değerler yalnızca seated | standing.
          pose: aiPose === "standing" ? "standing" : "seated",
          organizationId: organizationId || undefined,
          trainingId: trainingIdFromUrl,
          // Top-level görünüm alanları — her iki modda da geçerli, boşsa gönderilmez.
          ethnicity: aiAppearance.ethnicity.trim() || undefined,
          skinTone: resolveAiField(aiAppearance.skinTone, aiCustom.skinTone) || undefined,
          facialFeatures: aiAppearance.facialFeatures.trim() || undefined,
        };
        const payload =
          aiMode === "prompt"
            ? { ...basePayload, mode: "prompt", prompt: aiPrompt.trim() }
            : { ...basePayload, mode: "parameters", parameters: cleanParams };

        try {
          const res = await fetch(`${process.env.MW_API_URL}api/tts-avatar/ai-create`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            if (checkInsufficientBalance(errData)) {
              setAvatarCreating(false);
              return;
            }
            throw new Error(errData?.message || "Failed to create AI avatar");
          }
          const data = await res.json();
          if (data?.jobId) {
            // 202 — placeholder videoUrl gelmiş olsa bile poll'u başlatıyoruz.
            // Tamamlanınca pollAiAvatarJob aiResult'ı set edip preview'ı açacak.
            setAvatarJobId(data.jobId);
            setAiJobInfo({
              jobId: data.jobId,
              progress: 0,
              stage: "queued",
              placeholderUrl: data.videoUrl || null,
              message: data.message || "",
            });
            // avatarCreating false; ilerleme UI'sı aiJobInfo üzerinden render ediliyor.
            setAvatarCreating(false);
            avatarPollInterval.current = setTimeout(() => pollAiAvatarJob(data.jobId), 2000);
          } else if (data?.videoUrl) {
            // Eski senkron şekil için fallback (artık beklenmiyor ama güvenli kalsın).
            setAvatarCreating(false);
            setAiResult({
              videoUrl: data.videoUrl,
              refinedPrompt: data.refinedPrompt || "",
              provider: data.provider || aiProvider,
              lipsyncAvatarId: data.lipsyncAvatarId ?? null,
              metadata: data.metadata || null,
              threadId: data.threadId || null,
            });
          } else {
            // videoUrl yok, jobId yok — beklenmeyen şekil
            setAvatarCreating(false);
            onServerModal?.(
              i18n.t("error.warning"),
              "<span>" + (i18n.t("AvatarCreationFailed") || "Avatar creation failed.") + "</span>",
              false
            );
          }
        } catch {
          setAvatarCreating(false);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("AvatarCreationFailed") || "Avatar creation failed.") + "</span>",
            false
          );
        }
        return;
      }

      if (sourceType === "video") {
        if (!uploadedVideo) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("PleaseUploadVideo") || "Please upload a video first.") + "</span>",
            false
          );
          setAvatarCreating(false);
          return;
        }
        try {
          const formData = new FormData();
          formData.append("file", uploadedVideo);
          formData.append("name", avatarName.trim());
          formData.append("gender", gender);
          if (avatarRole.trim()) formData.append("description", avatarRole.trim());
          if (organizationId) formData.append("organizationId", organizationId);

          const res = await fetch(
            `${process.env.MW_API_URL}api/tts-avatar/custom-avatar/from-video`,
            { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: formData }
          );
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            if (checkInsufficientBalance(errData)) {
              setAvatarCreating(false);
              return;
            }
            throw new Error(errData?.message || "Failed to create avatar from video");
          }
          // 202 Accepted → { jobId, room, message }. Senkron değil: job'u
          // GET /api/tts-avatar/jobs/:jobId ile poll et (from-image ile aynı akış).
          // Tamamlanınca checkAvatarJobStatus loadAvatars ile listeyi (thumbnailUrl
          // dâhil) tazeler ve stock sekmesine geçer.
          const data = await res.json();
          setAvatarJobId(data.jobId);
          avatarPollInterval.current = setTimeout(() => checkAvatarJobStatus(data.jobId), 3000);
        } catch {
          setAvatarCreating(false);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("AvatarCreationFailed") || "Avatar creation failed.") + "</span>",
            false
          );
        }
      } else {
        if (!uploadedPhoto) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("PleaseUploadPhoto") || "Please upload a photo first.") + "</span>",
            false
          );
          setAvatarCreating(false);
          return;
        }
        try {
          const formData = new FormData();
          formData.append("file", uploadedPhoto);
          formData.append("name", avatarName.trim());
          formData.append("gender", gender);
          formData.append("provider", photoAnimateProvider);
          if (avatarRole.trim()) formData.append("description", avatarRole.trim());
          if (organizationId) formData.append("organizationId", organizationId);

          const res = await fetch(
            `${process.env.MW_API_URL}api/tts-avatar/custom-avatar/from-image`,
            { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: formData }
          );
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            if (checkInsufficientBalance(errData)) {
              setAvatarCreating(false);
              return;
            }
            throw new Error(errData?.message || "Failed to submit avatar from image");
          }
          const data = await res.json();
          setAvatarJobId(data.jobId);
          avatarPollInterval.current = setTimeout(() => checkAvatarJobStatus(data.jobId), 3000);
        } catch {
          setAvatarCreating(false);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("AvatarCreationFailed") || "Avatar creation failed.") + "</span>",
            false
          );
        }
      }
    };

    // ── Job polling ───────────────────────────────────────────────────────────
    const stopJobPolling = useCallback(() => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
        pollInterval.current = null;
      }
      setPolling(false);
    }, []);

    const checkJobStatus = useCallback(
      async (jId, prompt, statusEndpoint) => {
        try {
          const url = statusEndpoint || `${process.env.MW_API_URL}api/tts-avatar/jobs/${jId}`;
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (response.ok) {
            const jobData = await response.json();
            const jobState = jobData.state || jobData.status;
            setJobStatus(jobState);
            const completedVideoUrl =
              jobData.result?.videoUrl ||
              jobData.result?.url ||
              jobData.finalVideoUrl ||
              jobData.videoUrl;
            if (jobState === "completed" && completedVideoUrl) {
              stopJobPolling();
              const finalVideoUrl = completedVideoUrl;
              let fname = "AI-Avatar-" + prompt;
              if (prompt.length > 50) fname = "AI-Avatar-" + prompt.substr(0, 50);
              const idd = ID_PREFIX_FILE + Date.now();
              setVideoUrl(finalVideoUrl);
              setAvatarLoaded(true);
              setProcessing(false);
              setGeneratedVideoFileName(fname);
              setGeneratedVideoFileId(idd);
              setCurrentView("result");
            } else if (jobState === "failed") {
              stopJobPolling();
              setProcessing(false);
              onServerModal?.(
                i18n.t("FileModal.APIProviders.error"),
                "<span>Avatar generation failed</span>",
                false
              );
            } else {
              pollInterval.current = setTimeout(
                () => checkJobStatus(jId, prompt, statusEndpoint),
                3000
              );
            }
          } else {
            throw new Error("Failed to check job status");
          }
        } catch (error) {
          console.error("Error checking job status:", error);
          stopJobPolling();
          setProcessing(false);
          onServerModal?.(
            i18n.t("FileModal.APIProviders.error"),
            "<span>Error checking avatar generation status</span>",
            false
          );
        }
      },
      [stopJobPolling, onElementSelected, onServerModal]
    );

    // ── Sahne narration text'ini yükle ─────────────────────────────────────────
    // Mirrors InfographicSceneModal.handleFetchSceneText so the button labelled
    // the same thing finds text in the same cases.
    //
    // The narrower version this replaces required an audio row with BOTH a
    // filePath AND narrationTextOriginal, so a scene whose narration was stored
    // under narrationText (or voiceText) reported "no text in this scene" even
    // though the infographic modal read it fine.
    const loadSceneNarrationText = useCallback(async () => {
      try {
        const data = await SceneAudioService.getSceneAudios();
        const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
        const scene = scenes.find((s) => s.navId === navItemSelected);
        const audios = Array.isArray(scene?.audios) ? scene.audios : [];
        const textOf = (a) => a?.narrationTextOriginal || a?.narrationText || a?.voiceText || null;
        // Prefer an audio that actually carries text over merely the first one.
        const withText = audios.find((a) => textOf(a));
        return withText ? textOf(withText) : null;
      } catch (error) {
        console.error("Error loading narration text:", error);
        return null;
      }
    }, [navItemSelected]);

    // "Metinden Oluştur" (yeni anlatım sesi) moduna girince sahnenin anlatım
    // metnini AIVoiceTab gibi otomatik doldur. Kullanıcı metni elle yazmışsa
    // (narrationText doluysa) ezmeyiz; metin alanı boşken sahneden çekiyoruz.
    useEffect(() => {
      if (narrationSource !== "text") return;
      if (narrationText.trim()) return;
      let cancelled = false;
      setSceneTextLoading(true);
      (async () => {
        try {
          const text = await loadSceneNarrationText();
          if (!cancelled && text) {
            setNarrationText(text);
          }
        } finally {
          if (!cancelled) setSceneTextLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [narrationSource, navItemSelected, loadSceneNarrationText]);

    // "Sahne Sesinden Oluştur" modunda seslendirilen metni SADECE gösterim için
    // (read-only) yükle. Sahne sesinin kendi alanları doluysa fetch'e gerek yok;
    // boşsa API'den (narrationTextOriginal) çekip sceneAudioText'e yazıyoruz.
    useEffect(() => {
      if (narrationSource !== "sceneAudio" || !selectedSceneAudio) {
        setSceneAudioText("");
        return;
      }
      const inline =
        selectedSceneAudio.text || selectedSceneAudio.narrationText || selectedSceneAudio.content;
      if (inline) {
        setSceneAudioText("");
        return;
      }
      let cancelled = false;
      (async () => {
        const text = await loadSceneNarrationText();
        if (!cancelled) setSceneAudioText(text || "");
      })();
      return () => {
        cancelled = true;
      };
    }, [narrationSource, selectedSceneAudio, loadSceneNarrationText]);

    // ── Avatar videosu oluştur ────────────────────────────────────────────────
    const handleCreate = async () => {
      if (isTalking) {
        talkingPanelRef.current?.submit();
        return;
      }
      // Metin modu için text kontrolü, sahne sesi modu için selectedSceneAudio kontrolü
      if (narrationSource === "text" && !narrationText.trim()) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoText") + "</span>",
          false
        );
        return;
      }
      if (
        selectedModel === "tavus" &&
        narrationSource === "text" &&
        narrationText.trim().length < 45
      ) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("FileModal.APIProviders.AIAvatars.ErrorTextTooShort") ||
              "Tavus için metin en az 45 karakter olmalıdır.") +
            "</span>",
          false
        );
        return;
      }
      if (narrationSource === "sceneAudio" && !selectedSceneAudio) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + (i18n.t("PleaseSelectSceneAudio") || "Lütfen sahne sesini seçin.") + "</span>",
          false
        );
        return;
      }
      if (!selectedStockAvatar && selectedAvatarType === "stock") {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoAvatar") + "</span>",
          false
        );
        return;
      }
      // Custom (kendin oluştur) modda doğrudan üretim: önce persona/avatar üretilmiş
      // (selectedStockAvatar.videoUrl) ya da bir video yüklenmiş olmalı. Aksi halde
      // lipsync isteğine kaynak video eklenemez.
      if (
        selectedAvatarType === "custom" &&
        !selectedStockAvatar?.videoUrl &&
        !uploadedVideo &&
        !uploadedPhoto
      ) {
        onServerModal?.(
          i18n.t("error.warning"),
          "<span>" +
            (i18n.t("AvatarGenerateFirst") ||
              "Please generate your avatar first (Generate persona preview).") +
            "</span>",
          false
        );
        return;
      }

      setProcessing(true);
      setAvatarLoaded(false);
      setVideoUrl("");

      try {
        const searchParams = new URLSearchParams(window.location.search);
        const id = searchParams?.get("id");
        const navId = navItemSelected;
        const sceneIndex = String((navItemsIds?.indexOf(navItemSelected) ?? -1) + 1);

        let endpoint = "";
        let payload = {};

        if (selectedModel === "azure-video") {
          if (!selectedVoiceId) {
            onServerModal?.(
              i18n.t("error.warning"),
              "<span>" + i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoVoice") + "</span>",
              false
            );
            setProcessing(false);
            return;
          }
          endpoint = `${process.env.MW_API_URL}api/tts-avatar/generate`;
          payload = {
            avatarId: selectedStockAvatar.id,
            text: narrationText.trim(),
            transparent: transparentBg,
            aspectRatio: avatarAspectRatio,
            language: selectedLanguage,
            voiceName: selectedVoiceId,
            gestures: selectedStockAvatar.metadata?.supportedGestures || [],
            idealStudioTrainingId: id || "default",
            trainingId: id || "default",
            avatarImageUrl: selectedStockAvatar.thumbnailUrl,
            navId,
            sceneIndex,
          };
        } else if (selectedModel === "tavus") {
          // Tavus için sahne sesi modu kontrolü
          if (selectedVoice === "keep" && narrationSource === "sceneAudio") {
            // Sahne sesi validasyonu
            if (!selectedSceneAudio || !selectedSceneAudio.file) {
              onServerModal?.(
                i18n.t("error.warning"),
                "<span>" +
                  (i18n.t("PleaseSelectSceneAudio") || "Lütfen sahne sesini seçin.") +
                  "</span>",
                false
              );
              setProcessing(false);
              return;
            }

            // Tavus generate endpoint'i audioSourceUrl parametresi ile
            const queryParams = new URLSearchParams();
            queryParams.set("audioSourceUrl", selectedSceneAudio.file);
            const sceneAudioDuration = getSelectedSceneAudioDuration();
            if (sceneAudioDuration != null) {
              queryParams.set("durationSeconds", String(sceneAudioDuration));
            }

            endpoint = `${process.env.MW_API_URL}api/tts-avatar/tavus/generate?${queryParams.toString()}`;
            payload = {
              replicaId: selectedStockAvatar.azureAvatarId,
              transparentBackground: transparentBg,
              aspectRatio: avatarAspectRatio,
              trainingId: id || "default",
              idealStudioTrainingId: id || "default",
              navId,
              sceneIndex,
              avatarImageUrl: selectedStockAvatar.thumbnailUrl,
            };

            const response = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getToken()}`,
              },
              body: JSON.stringify(payload),
            });
            if (response.ok) {
              const result = await response.json();
              const jId = result.data?.jobId || result.jobId;
              if (jId) {
                setJobId(jId);
                setProcessing(false);
                setJobStatus("submitted");
                setPolling(true);
                const displayText = selectedSceneAudio?.name || "Scene Audio";
                checkJobStatus(jId, displayText);
              } else throw new Error("No job ID received");
            } else {
              const errorData = await response.json().catch(() => ({}));
              if (checkInsufficientBalance(errorData)) {
                setProcessing(false);
                return;
              }
              throw new Error(errorData.error?.message || "Failed to submit avatar generation job");
            }
            return;
          }

          // Text modu - "Anlatım sesini koru" seçiliyse voice profile kontrolü
          if (selectedVoice === "keep" && narrationSource === "text") {
            if (!voiceProfile || !profileService || !profileVoiceName) {
              onServerModal?.(
                i18n.t("error.warning"),
                "<span>" +
                  (i18n.t("VoiceProfileNotFound") ||
                    "Voice profile not found. Please configure voice settings in training settings.") +
                  "</span>",
                false
              );
              setProcessing(false);
              return;
            }

            // Tavus generate endpoint'i voice profile'dan TTS parametreleri ile
            const queryParams = new URLSearchParams();
            queryParams.set("text", narrationText.trim());
            queryParams.set("ttsProvider", profileService);
            queryParams.set("voiceId", profileVoiceId);
            queryParams.set("voiceLanguage", profileLanguage);

            endpoint = `${process.env.MW_API_URL}api/tts-avatar/tavus/generate?${queryParams.toString()}`;
            payload = {
              replicaId: selectedStockAvatar.azureAvatarId,
              transparentBackground: transparentBg,
              aspectRatio: avatarAspectRatio,
              trainingId: id || "default",
              navId,
              sceneIndex,
              avatarImageUrl: selectedStockAvatar.thumbnailUrl,
            };

            const response = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getToken()}`,
              },
              body: JSON.stringify(payload),
            });
            if (response.ok) {
              const result = await response.json();
              const jId = result.data?.jobId || result.jobId;
              if (jId) {
                setJobId(jId);
                setProcessing(false);
                setJobStatus("submitted");
                setPolling(true);
                checkJobStatus(jId, narrationText);
              } else throw new Error("No job ID received");
            } else {
              const errorData = await response.json().catch(() => ({}));
              if (checkInsufficientBalance(errorData)) {
                setProcessing(false);
                return;
              }
              throw new Error(errorData.error?.message || "Failed to submit avatar generation job");
            }
            return;
          }

          // Normal Tavus endpoint (different voice modu)
          if (selectedVoice !== "keep") {
            // "Different voice" modunda TTS provider ve voice zorunlu
            if (!ttsProvider) {
              onServerModal?.(
                i18n.t("error.warning"),
                "<span>" +
                  (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoTTSProvider") ||
                    "Please select TTS provider") +
                  "</span>",
                false
              );
              setProcessing(false);
              return;
            }
            if (!ttsSelectedVoice) {
              onServerModal?.(
                i18n.t("error.warning"),
                "<span>" +
                  (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoVoice") ||
                    "Please select a voice") +
                  "</span>",
                false
              );
              setProcessing(false);
              return;
            }

            // Tavus generate endpoint'i TTS parametreleri ile
            const queryParams = new URLSearchParams();
            queryParams.set("text", narrationText.trim());
            queryParams.set("ttsProvider", ttsProvider);
            queryParams.set("voiceId", ttsSelectedVoice.id);
            queryParams.set("voiceLanguage", ttsSelectedVoice.language);

            endpoint = `${process.env.MW_API_URL}api/tts-avatar/tavus/generate?${queryParams.toString()}`;
            payload = {
              replicaId: selectedStockAvatar.azureAvatarId,
              transparentBackground: transparentBg,
              aspectRatio: avatarAspectRatio,
              trainingId: id || "default",
              navId,
              sceneIndex,
              avatarImageUrl: selectedStockAvatar.thumbnailUrl,
            };

            const response = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getToken()}`,
              },
              body: JSON.stringify(payload),
            });
            if (response.ok) {
              const result = await response.json();
              const jId = result.data?.jobId || result.jobId;
              if (jId) {
                setJobId(jId);
                setProcessing(false);
                setJobStatus("submitted");
                setPolling(true);
                checkJobStatus(jId, narrationText);
              } else throw new Error("No job ID received");
            } else {
              const errorData = await response.json().catch(() => ({}));
              if (checkInsufficientBalance(errorData)) {
                setProcessing(false);
                return;
              }
              throw new Error(errorData.error?.message || "Failed to submit avatar generation job");
            }
            return;
          }
        } else if (selectedModel === "aidea") {
          // Aidea validasyon
          if (!uploadedAudio && narrationSource !== "sceneAudio") {
            // "Anlatım sesini koru" modunda voiceProfile kontrolü
            if (selectedVoice === "keep") {
              if (!voiceProfile || !profileService || !profileVoiceName) {
                onServerModal?.(
                  i18n.t("error.warning"),
                  "<span>" +
                    (i18n.t("VoiceProfileNotFound") ||
                      "Voice profile not found. Please configure voice settings in training settings.") +
                    "</span>",
                  false
                );
                setProcessing(false);
                return;
              }
            } else {
              // Manuel seçim modu: TTS provider + voice zorunlu
              if (!ttsProvider) {
                onServerModal?.(
                  i18n.t("error.warning"),
                  "<span>" +
                    (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoTTSProvider") ||
                      "Please select TTS provider") +
                    "</span>",
                  false
                );
                setProcessing(false);
                return;
              }
              if (!ttsSelectedVoice) {
                onServerModal?.(
                  i18n.t("error.warning"),
                  "<span>" +
                    (i18n.t("FileModal.APIProviders.AIAvatars.ErrorNoVoice") ||
                      "Please select a voice") +
                    "</span>",
                  false
                );
                setProcessing(false);
                return;
              }
            }
          }

          // Sahne sesi validasyonu
          if (narrationSource === "sceneAudio") {
            if (!selectedSceneAudio || !selectedSceneAudio.file) {
              onServerModal?.(
                i18n.t("error.warning"),
                "<span>" +
                  (i18n.t("PleaseSelectSceneAudio") || "Lütfen sahne sesini seçin.") +
                  "</span>",
                false
              );
              setProcessing(false);
              return;
            }
          }

          const queryParams = new URLSearchParams();
          if (id) queryParams.set("trainingId", id);
          if (navId) queryParams.set("navId", navId);
          queryParams.set("sceneIndex", sceneIndex);
          queryParams.set("removeBackground", transparentBg ? "true" : "false");
          // En/boy oranı kullanıcı seçimi; backend yalnızca "1:1" ise lipsync
          // çıktısını kareye kırpar, "16:9" ise native oranı korur.
          queryParams.set("aspectRatio", avatarAspectRatio);
          // Lipsync engine her zaman gönder. "auto" → pixverse,
          // "custom" → kullanıcının seçtiği (pixverse | fal-ai | sync-lipsync | heygen-lipsync).
          // Backend ApiQuery enum (processLipsync/v2): 'fal-ai' | 'pixverse' | 'sync-lipsync' | 'heygen-lipsync'.
          const resolvedLipsync = lipsyncMode === "custom" ? lipsyncProvider : "pixverse";
          queryParams.set("lipsyncProvider", resolvedLipsync);
          if (selectedStockAvatar?.thumbnailUrl)
            queryParams.set("avatarImageUrl", selectedStockAvatar.thumbnailUrl);

          const formData = new FormData();
          if (selectedStockAvatar?.videoUrl) {
            const videoResponse = await fetch(
              selectedStockAvatar.videoUrl,
              credentialsFor(selectedStockAvatar.videoUrl)
            );
            const videoBlob = await videoResponse.blob();
            formData.append("video", videoBlob, "avatar-video.mov");
          } else if (uploadedVideo) {
            formData.append("video", uploadedVideo);
          }

          // Sahne sesinden oluşturma modu
          if (narrationSource === "sceneAudio" && selectedSceneAudio) {
            // Sahne sesinin URL'ini audioUrl parametresine ekle
            queryParams.set("audioUrl", selectedSceneAudio.file);
            const sceneAudioDuration = getSelectedSceneAudioDuration();
            if (sceneAudioDuration != null) {
              queryParams.set("durationSeconds", String(sceneAudioDuration));
            }
            // TTS parametreleri gerekmez, ses dosyası zaten mevcut
          } else if (uploadedAudio) {
            // Manuel ses yükleme modu
            formData.append("audio", uploadedAudio);
            const uploadedAudioDuration = await getMediaDuration(uploadedAudio);
            if (uploadedAudioDuration != null) {
              queryParams.set("durationSeconds", String(uploadedAudioDuration));
            }
          } else if (previewUrl && previewMode === "full") {
            // Kullanıcı "Tamamını üret" ile metnin TAMAMINI seslendirdiyse: TTS'i
            // sunucuda tekrar çalıştırmak yerine o üretilmiş sesi doğrudan `audio`
            // dosyası olarak gönderiyoruz (curl: -F 'audio=@audio.mp3'). Böylece
            // dinlenen önizleme ile videodaki ses birebir aynı olur.
            // NOT: "5 sn üret" (short) önizlemesi yalnızca dinleme amaçlıdır; burada
            // kullanılmaz, aşağıdaki text-to-speech dalına düşülüp metnin tamamı üretilir.
            const previewResp = await fetch(previewUrl, credentialsFor(previewUrl));
            const previewBlob = await previewResp.blob();
            formData.append("audio", previewBlob, "voice-preview.mp3");
            const previewDur =
              previewDuration > 0 ? previewDuration : await getMediaDuration(previewBlob);
            if (previewDur != null && previewDur > 0) {
              queryParams.set("durationSeconds", String(Math.round(previewDur)));
            }
          } else {
            // Text-to-speech modu (önizleme üretilmediyse sunucu metinden üretir)
            queryParams.set("text", narrationText.trim());

            // "Anlatım sesini koru" modunda voiceProfile'dan otomatik al
            const keepProfileVoice = selectedVoice === "keep" && voiceProfile && profileService;
            if (keepProfileVoice) {
              queryParams.set("ttsProvider", profileService);
              queryParams.set("voiceId", profileVoiceId);
              queryParams.set("voiceLanguage", profileLanguage);
            } else {
              // Manuel seçim
              queryParams.set("ttsProvider", ttsProvider);
              queryParams.set("voiceId", ttsSelectedVoice.id);
              queryParams.set("voiceLanguage", ttsSelectedVoice.language);
            }
            // ElevenLabs ayarları istekte giden (etkin) sağlayıcıya göre eklenir —
            // "sesi koru" modunda bu profileService'tir, ttsProvider değil.
            const effectiveTtsProvider = keepProfileVoice ? profileService : ttsProvider;
            if (effectiveTtsProvider === "elevenlabs") {
              queryParams.set(
                "elevenLabsModel",
                keepProfileVoice
                  ? normalizeElevenLabsModel(voiceProfile?.elevenLabsModel)
                  : ttsElevenLabsModel
              );
              queryParams.set("stability", "0.5");
              queryParams.set("similarity", "0.75");
              queryParams.set("styleExaggeration", "0.0");
              queryParams.set("speakerBoost", "true");
            }
          }

          const response = await fetch(
            `${process.env.MW_API_URL}api/tts-avatar/processLipsync/v2?${queryParams.toString()}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${getToken()}` },
              body: formData,
            }
          );
          if (response.ok) {
            const result = await response.json();
            if (result.jobId) {
              setJobId(result.jobId);
              setProcessing(false);
              setJobStatus("submitted");
              setPolling(true);
              // Sahne sesi modunda ses adını, metin modunda metni kullan
              const displayText =
                narrationSource === "sceneAudio"
                  ? selectedSceneAudio?.name || "Scene Audio"
                  : narrationText;
              const lipsyncStatusUrl = `${process.env.MW_API_URL}api/tts-avatar/processLipsync/v2/${result.jobId}`;
              checkJobStatus(result.jobId, displayText, lipsyncStatusUrl);
            } else throw new Error("No job ID received");
          } else {
            const errorData = await response.json().catch(() => ({}));
            if (checkInsufficientBalance(errorData)) {
              setProcessing(false);
              return;
            }
            throw new Error(errorData.message || "Failed to submit lipsync job");
          }
          return;
        }

        // Azure / Tavus
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          const result = await response.json();
          const jId = result.data?.jobId || result.jobId;
          if (jId) {
            setJobId(jId);
            setProcessing(false);
            setJobStatus("submitted");
            setPolling(true);
            checkJobStatus(jId, narrationText);
          } else throw new Error("No job ID received");
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (checkInsufficientBalance(errorData)) {
            setProcessing(false);
            return;
          }
          const errorMessage =
            errorData?.error?.message ||
            errorData?.message ||
            errorData?.error ||
            (typeof errorData === "string" ? errorData : null) ||
            "Failed to submit avatar generation job";
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error("Error generating avatar:", error);
        setProcessing(false);
        onServerModal?.(
          i18n.t("FileModal.APIProviders.error"),
          "<span>" + error.message + "</span>",
          false
        );
      }
    };

    // ── Dışarıya aç (footer butonu için) ─────────────────────────────────────
    useImperativeHandle(ref, () => ({ handleCreate, polling, processing, currentView }), [
      handleCreate,
      polling,
      processing,
      currentView,
    ]);

    // RENDER — orijinal tasarım korunuyor
    // ─────────────────────────────────────────────────────────────────────────

    // ── Doğal konuşan video işi (süren / biten / başarısız) ────────────────────
    if (isTalking && talkingJob) {
      return (
        <div className="add-sound-modal__avatar-tab">
          <TalkingJobView
            job={talkingJob}
            soundList={soundList}
            onClose={onClose}
            onNavigate={onNavItemSelected}
          />
        </div>
      );
    }

    // ── Sonuç view'i ────────────────────────────────────────────────────────
    if (currentView === "result" && videoUrl) {
      return (
        <div className="add-sound-modal__avatar-tab">
          <div style={{ padding: "20px" }}>
            <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "20px", fontWeight: "600" }}>
              {i18n.t("AvatarVideoCreated") || "Avatar Video Oluşturuldu"}
            </h3>
            <div style={{ marginBottom: "24px" }}>
              {/* Height-capped, width follows: 9:16 / 1:1 results stay fully
                  visible instead of stretching far below the fold. */}
              <video
                controls
                src={videoUrl}
                style={{
                  display: "block",
                  width: "auto",
                  maxWidth: "100%",
                  maxHeight: "min(58vh, 560px)",
                  margin: "0 auto",
                  borderRadius: "8px",
                  backgroundColor: "#000",
                }}
                autoPlay
              />
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setCurrentView("form");
                  setAvatarLoaded(false);
                  setVideoUrl("");
                  setJobId(null);
                  setJobStatus(null);
                }}
                style={{
                  padding: "12px 24px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  backgroundColor: "#fff",
                  color: "#333",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {i18n.t("Regenerate") || "Yeniden Oluştur"}
              </button>
              <button
                onClick={() => {
                  handleAddToScene(videoUrl, generatedVideoFileName);
                  // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
                  approveJob(jobId);
                  // Kaynak avatar bu oturumda AI ile üretildiyse onun job'u da tüketildi.
                  if (
                    aiResult?.jobId &&
                    selectedStockAvatar?.isAiGenerated &&
                    selectedStockAvatar?.videoUrl === aiResult.videoUrl
                  ) {
                    approveJob(aiResult.jobId);
                  }
                  setCurrentView("form");
                  setAvatarLoaded(false);
                  setVideoUrl("");
                  setJobId(null);
                  setJobStatus(null);
                }}
                style={{
                  padding: "12px 24px",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: "#5b21b6",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {i18n.t("AddToScene") || "Sahneye Ekle"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ── İşleniyor view'i (avatar videosu üretilirken tüm sayfayı kaplar) ──────
    // Eskiden sol altta küçük bir "yükleniyor" yazısı vardı; artık formun yerini
    // ortalanmış dönen bir circle + "Avatar video üretiliyor…" ekranı alıyor.
    if (processing || polling) {
      return (
        <div className="add-sound-modal__avatar-tab">
          <div className="asm-av__processing">
            <div className="asm-av__processing-spinner" aria-hidden="true" />
            <div className="asm-av__processing-title">
              {i18n.t("AvatarVideoGenerating") || "Avatar video üretiliyor…"}
            </div>
            <div className="asm-av__processing-sub">
              {i18n.t("AvatarVideoGeneratingHint") ||
                "This may take a few minutes. Please keep this window open."}
            </div>
          </div>
        </div>
      );
    }

    // ── Form view'i ─────────────────────────────────────────────────────────
    return (
      <div className="add-sound-modal__avatar-tab">
        <TalkingMethodSelector
          value={generationMethod}
          onChange={(method) => {
            setGenerationMethod(method);
            // Dudak senkronu yalnızca 1:1 / 16:9 biliyor (9:16 talking'e özel).
            if (
              method === "lipsync" &&
              !AVATAR_ASPECT_OPTIONS.some((o) => o.value === avatarAspectRatio)
            ) {
              setAvatarAspectRatio(AVATAR_ASPECT_RATIO);
            }
          }}
        />
        {/* ══════════ ADIM 1 — AVATAR ══════════ */}
        <div className="add-sound-modal__avatar-section asm-av__step">
          <div className="asm-av__step-head">
            <div className="asm-av__step-head-main">
              <span className="asm-av__step-badge">1</span>
              <div className="asm-av__step-titles">
                <h3 className="asm-av__step-title">{i18n.t("Avatar") || "Avatar"}</h3>
                <p className="asm-av__step-sub">
                  {i18n.t("AvatarStepSubtitle") || "Make an avatar to narrate your scene"}
                </p>
              </div>
            </div>
          </div>
          {/* ── Kaynak seçimi ─────────────────────────────────────────────────
              Biri seçilene kadar iki kart görünür; seçilince diğeri gizlenir ve
              kompakt bir "seçili kaynak + Değiştir" satırına iner (aşamalı akış). */}
          {!sourceChosen ? (
            <div className="add-sound-modal__avatar-choice-grid">
              <button
                type="button"
                className={`add-sound-modal__avatar-choice-card${
                  sourceChosen && selectedAvatarType === "stock"
                    ? " add-sound-modal__avatar-choice-card--active"
                    : ""
                }`}
                onClick={() => {
                  setSelectedAvatarType("stock");
                  setSourceChosen(true);
                  // Adım 2 (anlatım kaynağı) seçimsiz/kapalı başlasın.
                  setNarrationChosen(false);
                }}
              >
                <span className="add-sound-modal__avatar-choice-icon add-sound-modal__avatar-choice-icon--primary">
                  <LuUser />
                </span>
                <span className="add-sound-modal__avatar-choice-info">
                  <span className="add-sound-modal__avatar-choice-name">
                    {i18n.t("AideaAvatarLibrary") || "Aidea Avatar Library"}
                  </span>
                  <span className="add-sound-modal__avatar-choice-meta">
                    {i18n.t("AideaAvatarLibraryMeta") || "Ready presenters from Aidea & partners"}
                  </span>
                </span>
                <span
                  className={`asm-av__radio${
                    sourceChosen && selectedAvatarType === "stock" ? " asm-av__radio--on" : ""
                  }`}
                />
              </button>
              <button
                type="button"
                className={`add-sound-modal__avatar-choice-card${
                  sourceChosen && selectedAvatarType === "custom"
                    ? " add-sound-modal__avatar-choice-card--active"
                    : ""
                }`}
                onClick={() => {
                  setSelectedAvatarType("custom");
                  setSourceChosen(true);
                  // Custom avatar'da sahne sesi yok, metin moduna geç
                  setNarrationSource("text");
                  // Yeni avatar oluştururken default olarak "With AI" akışı açılsın
                  setSourceType("ai");
                  // Kimlik (ad/unvan/cinsiyet) dolu başlar → cinsiyet seçili sayılır
                  // ve "Üretim yöntemi" bölümü hemen seçilebilir. Yöntem/anlatım
                  // hâlâ kullanıcı seçene kadar kapalı.
                  setMethodChosen(false);
                  setGenderChosen(true);
                  setNarrationChosen(false);
                  // Kimlik alanları boşsa gender'a göre rastgele isim + title ön-doldur.
                  // Kullanıcı isterse üzerine yazar / shuffle ile yeniler.
                  if (!avatarName.trim() && !avatarRole.trim()) {
                    const id = randomAvatarIdentity(gender);
                    setAvatarName(id.name);
                    setAvatarRole(id.role);
                    nameEditedRef.current = false;
                  }
                  // AI moduna girilirken varsa aktif kameralar kapansın
                  handleStopCamera();
                  handleStopVideoCamera();
                }}
              >
                <span className="add-sound-modal__avatar-choice-icon add-sound-modal__avatar-choice-icon--primary">
                  +
                </span>
                <span className="add-sound-modal__avatar-choice-info">
                  <span className="add-sound-modal__avatar-choice-name">
                    {i18n.t("CreateYourOwn") || "Create your own"}
                  </span>
                  <span className="add-sound-modal__avatar-choice-meta">
                    {i18n.t("CreateYourOwnMeta") || "Generate with AI, or upload a video or photo"}
                  </span>
                </span>
                <span
                  className={`asm-av__radio${
                    sourceChosen && selectedAvatarType === "custom" ? " asm-av__radio--on" : ""
                  }`}
                />
              </button>
            </div>
          ) : (
            <div className="asm-av__source-collapsed">
              <span className="asm-av__source-collapsed-icon">
                {selectedAvatarType === "custom" ? <LuPlus /> : <LuUser />}
              </span>
              <span className="asm-av__source-collapsed-info">
                <span className="asm-av__source-collapsed-name">
                  {selectedAvatarType === "custom"
                    ? i18n.t("CreateYourOwn") || "Create your own"
                    : i18n.t("AideaAvatarLibrary") || "Aidea Avatar Library"}
                </span>
                <span className="asm-av__source-collapsed-meta">
                  {selectedAvatarType === "custom"
                    ? i18n.t("CreateYourOwnMeta") || "Generate with AI, or upload a video or photo"
                    : i18n.t("AideaAvatarLibraryMeta") || "Ready presenters from Aidea & partners"}
                </span>
              </span>
              <button
                type="button"
                className="asm-av__source-change"
                onClick={() => {
                  // Kaynağı değiştirince aşağıdaki tüm adımlar KAPALI/seçimsiz
                  // başlasın (kullanıcı yeniden adım adım seçer).
                  setSourceChosen(false);
                  setMethodChosen(false);
                  setGenderChosen(false);
                  setNarrationChosen(false);
                  setSelectedStockAvatar(null);
                }}
              >
                {i18n.t("AvatarSourceChange") || "Değiştir"}
              </button>
            </div>
          )}

          {/* ── Aidea Avatar Library galerisi (API'den) ───────────────────── */}
          {selectedAvatarType === "stock" && sourceChosen && (
            <div className="add-sound-modal__avatar-gallery asm-av__anim-in">
              {/* Kategori çipleri (avatar verisinden türetilir) + See all */}
              <div className="asm-av__chips-row">
                <div className="asm-av__chips">
                  <button
                    type="button"
                    className={`asm-av__chip${avatarFilter === "all" ? " asm-av__chip--on" : ""}`}
                    onClick={() => setAvatarFilter("all")}
                  >
                    {i18n.t("All") || "All"}
                  </button>
                  {avatarCategories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`asm-av__chip${
                        avatarFilter.toLowerCase() === cat.toLowerCase() ? " asm-av__chip--on" : ""
                      }`}
                      onClick={() => setAvatarFilter(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {isLoadingAvatars ? (
                <div className="add-sound-modal__avatar-loading">
                  {i18n.t("FileModal.APIProviders.AIAvatars.LoadingAvatars") ||
                    "Loading avatars..."}
                </div>
              ) : filteredAvatars.length === 0 ? (
                <div className="add-sound-modal__avatar-empty">
                  {i18n.t("FileModal.APIProviders.AIAvatars.NoAvatarsAvailable") ||
                    "No avatars available"}
                </div>
              ) : (
                <div className="add-sound-modal__avatar-gallery-grid asm-av__grid">
                  {(showAllAvatars ? filteredAvatars : filteredAvatars.slice(0, 8)).map(
                    (avatar) => {
                      const active = selectedStockAvatar?.id === avatar.id;
                      const role = avatarRoleLabel(avatar);
                      return (
                        <button
                          key={avatar.id}
                          type="button"
                          data-avatar-id={avatar.id}
                          className={`add-sound-modal__avatar-card asm-av__avatar${
                            active ? " add-sound-modal__avatar-card--active asm-av__avatar--on" : ""
                          }`}
                          onClick={() => {
                            // Sadece avatar karakterini değiştir. Seçili ses ve üretilmiş
                            // önizleme avatardan bağımsızdır; bu yüzden onları sıfırlamıyoruz
                            // (aksi halde avatar değişince alttaki önizleme sesi kayboluyordu).
                            setSelectedStockAvatar(avatar);
                          }}
                        >
                          {playingAvatarId === avatar.id && avatar.videoUrl ? (
                            // Aynı kare içinde avatarın DB videosunu oynat (videoUrl
                            // aidea avatarlarında dolu). stopPropagation → kartı seçmez.
                            <video
                              className="asm-av__avatar-video"
                              src={avatar.videoUrl}
                              // Video ilk kare hazır olana kadar thumbnail'i göster →
                              // oynatmaya başlarkenki ~1sn siyah flash gider.
                              poster={avatar.thumbnailUrl || undefined}
                              autoPlay
                              playsInline
                              onClick={(e) => e.stopPropagation()}
                              onEnded={() => setPlayingAvatarId(null)}
                            />
                          ) : (
                            <div
                              className="add-sound-modal__avatar-card-image asm-av__avatar-img"
                              style={{
                                backgroundImage: avatar.thumbnailUrl
                                  ? `url(${avatar.thumbnailUrl})`
                                  : undefined,
                                backgroundSize: "cover",
                                backgroundPosition: "center top",
                              }}
                            />
                          )}
                          {/* Sağ üst play/pause — sadece videosu olan avatarlarda */}
                          {avatar.videoUrl && (
                            <span
                              className="asm-av__avatar-play"
                              role="button"
                              tabIndex={0}
                              aria-label={i18n.t("AvatarPlaySample") || "Play sample"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPlayingAvatarId((prev) =>
                                  prev === avatar.id ? null : avatar.id
                                );
                              }}
                            >
                              {playingAvatarId === avatar.id ? (
                                <LuPause size={13} />
                              ) : (
                                <LuPlay size={13} />
                              )}
                            </span>
                          )}
                          {/* Sol üst çöp kutusu — yalnızca org'a ait kayıtlı avatarlarda,
                              kart hover/focus olunca görünür. stopPropagation → kartı seçmez. */}
                          {isDeletableAvatar(avatar) && (
                            <span
                              className="asm-av__avatar-delete"
                              role="button"
                              tabIndex={0}
                              aria-label={i18n.t("AvatarDelete.Button") || "Avatarı sil"}
                              title={i18n.t("AvatarDelete.Button") || "Avatarı sil"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAvatarToDelete(avatar);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAvatarToDelete(avatar);
                                }
                              }}
                            >
                              <LuTrash2 size={13} />
                            </span>
                          )}
                          {active && <span className="asm-av__avatar-check">✓</span>}
                          <div className="asm-av__avatar-overlay">
                            <div className="asm-av__avatar-name">{avatar.name}</div>
                            {role && <div className="asm-av__avatar-role">{role}</div>}
                          </div>
                        </button>
                      );
                    }
                  )}
                </div>
              )}

              {/* Daha fazla / daha az avatar — galerinin altında */}
              {!isLoadingAvatars && filteredAvatars.length > 8 && (
                <div className="asm-av__see-all-row">
                  <button
                    type="button"
                    className="asm-av__see-all"
                    onClick={() => setShowAllAvatars((prev) => !prev)}
                  >
                    {showAllAvatars
                      ? i18n.t("ShowLess") || "Show less"
                      : i18n.t("SeeAllCount", {
                          num: filteredAvatars.length,
                          defaultValue: "See all {{num}}",
                        })}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Custom avatar oluşturma ───────────────────────────────────── */}
          {selectedAvatarType === "custom" && sourceChosen && (
            <div className="add-sound-modal__avatar-create asm-av__anim-in">
              {/* A — Kimlik (rastgele isim + unvan; shuffle ile yenilenir) */}
              <div className="asm-av__subhead">
                <span className="asm-av__subhead-badge">A</span>
                {i18n.t("AvatarIdentity") || "Identity"}
                <button
                  type="button"
                  className="asm-av__shuffle"
                  title={i18n.t("AvatarShuffleIdentity") || "Rastgele isim & unvan"}
                  onClick={() => {
                    const id = randomAvatarIdentity(gender);
                    setAvatarName(id.name);
                    setAvatarRole(id.role);
                    nameEditedRef.current = false;
                  }}
                >
                  <LuShuffle size={13} />
                  {i18n.t("Shuffle") || "Rastgele"}
                </button>
              </div>
              <div className="add-sound-modal__avatar-create-grid asm-av__identity-grid">
                <div className="add-sound-modal__avatar-create-field">
                  <label htmlFor="avatar-name">{i18n.t("AvatarNameShort") || "Name"}</label>
                  <input
                    id="avatar-name"
                    type="text"
                    placeholder={i18n.t("AvatarNamePlaceholder") || 'e.g. "Kemal"'}
                    value={avatarName}
                    onChange={(e) => {
                      nameEditedRef.current = true;
                      setAvatarName(e.target.value);
                    }}
                  />
                </div>
                <div className="add-sound-modal__avatar-create-field">
                  <label htmlFor="avatar-role">{i18n.t("TitleRole") || "Title / Role"}</label>
                  <input
                    id="avatar-role"
                    type="text"
                    placeholder={i18n.t("TitleRolePlaceholder") || 'e.g. "CEO" or "Trainer"'}
                    value={avatarRole}
                    onChange={(e) => setAvatarRole(e.target.value)}
                  />
                </div>
                <div className="add-sound-modal__avatar-create-field">
                  <label>{i18n.t("Gender") || "Gender"}</label>
                  <div className="asm-av__gender">
                    <button
                      type="button"
                      className={`asm-av__gender-btn${
                        genderChosen && gender === "female" ? " asm-av__gender-btn--on" : ""
                      }`}
                      onClick={() => {
                        genderTouchedRef.current = true;
                        setGenderChosen(true);
                        setGender("female");
                        // İsim elle düzenlenmediyse yeni cinsiyete göre yeniden ata
                        if (!nameEditedRef.current) {
                          setAvatarName(randomAvatarIdentity("female").name);
                        }
                      }}
                    >
                      {i18n.t("Female") || "Female"}
                    </button>
                    <button
                      type="button"
                      className={`asm-av__gender-btn${
                        genderChosen && gender === "male" ? " asm-av__gender-btn--on" : ""
                      }`}
                      onClick={() => {
                        genderTouchedRef.current = true;
                        setGenderChosen(true);
                        setGender("male");
                        if (!nameEditedRef.current) {
                          setAvatarName(randomAvatarIdentity("male").name);
                        }
                      }}
                    >
                      {i18n.t("Male") || "Male"}
                    </button>
                  </div>
                </div>
              </div>

              {/* B — Üretim yöntemi (Prompt / Guide / Kendi medyan) — cinsiyet
                  seçilene kadar kilitli (aşamalı akış) */}
              <div className="asm-av__subhead">
                <span className="asm-av__subhead-badge">B</span>
                {i18n.t("GenerationMethod") || "Generation method"}
                {!genderChosen && (
                  <span className="asm-av__lock-hint">
                    {i18n.t("AvatarPickGenderHint") || "Önce cinsiyet seçin"}
                  </span>
                )}
              </div>
              <div
                className={`asm-av__method-grid${!genderChosen ? " asm-av__section--locked" : ""}`}
              >
                <button
                  type="button"
                  className={`asm-av__method${
                    methodChosen && sourceType === "ai" && aiMode === "prompt"
                      ? " asm-av__method--on"
                      : ""
                  }`}
                  onClick={() => {
                    setSourceType("ai");
                    setAiMode("prompt");
                    setMethodChosen(true);
                    handleStopCamera();
                    handleStopVideoCamera();
                  }}
                >
                  <span className="asm-av__method-icon">
                    <LuSparkles size={16} />
                  </span>
                  <span className="asm-av__method-title">
                    {i18n.t("GenerateWithPrompt") || "Generate with Prompt"}
                  </span>
                  <span className="asm-av__method-meta">
                    {i18n.t("GenerateWithPromptMeta") || "Describe your avatar free-form"}
                  </span>
                </button>
                <button
                  type="button"
                  className={`asm-av__method${
                    methodChosen && sourceType === "ai" && aiMode === "properties"
                      ? " asm-av__method--on"
                      : ""
                  }`}
                  onClick={() => {
                    setSourceType("ai");
                    setAiMode("properties");
                    setMethodChosen(true);
                    handleStopCamera();
                    handleStopVideoCamera();
                  }}
                >
                  <span className="asm-av__method-icon">≣</span>
                  <span className="asm-av__method-title">
                    {i18n.t("GenerateWithGuide") || "Generate with Guide"}
                  </span>
                  <span className="asm-av__method-meta">
                    {i18n.t("GenerateWithGuideMeta") || "Answer a few quick questions"}
                  </span>
                </button>
                <button
                  type="button"
                  className={`asm-av__method${
                    methodChosen && (sourceType === "video" || sourceType === "photo")
                      ? " asm-av__method--on"
                      : ""
                  }`}
                  onClick={() => {
                    setSourceType("video");
                    setMethodChosen(true);
                    handleStopCamera();
                  }}
                >
                  <span className="asm-av__method-icon">
                    <LuVideo size={16} />
                  </span>
                  <span className="asm-av__method-title">
                    {i18n.t("UseYourOwnMedia") || "Use your own media"}
                  </span>
                  <span className="asm-av__method-meta">
                    {i18n.t("UseYourOwnMediaMeta") || "Upload a video or a photo"}
                  </span>
                </button>
              </div>

              {/* Yöntem seçilene kadar alt panel + "Generate" gizli (aşamalı akış) */}
              {methodChosen && (
                <>
                  {/* Açıklama / üretim alanları */}
                  <div className="add-sound-modal__avatar-create-field asm-av__source asm-av__anim-in">
                    {/* Kendi medyan: video / fotoğraf alt seçimi */}
                    {(sourceType === "video" || sourceType === "photo") && (
                      <div className="asm-av__media-toggle">
                        <button
                          type="button"
                          className={`asm-av__media-btn${
                            sourceType === "video" ? " asm-av__media-btn--on" : ""
                          }`}
                          onClick={() => {
                            setSourceType("video");
                            handleStopCamera();
                          }}
                        >
                          <LuVideo size={14} />
                          {i18n.t("SourceTypeVideo") || "Video"}
                        </button>
                        <button
                          type="button"
                          className={`asm-av__media-btn${
                            sourceType === "photo" ? " asm-av__media-btn--on" : ""
                          }`}
                          onClick={() => {
                            setSourceType("photo");
                            handleStopVideoCamera();
                          }}
                        >
                          <LuImage size={14} />
                          {i18n.t("SourceTypePhoto") || "Photo"}
                        </button>
                      </div>
                    )}

                    {/* Video bölümü */}
                    {sourceType === "video" && (
                      <>
                        <div className="add-sound-modal__avatar-source-grid">
                          <label className="add-sound-modal__avatar-source-card">
                            <input
                              type="file"
                              accept=".mp4,.webm,.mov,.avi,.mkv,.flv,.wmv,.m4v,video/*"
                              onChange={handleVideoUpload}
                            />
                            <span className="add-sound-modal__avatar-source-title">
                              {uploadingVideo
                                ? i18n.t("Uploading") || "Uploading..."
                                : i18n.t("UploadVideo") || "Upload Video"}
                            </span>
                            <span className="add-sound-modal__avatar-source-meta">
                              {i18n.t("VideoFormats") || "MP4, MOV · 10s–60s"}
                            </span>
                          </label>
                          <button
                            type="button"
                            className="add-sound-modal__avatar-source-card add-sound-modal__avatar-source-card--record"
                            onClick={
                              showVideoCamera ? handleStopVideoCamera : handleStartVideoCamera
                            }
                          >
                            <span className="add-sound-modal__avatar-source-title">
                              {showVideoCamera
                                ? i18n.t("StopCamera") || "Stop Camera"
                                : i18n.t("RecordVideo") || "Record Video"}
                            </span>
                            <span className="add-sound-modal__avatar-source-meta">
                              {i18n.t("UseYourWebcam") || "Use your webcam"}
                            </span>
                          </button>
                        </div>

                        {/* Canlı kamera önizleme + kayıt kontrolleri */}
                        {showVideoCamera && (
                          <div style={{ marginTop: "10px" }}>
                            <video
                              ref={videoRecordRef}
                              style={{
                                width: "100%",
                                maxHeight: "400px",
                                borderRadius: "8px",
                                background: "#000",
                                display: "block",
                                objectFit: "contain",
                              }}
                              autoPlay
                              muted
                              playsInline
                            />
                            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                              {!isRecording ? (
                                <button
                                  type="button"
                                  onClick={handleStartRecording}
                                  style={{
                                    flex: 1,
                                    padding: "8px",
                                    background: "#ef4444",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                  }}
                                >
                                  ● {i18n.t("StartRecording") || "Start Recording"}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={handleStopRecording}
                                  style={{
                                    flex: 1,
                                    padding: "8px",
                                    background: "#64748b",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                  }}
                                >
                                  ■ {i18n.t("StopRecording") || "Stop Recording"}
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Video önizleme (yüklenen veya kaydedilen) */}
                        {uploadedVideoUrl && !showVideoCamera && (
                          <div style={{ marginTop: "10px" }}>
                            <video
                              src={uploadedVideoUrl}
                              controls
                              style={{
                                width: "100%",
                                maxHeight: "400px",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                display: "block",
                                objectFit: "contain",
                                backgroundColor: "#000",
                              }}
                            />
                          </div>
                        )}

                        {sourcePreview && (
                          <div className="add-sound-modal__avatar-source-selected">
                            <div className="add-sound-modal__avatar-source-file">
                              <span className="add-sound-modal__avatar-source-icon">🎬</span>
                              <div>
                                <div className="add-sound-modal__avatar-source-name">
                                  {sourcePreview}
                                </div>
                                <div className="add-sound-modal__avatar-source-size">
                                  {sourceSize}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="add-sound-modal__avatar-source-remove"
                              onClick={handleRemoveSource}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Fotoğraf bölümü */}
                    {sourceType === "photo" && (
                      <>
                        {/* Animate provider seçimi */}
                        <div style={{ marginBottom: "10px" }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#475569",
                              marginBottom: "6px",
                            }}
                          >
                            {i18n.t("AnimateProvider") || "Animate Provider"}
                          </label>
                          <div style={{ display: "flex", gap: "4px" }}>
                            {PHOTO_ANIMATE_PROVIDERS.map((p) => (
                              <button
                                key={p.value}
                                type="button"
                                onClick={() => setPhotoAnimateProvider(p.value)}
                                style={{
                                  flex: 1,
                                  padding: "6px 0",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  border: "1px solid",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  background:
                                    photoAnimateProvider === p.value ? "#3b82f6" : "#f1f5f9",
                                  color: photoAnimateProvider === p.value ? "#fff" : "#64748b",
                                  borderColor:
                                    photoAnimateProvider === p.value ? "#3b82f6" : "#e2e8f0",
                                }}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
                            {i18n.t("AnimateProviderHint") ||
                              "The selected provider will be used to animate the photo."}
                          </div>
                        </div>

                        <div className="add-sound-modal__avatar-source-grid">
                          <label className="add-sound-modal__avatar-source-card">
                            <input
                              ref={photoInputRef}
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={handlePhotoUpload}
                            />
                            <span className="add-sound-modal__avatar-source-title">
                              {uploadingPhoto
                                ? i18n.t("Uploading") || "Uploading..."
                                : i18n.t("UploadPhoto") || "Upload Photo"}
                            </span>
                            <span className="add-sound-modal__avatar-source-meta">
                              {i18n.t("PhotoFormats") || "JPG, PNG · Min 512px"}
                            </span>
                          </label>
                          <button
                            type="button"
                            className="add-sound-modal__avatar-source-card add-sound-modal__avatar-source-card--record"
                            onClick={showCamera ? handleStopCamera : handleStartCamera}
                          >
                            <span className="add-sound-modal__avatar-source-title">
                              {showCamera
                                ? i18n.t("StopCamera") || "Stop Camera"
                                : i18n.t("TakePhoto") || "Take Photo"}
                            </span>
                            <span className="add-sound-modal__avatar-source-meta">
                              {i18n.t("UseYourCamera") || "Use your camera"}
                            </span>
                          </button>
                        </div>

                        {showCamera && (
                          <div style={{ marginTop: "10px" }}>
                            <video
                              ref={cameraVideoRef}
                              style={{
                                width: "100%",
                                maxHeight: "400px",
                                borderRadius: "8px",
                                background: "#000",
                                display: "block",
                                objectFit: "contain",
                              }}
                              autoPlay
                              muted
                              playsInline
                            />
                            <button
                              type="button"
                              onClick={handleCapturePhoto}
                              style={{
                                marginTop: "8px",
                                width: "100%",
                                padding: "8px",
                                background: "#3b82f6",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: "600",
                              }}
                            >
                              {i18n.t("CapturePhoto") || "Capture Photo"}
                            </button>
                          </div>
                        )}

                        {uploadedPhotoUrl && !showCamera && (
                          <div style={{ marginTop: "10px" }}>
                            <img
                              src={uploadedPhotoUrl}
                              alt="preview"
                              style={{
                                width: "100%",
                                maxHeight: "400px",
                                objectFit: "contain",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#f8fafc",
                              }}
                            />
                          </div>
                        )}

                        {sourcePhotoPreview && (
                          <div className="add-sound-modal__avatar-source-selected">
                            <div className="add-sound-modal__avatar-source-file">
                              <span className="add-sound-modal__avatar-source-icon">🖼️</span>
                              <div>
                                <div className="add-sound-modal__avatar-source-name">
                                  {sourcePhotoPreview}
                                </div>
                                <div className="add-sound-modal__avatar-source-size">
                                  {sourcePhotoSize}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="add-sound-modal__avatar-source-remove"
                              onClick={handleRemovePhoto}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* AI ile avatar üretme bölümü ─────────────────────────────── */}
                    {sourceType === "ai" && aiResult && (
                      <div>
                        {/* Üretim sonucu önizleme — videoUrl response'dan gelir */}
                        <div
                          style={{
                            marginBottom: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1e293b",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <LuSparkles size={14} color="#7c3aed" />
                            {i18n.t("AvatarAiPreviewTitle") || "Generated avatar preview"}
                            <span
                              style={{
                                marginLeft: "auto",
                                fontSize: "10px",
                                fontWeight: "500",
                                color: "#64748b",
                                padding: "2px 6px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "4px",
                                textTransform: "lowercase",
                              }}
                            >
                              {aiResult.provider || aiProvider}
                            </span>
                          </div>
                          <video
                            src={aiResult.videoUrl}
                            controls
                            playsInline
                            style={{
                              width: "100%",
                              maxHeight: "360px",
                              borderRadius: "6px",
                              background: "#000",
                              display: "block",
                              objectFit: "contain",
                            }}
                          />
                          {aiResult.refinedPrompt && (
                            <details style={{ marginTop: "10px" }}>
                              <summary
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  color: "#64748b",
                                  cursor: "pointer",
                                  userSelect: "none",
                                }}
                              >
                                {i18n.t("AvatarAiRefinedPrompt") || "Refined prompt"}
                              </summary>
                              <div
                                style={{
                                  marginTop: "6px",
                                  padding: "8px 10px",
                                  fontSize: "12px",
                                  lineHeight: "1.5",
                                  color: "#475569",
                                  background: "#fff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "6px",
                                  whiteSpace: "pre-wrap",
                                  maxHeight: "180px",
                                  overflowY: "auto",
                                }}
                              >
                                {aiResult.refinedPrompt}
                              </div>
                            </details>
                          )}
                          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                            <button
                              type="button"
                              onClick={() => {
                                // Üretilen avatar zaten selectedStockAvatar'a yazıldı
                                // (aiResult effect'i) → Adım 2 (Ses & Anlatım) açık. Listeye
                                // dönmeden doğrudan Adım 2'ye kaydır.
                                narrationStepRef.current?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }}
                              style={{
                                flex: 1,
                                padding: "8px 12px",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#fff",
                                background: "#7c3aed",
                                border: "1px solid #7c3aed",
                                borderRadius: "6px",
                                cursor: "pointer",
                              }}
                            >
                              {i18n.t("AvatarAiUseThis") || "Use this avatar"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                // Önizlemeyi temizle + üretilen seçimi geri al → Adım 2 kilitlenir
                                setAiResult(null);
                                setSelectedStockAvatar(null);
                              }}
                              style={{
                                flex: 1,
                                padding: "8px 12px",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#64748b",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "6px",
                                cursor: "pointer",
                              }}
                            >
                              {i18n.t("AvatarAiGenerateAnother") || "Generate another"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI iş kuyruğa alındı — worker ilerlerken progress göster */}
                    {sourceType === "ai" && !aiResult && aiJobInfo && (
                      <div
                        style={{
                          marginBottom: "12px",
                          padding: "16px",
                          borderRadius: "8px",
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#1e293b",
                            marginBottom: "10px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <LuSparkles size={14} color="#7c3aed" />
                          {i18n.t("AvatarAiProcessing") || "Generating your AI avatar…"}
                          <span
                            style={{
                              marginLeft: "auto",
                              fontSize: "12px",
                              fontWeight: "500",
                              color: "#64748b",
                            }}
                          >
                            {aiJobInfo.progress}%
                          </span>
                        </div>
                        {/* Progress bar */}
                        <div
                          style={{
                            width: "100%",
                            height: "6px",
                            borderRadius: "4px",
                            background: "#e2e8f0",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.max(2, aiJobInfo.progress)}%`,
                              height: "100%",
                              background: "#7c3aed",
                              transition: "width 0.4s ease",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          {aiJobInfo.stage === "queued"
                            ? i18n.t("AvatarAiStageQueued") || "Queued…"
                            : aiJobInfo.stage === "prompt-refinement"
                              ? i18n.t("AvatarAiStagePromptRefine") || "Refining prompt…"
                              : aiJobInfo.stage === "generating-video"
                                ? i18n.t("AvatarAiStageGenerating") ||
                                  "Generating video (this may take a few minutes)…"
                                : aiJobInfo.stage === "finalizing"
                                  ? i18n.t("AvatarAiStageFinalizing") || "Finalizing…"
                                  : aiJobInfo.stage}
                        </div>
                      </div>
                    )}

                    {sourceType === "ai" && !aiResult && !aiJobInfo && (
                      <div className="asm-av__build">
                        {/* C — Avatarını tanımla. Sağlayıcı (engine) ve kategori artık
                        Step 2 → Advanced settings altında; mod (Prompt/Guide) ise
                        üstteki üretim yöntemi kartlarıyla seçiliyor. */}
                        <div className="asm-av__subhead">
                          <span className="asm-av__subhead-badge">C</span>
                          {aiMode === "properties"
                            ? i18n.t("BuildYourAvatar") || "Build your avatar"
                            : i18n.t("DescribeYourAvatar") || "Describe your avatar"}
                        </div>

                        {/* Görsel stil — çip olarak (hem prompt hem guide modunda geçerli) */}
                        <div
                          className="add-sound-modal__avatar-create-field"
                          style={{ marginBottom: "14px" }}
                        >
                          <label>{i18n.t("AvatarAiStyle") || "Style"}</label>
                          <div className="asm-av__chips">
                            {AI_STYLE_OPTIONS.map((o) => {
                              const on = aiStyle === o.value;
                              return (
                                <button
                                  key={o.value}
                                  type="button"
                                  className={`asm-av__chip${on ? " asm-av__chip--on" : ""}`}
                                  onClick={() => setAiStyle(o.value)}
                                >
                                  {on && <LuCheck className="asm-av__chip-check" size={13} />}
                                  {i18n.t(o.labelKey) || o.fallback}
                                </button>
                              );
                            })}
                            {/* Style için "+ Özel" — diğer çip grupları gibi. Seçilince enum
                            yerine aiCustom.style serbest metni payload'a style olarak gider. */}
                            <button
                              type="button"
                              className={`asm-av__chip asm-av__chip--add${
                                aiStyle === AI_OTHER_VALUE ? " asm-av__chip--on" : ""
                              }`}
                              onClick={() =>
                                setAiStyle(
                                  aiStyle === AI_OTHER_VALUE ? "realistic" : AI_OTHER_VALUE
                                )
                              }
                            >
                              <LuPlus size={13} />
                              {i18n.t("Custom") || "Custom"}
                            </button>
                          </div>
                          {aiStyle === AI_OTHER_VALUE &&
                            renderAiCustomInput("style", () => setAiStyle("realistic"))}
                        </div>

                        {/* Poz — top-level payload alanı, hem prompt hem guide modunda geçerli */}
                        <div
                          className="add-sound-modal__avatar-create-field"
                          style={{ marginBottom: "14px" }}
                        >
                          <label>{i18n.t("AvatarAiPose") || "Pose"}</label>
                          <div className="asm-av__chips">
                            {AI_POSE_OPTIONS.map((o) => {
                              const on = aiPose === o.value;
                              return (
                                <button
                                  key={o.value}
                                  type="button"
                                  className={`asm-av__chip${on ? " asm-av__chip--on" : ""}`}
                                  onClick={() => setAiPose(o.value)}
                                >
                                  {on && <LuCheck className="asm-av__chip-check" size={13} />}
                                  {i18n.t(o.labelKey) || o.fallback}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Prompt modu: etnik köken + cilt tonu + yüz özellikleri + serbest metin */}
                        {aiMode === "prompt" && (
                          <>
                            <div
                              className="add-sound-modal__avatar-create-grid asm-av__build-pair"
                              style={{ marginBottom: "14px" }}
                            >
                              <div className="add-sound-modal__avatar-create-field">
                                <label htmlFor="ai-appearance-ethnicity">
                                  {i18n.t("AvatarAiEthnicity") || "Ethnicity / heritage"}
                                </label>
                                <input
                                  id="ai-appearance-ethnicity"
                                  type="text"
                                  placeholder={
                                    i18n.t("AvatarAiEthnicityPlaceholder") ||
                                    'e.g. "Mediterranean", "East Asian"'
                                  }
                                  value={aiAppearance.ethnicity}
                                  onChange={(e) =>
                                    setAiAppearance((prev) => ({
                                      ...prev,
                                      ethnicity: e.target.value,
                                    }))
                                  }
                                  style={AI_INPUT_STYLE}
                                  onFocus={focusField}
                                  onBlur={blurField}
                                />
                              </div>
                              <div className="add-sound-modal__avatar-create-field">
                                <label>{i18n.t("AvatarAiSkinTone") || "Skin tone"}</label>
                                {renderAiSwatches({
                                  value: aiAppearance.skinTone,
                                  onChange: (v) =>
                                    setAiAppearance((prev) => ({ ...prev, skinTone: v })),
                                  options: AI_SKIN_TONE_OPTIONS,
                                  fieldKey: "skinTone",
                                })}
                              </div>
                            </div>
                            <div
                              className="add-sound-modal__avatar-create-field"
                              style={{ marginBottom: "14px" }}
                            >
                              <label htmlFor="ai-appearance-facial">
                                {i18n.t("AvatarAiFacialFeatures") || "Facial features"}
                              </label>
                              <input
                                id="ai-appearance-facial"
                                type="text"
                                placeholder={
                                  i18n.t("AvatarAiFacialFeaturesPlaceholder") ||
                                  'e.g. "oval face, almond eyes, straight nose"'
                                }
                                value={aiAppearance.facialFeatures}
                                onChange={(e) =>
                                  setAiAppearance((prev) => ({
                                    ...prev,
                                    facialFeatures: e.target.value,
                                  }))
                                }
                                style={AI_INPUT_STYLE}
                                onFocus={focusField}
                                onBlur={blurField}
                              />
                            </div>
                            <div className="add-sound-modal__avatar-create-field">
                              <label htmlFor="avatar-ai-prompt">
                                {i18n.t("AvatarAiPromptLabel") || "Describe your avatar"}
                              </label>
                              <textarea
                                id="avatar-ai-prompt"
                                rows={5}
                                value={aiPrompt}
                                onChange={(e) => setAiPrompt(e.target.value)}
                                placeholder={
                                  i18n.t("AvatarAiPromptPlaceholder") ||
                                  'e.g. "A 30-year-old Mediterranean woman, shoulder-length wavy brown hair, hazel eyes, friendly smile, wearing a navy business jacket, plain studio background."'
                                }
                                style={AI_TEXTAREA_STYLE}
                                onFocus={focusField}
                                onBlur={blurField}
                              />
                              {/* Öneri çipleri — prompt'a yönlendirici etiket ekler */}
                              <div className="asm-av__suggest">
                                <span className="asm-av__suggest-label">
                                  {i18n.t("Suggestions") || "Suggestions"}:
                                </span>
                                {[
                                  { k: "age", t: i18n.t("AvatarSuggestAge") || "age" },
                                  {
                                    k: "profession",
                                    t: i18n.t("AvatarSuggestProfession") || "profession",
                                  },
                                  {
                                    k: "hairColor",
                                    t: i18n.t("AvatarSuggestHairColor") || "hair color",
                                  },
                                  {
                                    k: "clothing",
                                    t: i18n.t("AvatarSuggestClothing") || "clothing",
                                  },
                                  {
                                    k: "background",
                                    t: i18n.t("AvatarSuggestBackground") || "background",
                                  },
                                  {
                                    k: "hands",
                                    t: i18n.t("AvatarSuggestHands") || "hands visible",
                                  },
                                ].map((s) => (
                                  <button
                                    key={s.k}
                                    type="button"
                                    className="asm-av__suggest-chip"
                                    onClick={() =>
                                      setAiPrompt((p) =>
                                        `${p}${p && !/\s$/.test(p) ? " " : ""}${s.t}: `.trimStart()
                                      )
                                    }
                                  >
                                    {s.t}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </>
                        )}

                        {/* Guide modu: yapılandırılmış çip / swatch alanları */}
                        {aiMode === "properties" && (
                          <>
                            {/* Yaş */}
                            <div
                              className="add-sound-modal__avatar-create-field"
                              style={{ marginBottom: "14px" }}
                            >
                              <label>{i18n.t("AvatarAiAgeRange") || "Age range"}</label>
                              {renderAiChips({
                                value: aiProperties.ageRange,
                                onChange: (v) =>
                                  setAiProperties((prev) => ({ ...prev, ageRange: v })),
                                options: AI_AGE_OPTIONS,
                                fieldKey: "ageRange",
                              })}
                            </div>

                            {/* Görünüm — solda etnik köken + saç tipi + ifade (üst üste),
                            sağda renkler (ten / saç / göz) tek sütunda üst üste yığılı.
                            İki bağımsız sütun: renkler sol taraftaki çiplerin yüksekliğinden
                            etkilenmeden alt alta sıkışık durur (saç rengi ortada). */}
                            <div
                              className="add-sound-modal__avatar-create-grid asm-av__build-pair"
                              style={{ marginBottom: "14px" }}
                            >
                              <div className="asm-av__build-col">
                                <div className="add-sound-modal__avatar-create-field">
                                  <label htmlFor="ai-appearance-ethnicity-g">
                                    {i18n.t("AvatarAiEthnicity") || "Ethnicity / heritage"}
                                  </label>
                                  <input
                                    id="ai-appearance-ethnicity-g"
                                    type="text"
                                    placeholder={
                                      i18n.t("AvatarAiEthnicityPlaceholder") ||
                                      'e.g. "Mediterranean", "East Asian"'
                                    }
                                    value={aiAppearance.ethnicity}
                                    onChange={(e) =>
                                      setAiAppearance((prev) => ({
                                        ...prev,
                                        ethnicity: e.target.value,
                                      }))
                                    }
                                    style={AI_INPUT_STYLE}
                                    onFocus={focusField}
                                    onBlur={blurField}
                                  />
                                </div>
                                <div className="add-sound-modal__avatar-create-field">
                                  <label>{i18n.t("AvatarAiHairStyle") || "Hair type"}</label>
                                  {renderAiChips({
                                    value: aiProperties.hairStyle,
                                    onChange: (v) =>
                                      setAiProperties((prev) => ({ ...prev, hairStyle: v })),
                                    options: AI_HAIR_STYLE_OPTIONS,
                                    fieldKey: "hairStyle",
                                  })}
                                </div>
                                <div className="add-sound-modal__avatar-create-field">
                                  <label>{i18n.t("AvatarAiExpression") || "Expression"}</label>
                                  {renderAiChips({
                                    value: aiProperties.expression,
                                    onChange: (v) =>
                                      setAiProperties((prev) => ({ ...prev, expression: v })),
                                    options: AI_EXPRESSION_OPTIONS,
                                    fieldKey: "expression",
                                  })}
                                </div>
                              </div>
                              <div className="asm-av__build-col asm-av__build-col--colors">
                                <div className="add-sound-modal__avatar-create-field">
                                  <label>{i18n.t("AvatarAiSkinTone") || "Skin tone"}</label>
                                  {renderAiSwatches({
                                    value: aiAppearance.skinTone,
                                    onChange: (v) =>
                                      setAiAppearance((prev) => ({ ...prev, skinTone: v })),
                                    options: AI_SKIN_TONE_OPTIONS,
                                    fieldKey: "skinTone",
                                  })}
                                </div>
                                <div className="add-sound-modal__avatar-create-field">
                                  <label>{i18n.t("AvatarAiHairColor") || "Hair color"}</label>
                                  {renderAiSwatches({
                                    value: aiProperties.hairColor,
                                    onChange: (v) =>
                                      setAiProperties((prev) => ({ ...prev, hairColor: v })),
                                    options: AI_HAIR_COLOR_OPTIONS,
                                    fieldKey: "hairColor",
                                  })}
                                </div>
                                <div className="add-sound-modal__avatar-create-field">
                                  <label>{i18n.t("AvatarAiEyeColor") || "Eye color"}</label>
                                  {renderAiSwatches({
                                    value: aiProperties.eyeColor,
                                    onChange: (v) =>
                                      setAiProperties((prev) => ({ ...prev, eyeColor: v })),
                                    options: AI_EYE_COLOR_OPTIONS,
                                    fieldKey: "eyeColor",
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Kıyafet */}
                            <div
                              className="add-sound-modal__avatar-create-field"
                              style={{ marginBottom: "14px" }}
                            >
                              <label>{i18n.t("AvatarAiClothing") || "Clothing"}</label>
                              {renderAiChips({
                                value: aiProperties.outfitStyle,
                                onChange: (v) =>
                                  setAiProperties((prev) => ({ ...prev, outfitStyle: v })),
                                options: AI_CLOTHING_OPTIONS,
                                fieldKey: "outfitStyle",
                              })}
                            </div>

                            {/* Arka plan */}
                            <div
                              className="add-sound-modal__avatar-create-field"
                              style={{ marginBottom: "14px" }}
                            >
                              <label>{i18n.t("AvatarAiBackground") || "Background"}</label>
                              {renderAiChips({
                                value: aiProperties.background,
                                onChange: (v) =>
                                  setAiProperties((prev) => ({ ...prev, background: v })),
                                options: AI_BACKGROUND_OPTIONS,
                                fieldKey: "background",
                              })}
                            </div>

                            {/* Aksesuarlar — çoklu seçim */}
                            <div
                              className="add-sound-modal__avatar-create-field"
                              style={{ marginBottom: "14px" }}
                            >
                              <label>
                                {i18n.t("AvatarAiAccessories") || "Accessories"}
                                <span className="asm-av__label-meta">
                                  {i18n.t("AvatarAiAccessoriesMeta") || "optional · pick any"}
                                </span>
                              </label>
                              {renderAiAccessoryChips()}
                            </div>

                            {/* Ek detaylar + öneri çipleri */}
                            <div className="add-sound-modal__avatar-create-field">
                              <label htmlFor="ai-prop-extra">
                                {i18n.t("AvatarAiAdditionalDetails") || "Additional details"}
                                <span className="asm-av__label-meta">
                                  {i18n.t("AvatarAiOptionalMeta") || "optional"}
                                </span>
                              </label>
                              <textarea
                                id="ai-prop-extra"
                                rows={3}
                                value={aiProperties.additionalDetails}
                                onChange={(e) =>
                                  setAiProperties((prev) => ({
                                    ...prev,
                                    additionalDetails: e.target.value,
                                  }))
                                }
                                placeholder={
                                  i18n.t("AvatarAiAdditionalDetailsPlaceholder") ||
                                  "Anything else worth describing (mood, lighting, props, vibe)…"
                                }
                                style={AI_TEXTAREA_STYLE}
                                onFocus={focusField}
                                onBlur={blurField}
                              />
                              <div className="asm-av__suggest">
                                <span className="asm-av__suggest-label">
                                  {i18n.t("Suggestions") || "Suggestions"}:
                                </span>
                                {[
                                  {
                                    k: "faceShape",
                                    t: i18n.t("AvatarSuggestFaceShape") || "face shape",
                                  },
                                  { k: "mood", t: i18n.t("AvatarSuggestMood") || "mood" },
                                  {
                                    k: "lighting",
                                    t: i18n.t("AvatarSuggestLighting") || "lighting",
                                  },
                                  {
                                    k: "hands",
                                    t: i18n.t("AvatarSuggestHands") || "hands visible",
                                  },
                                ].map((s) => (
                                  <button
                                    key={s.k}
                                    type="button"
                                    className="asm-av__suggest-chip"
                                    onClick={() =>
                                      setAiProperties((prev) => ({
                                        ...prev,
                                        additionalDetails: `${prev.additionalDetails}${
                                          prev.additionalDetails &&
                                          !/\s$/.test(prev.additionalDetails)
                                            ? " "
                                            : ""
                                        }${s.t}: `.trimStart(),
                                      }))
                                    }
                                  >
                                    {s.t}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* AI üretimi 202 + jobId döndükten sonra avatarCreating false oluyor;
                  iş hâlâ sürüyorsa (aiJobInfo var, sonuç yok) buton yine kilitli
                  kalmalı. personaBusy bunu kapsar. */}
                  {(() => {
                    const personaBusy =
                      avatarCreating || (sourceType === "ai" && !!aiJobInfo && !aiResult);
                    const formReady =
                      avatarName.trim() &&
                      avatarRole.trim() &&
                      ((sourceType === "video" && uploadedVideo) ||
                        (sourceType === "photo" && uploadedPhoto) ||
                        (sourceType === "ai" &&
                          ((aiMode === "prompt" && aiPrompt.trim()) ||
                            (aiMode === "properties" &&
                              (aiAccessories.some((v) => v !== AI_OTHER_VALUE) ||
                                Object.entries(aiProperties).some(([k, v]) => {
                                  const r = resolveAiField(v, aiCustom[k]);
                                  return r && r.trim();
                                }))))));
                    return (
                      <button
                        type="button"
                        className={`add-sound-modal__avatar-save asm-av__persona-btn ${
                          !personaBusy && formReady ? "add-sound-modal__avatar-save--active" : ""
                        }`}
                        onClick={handleSaveAvatar}
                        disabled={personaBusy || !formReady}
                      >
                        {personaBusy
                          ? sourceType === "photo"
                            ? i18n.t("AvatarCreatingPhoto") || "Animating…"
                            : sourceType === "ai"
                              ? i18n.t("AvatarCreatingAi") || "Generating…"
                              : i18n.t("AvatarCreatingVideo") || "Creating…"
                          : sourceType === "ai"
                            ? i18n.t("GeneratePersonaPreview") || "Generate persona preview"
                            : i18n.t("SaveAndUseAvatar") || "Save & Use Avatar"}
                      </button>
                    );
                  })()}
                  <div className="add-sound-modal__avatar-hint">
                    {i18n.t("AvatarCreationHint") ||
                      "Face must be clearly visible with good lighting. Saved avatars are reusable across all projects."}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ══════════ ADIM 2 — SES & ANLATIM ══════════ */}
        {isTalking ? (
          <TalkingVideoPanel
            ref={talkingPanelRef}
            stepRef={narrationStepRef}
            locked={!talkingSource.ready && !talkingSource.pending}
            navId={navItemSelected}
            sceneIndex={(navItemsIds?.indexOf(navItemSelected) ?? -1) + 1}
            source={talkingSource}
            text={narrationText}
            onTextChange={setNarrationText}
            soundList={soundList}
            loadSceneNarrationText={loadSceneNarrationText}
            transparentBg={transparentBg}
            onTransparentBgChange={setTransparentBg}
            aspectRatio={avatarAspectRatio}
            onAspectRatioChange={setAvatarAspectRatio}
            voiceProfile={voiceProfile}
            onStatusChange={handleTalkingStatus}
            onInsufficientBalance={checkInsufficientBalance}
            testVideoUrl={talkingTestVideoUrl || undefined}
            avatarGender={
              selectedStockAvatar?.gender || (talkingSource.kind === "ai" ? gender : undefined)
            }
          />
        ) : (
          <div
            ref={narrationStepRef}
            className={`add-sound-modal__narration asm-av__step${
              !avatarReady ? " asm-av__step--locked" : ""
            }`}
          >
            <div className="asm-av__step-head">
              <div className="asm-av__step-head-main">
                <span className="asm-av__step-badge">2</span>
                <div className="asm-av__step-titles">
                  <h3 className="asm-av__step-title">
                    {i18n.t("VoiceAndNarration") || "Voice & narration"}
                  </h3>
                  <p className="asm-av__step-sub">
                    {!avatarReady
                      ? i18n.t("AvatarSelectFirstHint") || "Önce bir avatar seçin veya oluşturun"
                      : i18n.t("VoiceAndNarrationSub") ||
                        "What the avatar says, and how it sounds."}
                  </p>
                </div>
              </div>
            </div>

            {/* Anlatım kaynağı: Sahne sesi / Yeni ses üret — kart olarak.
              Hem "Aidea kütüphanesi" (stock) hem "Kendin oluştur" (custom) modunda
              gösterilir. Azure sahne sesini desteklemediği için "Sahne sesini kullan"
              kartı yalnızca Azure dışındaki modellerde görünür. */}
            {(selectedAvatarType === "stock" || selectedAvatarType === "custom") && (
              <div className="add-sound-modal__avatar-choice-grid">
                {/* Azure sahne sesini desteklemiyor → bu kart Azure'da gizli. */}
                {selectedModel !== "azure-video" && (
                  <button
                    type="button"
                    className={`add-sound-modal__avatar-choice-card${
                      narrationChosen && narrationSource === "sceneAudio"
                        ? " add-sound-modal__avatar-choice-card--active"
                        : ""
                    }`}
                    onClick={() => {
                      setNarrationSource("sceneAudio");
                      setNarrationChosen(true);
                    }}
                  >
                    <span className="add-sound-modal__avatar-choice-icon add-sound-modal__avatar-choice-icon--primary">
                      <LuUser />
                    </span>
                    <span className="add-sound-modal__avatar-choice-info">
                      <span className="add-sound-modal__avatar-choice-name">
                        {i18n.t("UseSceneAudio") || "Use scene audio"}
                      </span>
                      <span className="add-sound-modal__avatar-choice-meta">
                        {i18n.t("UseSceneAudioMeta") || "Narrate with this scene's existing audio"}
                      </span>
                    </span>
                    <span
                      className={`asm-av__radio${
                        narrationChosen && narrationSource === "sceneAudio"
                          ? " asm-av__radio--on"
                          : ""
                      }`}
                    />
                  </button>
                )}
                <button
                  type="button"
                  className={`add-sound-modal__avatar-choice-card${
                    narrationChosen && narrationSource === "text"
                      ? " add-sound-modal__avatar-choice-card--active"
                      : ""
                  }`}
                  onClick={() => {
                    setNarrationSource("text");
                    setNarrationChosen(true);
                  }}
                >
                  <span className="add-sound-modal__avatar-choice-icon add-sound-modal__avatar-choice-icon--primary">
                    <LuSparkles />
                  </span>
                  <span className="add-sound-modal__avatar-choice-info">
                    <span className="add-sound-modal__avatar-choice-name">
                      {i18n.t("GenerateNewVoice") || "Generate new voice"}
                    </span>
                    <span className="add-sound-modal__avatar-choice-meta">
                      {i18n.t("GenerateNewVoiceMeta") || "Pick an AI voice to read the narration"}
                    </span>
                  </span>
                  <span
                    className={`asm-av__radio${
                      narrationChosen && narrationSource === "text" ? " asm-av__radio--on" : ""
                    }`}
                  />
                </button>
              </div>
            )}

            {/* Metin girişi (sadece kullanıcı "Yeni ses üret"i seçince) */}
            {narrationChosen && narrationSource === "text" && (
              <div className="asm-av__reveal">
                {/* 1) Ses seçimi — AIVoiceTab'daki voice-profile yapısı: seçili anlatıcıyı gösteren
                  satır + "Anlatıcı Seç / Değiştir" ile açılıp kapanan panel. Panelin içinde
                  Sağlayıcı + Dil + Cinsiyet filtreleri ve dinlenebilir (sample) ses kartları.
                  Hem stock (Aidea kütüphanesi) hem custom (kendin oluştur) için gösterilir;
                  Azure kendi ses dropdown'larını kullanır. */}
                {(selectedModel === "aidea" || selectedModel === "tavus") && (
                  <div className="add-sound-modal__voice-profile" style={{ marginTop: "16px" }}>
                    <div className="add-sound-modal__voice-row">
                      {/* Seçili anlatıcı avatarı — renk cinsiyete göre (kadın pembe,
                        erkek mavi, bilinmiyor mor). Eskiden hep pembeydi. */}
                      <div
                        className="add-sound-modal__voice-avatar"
                        style={genderAvatarStyle(ttsSelectedVoice?.gender)}
                      >
                        {ttsSelectedVoice?.name ? ttsSelectedVoice.name[0].toUpperCase() : "?"}
                      </div>
                      <div className="add-sound-modal__voice-details">
                        <div className="add-sound-modal__voice-title-row">
                          <span className="add-sound-modal__voice-name">
                            {ttsSelectedVoice?.name || "-"}
                          </span>
                          {ttsProvider && (
                            <span className="add-sound-modal__voice-pill">
                              {ttsProviderLabel(ttsProvider, ttsElevenLabsModel)}
                            </span>
                          )}
                          {ttsSelectedVoice?.gender && (
                            <span className="add-sound-modal__voice-pill">
                              {normalizeGender(ttsSelectedVoice.gender) === "male"
                                ? i18n.t("Male") || "Erkek"
                                : i18n.t("Female") || "Kadın"}
                            </span>
                          )}
                          {ttsSelectedVoice?.language && (
                            <span className="add-sound-modal__voice-pill">
                              {ttsSelectedVoice.language}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Üretilen ses önizlemesini buradan dinle. Bu örnek (sample) ses
                        DEĞİL; aşağıdaki "Ses önizlemesi üret" ile metin seçili sesle
                        seslendirildiğinde (previewUrl) aktifleşir. Önizleme üretilmeden
                        dinlenemez. Aşağıdaki mini oynatıcıyla aynı state'i paylaşır. */}
                      {ttsSelectedVoice && (
                        <button
                          type="button"
                          className="add-sound-modal__voice-card-play"
                          title={i18n.t("ListenPreview") || "Listen to the generated preview"}
                          aria-label={i18n.t("ListenPreview") || "Listen to the generated preview"}
                          onClick={handlePreviewPlayPause}
                          disabled={!previewUrl}
                        >
                          {previewPlaying ? <LuPause /> : <LuPlay />}
                        </button>
                      )}
                      <button
                        type="button"
                        className="add-sound-modal__voice-action"
                        onClick={() => setVoiceSelectOpen((prev) => !prev)}
                      >
                        {ttsSelectedVoice?.name
                          ? i18n.t("ChangeVoice") || "Change voice"
                          : i18n.t("SelectVoice") || "Select voice"}
                        <span className="add-sound-modal__voice-action-icon">
                          {voiceSelectOpen ? <LuChevronUp /> : <LuChevronDown />}
                        </span>
                      </button>
                    </div>
                    {voiceSelectOpen && (
                      <div>
                        {/* Sağlayıcı + (Azure'da) Dil + Cinsiyet filtreleri — eski
                          dropdown'lar yerine çip/pill butonları (mockup tasarımı). */}
                        <div className="add-sound-modal__voice-filters">
                          <div className="add-sound-modal__voice-filter">
                            <div className="add-sound-modal__voice-filter-label">
                              {i18n.t("Provider") || "Provider"}
                            </div>
                            <div className="add-sound-modal__voice-filter-chips">
                              {[
                                { value: "", label: i18n.t("AllProviders") || "All" },
                                // ElevenLabs iki ayrı chip: model (2.5 / 3) seçimi buradan yapılır.
                                { value: "elevenlabs", model: "v2_5", label: "ElevenLabs 2.5" },
                                { value: "elevenlabs", model: "v3", label: "ElevenLabs 3" },
                                { value: "gemini", label: i18n.t("Google") || "Google" },
                                { value: "azure", label: i18n.t("Azure") || "Azure" },
                              ].map((p) => (
                                <button
                                  key={p.model || p.value || "all"}
                                  type="button"
                                  className={`add-sound-modal__voice-filter-chip add-sound-modal__voice-filter-chip--provider${
                                    ttsProvider === p.value &&
                                    (!p.model || ttsElevenLabsModel === p.model)
                                      ? " add-sound-modal__voice-filter-chip--on"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    // 2.5 ↔ 3 arası geçişte ses listesi aynı kalır; sadece model değişir.
                                    if (!p.model || ttsProvider !== p.value) {
                                      handleTtsProviderChange(p.value);
                                    }
                                    if (p.model) setTtsElevenLabsModel(p.model);
                                  }}
                                >
                                  {p.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Dil filtresi: ElevenLabs (multilingual) ve Google (gemini)
                              hariç gösterilir. */}
                          {ttsProvider &&
                            ttsProvider !== "elevenlabs" &&
                            ttsProvider !== "gemini" && (
                              <div className="add-sound-modal__voice-filter">
                                <div className="add-sound-modal__voice-filter-label">
                                  {i18n.t("language") || "Language"}
                                </div>
                                <div className="add-sound-modal__voice-select-input">
                                  <select
                                    value={ttsLanguage}
                                    onChange={(e) => {
                                      setTtsLanguage(e.target.value);
                                      setTtsCurrentPage(1);
                                      if (ttsProvider) {
                                        loadCourseVoices(
                                          ttsProvider,
                                          e.target.value,
                                          ttsGender,
                                          1,
                                          ""
                                        );
                                      }
                                    }}
                                    disabled={!ttsProvider}
                                  >
                                    <option value="">
                                      {i18n.t("SelectLanguage") || "Bir dil seçiniz"}
                                    </option>
                                    {getTtsLanguageOptions(ttsProvider).map((lang) => (
                                      <option key={lang.value} value={lang.value}>
                                        {lang.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}

                          <div className="add-sound-modal__voice-filter add-sound-modal__voice-filter--gender">
                            <div className="add-sound-modal__voice-filter-label">
                              {i18n.t("Gender") || "Gender"}
                            </div>
                            <div className="add-sound-modal__voice-filter-chips">
                              {[
                                { value: "", label: i18n.t("AllGenders") || "All" },
                                { value: "female", label: i18n.t("Female") || "Female" },
                                { value: "male", label: i18n.t("Male") || "Male" },
                              ].map((g) => (
                                <button
                                  key={g.value || "all"}
                                  type="button"
                                  className={`add-sound-modal__voice-filter-chip${
                                    ttsGender === g.value
                                      ? " add-sound-modal__voice-filter-chip--on"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    setTtsGender(g.value);
                                    setTtsCurrentPage(1);
                                    // "Tümü" (All) dahil her durumda cinsiyeti gönder
                                    // (ttsProvider "" ise service paramı eklenmez).
                                    loadCourseVoices(ttsProvider, ttsLanguage, g.value, 1, "");
                                  }}
                                >
                                  {g.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {(ttsProvider || isLoadingTTSVoices || ttsVoices.length > 0) && (
                          <div className="add-sound-modal__voice-list">
                            {isLoadingTTSVoices ? (
                              <div className="add-sound-modal__voice-empty">
                                {i18n.t("Loading") || "Loading..."}
                              </div>
                            ) : ttsVoices.length === 0 ? (
                              <div className="add-sound-modal__voice-empty">
                                {i18n.t("NoVoicesFound") || "No voices found"}
                              </div>
                            ) : (
                              <>
                                {ttsVoices.map((voice) => {
                                  const availableLanguages = voice.sampleUrlsByLanguage
                                    ? Object.keys(voice.sampleUrlsByLanguage)
                                    : [];
                                  const hasSample =
                                    availableLanguages.length > 0 ||
                                    voice.sampleUrl ||
                                    voice.audioSampleUrl ||
                                    voice.previewUrl;
                                  const isActive = ttsSelectedVoice?.id === voice.id;
                                  return (
                                    <div
                                      key={voice.id}
                                      className="add-sound-modal__voice-card-wrapper"
                                    >
                                      <div
                                        className={`add-sound-modal__voice-card${
                                          isActive ? " add-sound-modal__voice-card--active" : ""
                                        }`}
                                        onClick={() => {
                                          setTtsSelectedVoice(voice);
                                          // "Tümü" (All) filtresindeyken provider boş; üretim
                                          // için seçilen sesin gerçek sağlayıcısını set et.
                                          if (!ttsProvider) {
                                            const prov = voiceToProvider(voice);
                                            if (prov) setTtsProvider(prov);
                                          }
                                          if (voice.language) {
                                            setTtsLanguage(normalizeLanguageCode(voice.language));
                                          }
                                          if (voice.gender) {
                                            setTtsGender(normalizeGender(voice.gender));
                                          }
                                        }}
                                        style={{ cursor: "pointer" }}
                                      >
                                        {/* Sample oynat butonu — cinsiyete göre renkli (kadın
                                          pembe, erkek mavi, bilinmiyor mor). */}
                                        <button
                                          type="button"
                                          className="add-sound-modal__voice-card-play"
                                          style={genderAvatarStyle(voice.gender)}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTtsSamplePlay(voice);
                                          }}
                                          disabled={!hasSample}
                                        >
                                          {ttsSamplePlayingId === voice.id ? (
                                            <LuPause />
                                          ) : (
                                            <LuPlay />
                                          )}
                                        </button>
                                        <div className="add-sound-modal__voice-card-body">
                                          <div className="add-sound-modal__voice-card-name">
                                            {voice.name}
                                          </div>
                                          <div className="add-sound-modal__voice-card-meta">
                                            {voice.gender && (
                                              <span>
                                                {normalizeGender(voice.gender) === "male"
                                                  ? i18n.t("Male") || "Erkek"
                                                  : i18n.t("Female") || "Kadın"}
                                              </span>
                                            )}
                                            {voice.servicename && <span>{voice.servicename}</span>}
                                          </div>
                                        </div>
                                        {isActive && (
                                          <span className="add-sound-modal__voice-card-check">
                                            <LuCheck size={14} />
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="add-sound-modal__voice-list-footer">
                                  <span className="add-sound-modal__voice-count">
                                    {ttsTotalVoices != null
                                      ? `${ttsVoices.length} ${i18n.t("Of") || "of"} ${ttsTotalVoices} ${i18n.t("Voices") || "voices"}`
                                      : `${ttsVoices.length} ${i18n.t("Voices") || "voices"}`}
                                    {ttsProvider &&
                                      ` · ${ttsProviderLabel(ttsProvider, ttsElevenLabsModel)}`}
                                  </span>
                                  {ttsTotalPages > 1 && (
                                    <div className="add-sound-modal__voice-pagination">
                                      {Array.from({ length: ttsTotalPages }, (_, i) => i + 1).map(
                                        (page) => (
                                          <button
                                            key={page}
                                            type="button"
                                            className={`add-sound-modal__voice-pagination-btn${
                                              ttsCurrentPage === page
                                                ? " add-sound-modal__voice-pagination-btn--active"
                                                : ""
                                            }`}
                                            onClick={() => {
                                              setTtsCurrentPage(page);
                                              loadCourseVoices(
                                                ttsProvider,
                                                ttsLanguage,
                                                ttsGender,
                                                page,
                                                ""
                                              );
                                            }}
                                          >
                                            {page}
                                          </button>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* 2) Anlatım metni (AIVoiceTab'daki Voice Text gibi). */}
                <div
                  className="add-sound-modal__narration-header"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <label className="add-sound-modal__narration-label" style={{ margin: 0 }}>
                      {i18n.t("NarrationText") || "Narration Text"} <span>*</span>
                    </label>
                    {SHOW_AUDIO_TAGS_BUTTON && (
                      <button
                        type="button"
                        onClick={() => setShowSSML(!showSSML)}
                        style={{
                          padding: "3px 8px",
                          fontSize: "10px",
                          fontWeight: "600",
                          border: "1px solid #e2e8f0",
                          borderRadius: "4px",
                          background: showSSML ? "#7c3aed" : "#f8fafc",
                          color: showSSML ? "#fff" : "#64748b",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        {i18n.t("AudioTags") || "Audio tags"}
                      </button>
                    )}
                  </div>
                  {/* "Sahne Metnini Getir" — same control the infographic-scene
                    modal puts above its narration box.

                    It used to hide itself as soon as the field had any text,
                    which meant that after the autofill ran (or the user typed a
                    word) there was no way to pull the scene's text again. It
                    stays put now and simply overwrites, which is what a button
                    with this label is expected to do. */}
                  <button
                    type="button"
                    className="add-sound-modal__narration-use"
                    disabled={sceneTextLoading}
                    // Keeps this modal's own pill styling (.narration-use) rather
                    // than copying the infographic modal's bare text link — it
                    // sits next to the "Audio tags" pill and should match it.
                    style={{
                      opacity: sceneTextLoading ? 0.6 : 1,
                      cursor: sceneTextLoading ? "default" : "pointer",
                    }}
                    onClick={async () => {
                      setSceneTextLoading(true);
                      try {
                        const text = await loadSceneNarrationText();
                        if (text) {
                          setNarrationText(text);
                        } else {
                          onServerModal?.(
                            i18n.t("error.warning"),
                            "<span>" +
                              // i18next returns the key itself for a missing
                              // entry, so `t(...) || fallback` never falls back.
                              i18n.t("NoNarrationTextFoundInScene", {
                                defaultValue: "Bu sahnede anlatım metni bulunamadı.",
                              }) +
                              "</span>",
                            false
                          );
                        }
                      } finally {
                        setSceneTextLoading(false);
                      }
                    }}
                  >
                    {sceneTextLoading
                      ? i18n.t("Loading") || "Loading..."
                      : i18n.t("FetchSceneText") || "Fetch scene text"}
                  </button>
                </div>
                <textarea
                  className="add-sound-modal__narration-textarea"
                  placeholder={
                    i18n.t("EnterTextToBeSpokenByAvatar") ||
                    "Enter the text to be spoken by the avatar..."
                  }
                  rows={4}
                  value={displayedText}
                  onChange={(e) => setNarrationText(e.target.value)}
                />
                {/* Karakter sayacı kaldırıldı (üst sınır yok). Tavus min-45 kuralı
                  zaten "Avatar Oluştur" butonunu pasif yaparak (canCreate) uygulanıyor. */}

                {/* Ses önizlemesi üret — gerçek TTS API'sine gider. Üretim bitince buton
                  yerini oynat/ilerleme çubuklu mini oynatıcıya bırakır.
                  Azure kendi ses dropdown'larını kullandığından (ttsSelectedVoice yok)
                  bu bölüm Azure'da gizlenir. */}
                {selectedModel !== "azure-video" && (
                  <div className="asm-av__preview-row">
                    {previewUrl ? (
                      <>
                        {/* Hidden audio element (önizleme) */}
                        <audio
                          ref={previewAudioRef}
                          src={previewUrl}
                          onTimeUpdate={handlePreviewTimeUpdate}
                          onLoadedMetadata={handlePreviewLoadedMetadata}
                          onEnded={handlePreviewEnded}
                          style={{ display: "none" }}
                        />
                        <div className="add-sound-modal__ai-voice-player asm-av__preview-player">
                          <button
                            type="button"
                            className="add-sound-modal__ai-voice-play"
                            onClick={handlePreviewPlayPause}
                          >
                            {previewPlaying ? <LuPause /> : <LuPlay />}
                          </button>
                          <div
                            className="add-sound-modal__ai-voice-bar"
                            onClick={handlePreviewBarClick}
                            style={{ cursor: "pointer" }}
                          >
                            <span
                              className="add-sound-modal__ai-voice-bar-fill"
                              style={{ width: `${previewProgress}%` }}
                            />
                          </div>
                          <span className="add-sound-modal__ai-voice-time">
                            {formatTime(previewCurrentTime)} / {formatTime(previewDuration)}
                          </span>
                          {/* "5 sn üret" önizlemesi yalnızca dinleme amaçlı olduğundan
                            sağına temizleme (×) butonu koyuyoruz. */}
                          {previewMode === "short" && (
                            <button
                              type="button"
                              className="asm-av__preview-clear"
                              onClick={handleClearPreview}
                              title={i18n.t("DeletePreview") || "Delete preview"}
                            >
                              <LuX size={14} />
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="asm-av__preview-menu-wrap" ref={previewMenuRef}>
                        <button
                          type="button"
                          className="asm-av__preview-btn"
                          disabled={
                            !ttsSelectedVoice?.id || !narrationText.trim() || previewGenerating
                          }
                          onClick={() => setPreviewMenuOpen((open) => !open)}
                        >
                          <LuSparkles size={14} />
                          {previewGenerating
                            ? i18n.t("GeneratingVoicePreview") || "Generating…"
                            : i18n.t("GenerateVoicePreview") || "Generate voice preview"}
                          <LuChevronDown size={14} />
                        </button>
                        {previewMenuOpen && !previewGenerating && (
                          <div className="asm-av__preview-menu">
                            <button
                              type="button"
                              className="asm-av__preview-menu-item"
                              onClick={() => handleGeneratePreview("full")}
                            >
                              {i18n.t("GenerateVoicePreviewFull") || "Generate full"}
                            </button>
                            <button
                              type="button"
                              className="asm-av__preview-menu-item"
                              onClick={() => handleGeneratePreview("short")}
                            >
                              {i18n.t("GenerateVoicePreviewShort") || "Generate 5s preview"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="asm-av__preview-hint">
                      {i18n.t("GenerateVoicePreviewHint") ||
                        "Reads the narration text with the selected voice"}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sahne sesi önizleme (sadece kullanıcı "Sahne sesini kullan"ı seçince) */}
            {narrationChosen && narrationSource === "sceneAudio" && (
              <div className="asm-av__reveal" style={{ marginTop: "12px" }}>
                {soundList && soundList.length > 0 && selectedSceneAudio ? (
                  <div>
                    {/* Seslendirilen metin (varsa) — SADECE gösterim (read-only). */}
                    {sceneAudioResolvedText && (
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "6px",
                          }}
                        >
                          <label
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              margin: 0,
                            }}
                          >
                            {i18n.t("NarrationText") || "Seslendirilen Metin:"}
                          </label>
                          {SHOW_AUDIO_TAGS_BUTTON && (
                            <button
                              type="button"
                              onClick={() => setShowSSML(!showSSML)}
                              style={{
                                padding: "3px 8px",
                                fontSize: "10px",
                                fontWeight: "600",
                                border: "1px solid #e2e8f0",
                                borderRadius: "4px",
                                background: showSSML ? "#7c3aed" : "#f8fafc",
                                color: showSSML ? "#fff" : "#64748b",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                            >
                              {i18n.t("AudioTags") || "Audio tags"}
                            </button>
                          )}
                          {/* "Metni Düzenle" — sahne sesinin metnini + sesini önceden
                            doldurarak "Yeni ses üret / Sahne sesi düzenle" moduna
                            geçirir; kullanıcı metni düzenleyip yeni ses üretebilir. */}
                          <button
                            type="button"
                            className="add-sound-modal__narration-use"
                            style={{ marginLeft: "auto" }}
                            onClick={() => {
                              if (sceneAudioResolvedText) setNarrationText(sceneAudioResolvedText);
                              setNarrationSource("text");
                              setNarrationChosen(true);
                            }}
                          >
                            {i18n.t("EditText") || "Metni Düzenle"}
                          </button>
                        </div>
                        <div
                          style={{
                            padding: "12px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            color: "#333",
                            lineHeight: "1.6",
                            maxHeight: "200px",
                            overflowY: "auto",
                            wordWrap: "break-word",
                            whiteSpace: "pre-wrap",
                            overflowWrap: "break-word",
                          }}
                        >
                          {showSSML ? sceneAudioResolvedText : stripSSML(sceneAudioResolvedText)}
                        </div>
                      </div>
                    )}

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        {i18n.t("Preview") || "Ön izleme:"}
                        {/* Sahne sesinin anlatıcı adı + sağlayıcısı (voiceData'dan). */}
                        {selectedSceneAudio?.voiceData?.voiceName && (
                          <span style={{ marginLeft: "8px", fontWeight: 400, color: "#64748b" }}>
                            {selectedSceneAudio.voiceData.voiceName}
                            {selectedSceneAudio.voiceData.provider && (
                              <>
                                {" · "}
                                {ttsProviderLabel(
                                  normalizeTtsProvider(selectedSceneAudio.voiceData.provider),
                                  selectedSceneAudio.voiceData.elevenLabsModel
                                ) || selectedSceneAudio.voiceData.provider}
                              </>
                            )}
                          </span>
                        )}
                      </label>
                      {/* Hidden audio element */}
                      <audio
                        ref={audioRef}
                        src={selectedSceneAudio.file}
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleLoadedMetadata}
                        onEnded={handleAudioEnded}
                        style={{ display: "none" }}
                      />
                      <div className="add-sound-modal__ai-voice-player">
                        <button
                          type="button"
                          className="add-sound-modal__ai-voice-play"
                          onClick={handlePlayPause}
                        >
                          {isPlaying ? <LuPause /> : <LuPlay />}
                        </button>
                        <div
                          className="add-sound-modal__ai-voice-bar"
                          onClick={handleProgressBarClick}
                          style={{ cursor: "pointer" }}
                        >
                          <span
                            className="add-sound-modal__ai-voice-bar-fill"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="add-sound-modal__ai-voice-time">
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: "#999", fontSize: "13px" }}>
                    {i18n.t("NoAudioFoundInScene") || "Bu sahnede ses bulunamadı."}
                  </p>
                )}
              </div>
            )}
            <div className="add-sound-modal__narration-toggle">
              <div>
                <div className="add-sound-modal__narration-toggle-title">
                  {i18n.t("TransparentBackground") || "Transparent Background"}
                </div>
                <div className="add-sound-modal__narration-toggle-sub">
                  {i18n.t("RemoveAvatarBackground") || "Remove avatar background"}
                </div>
              </div>
              <button
                type="button"
                className={`add-sound-modal__narration-switch${
                  transparentBg ? " add-sound-modal__narration-switch--active" : ""
                }`}
                onClick={() => setTransparentBg((prev) => !prev)}
              >
                <span />
              </button>
            </div>

            {/* ── Advanced settings (lipsync / engine / azure ses / keep ses bilgisi) ── */}
            <div className="asm-av__advanced">
              <button
                type="button"
                className="asm-av__advanced-head"
                onClick={() => setAdvancedOpen((p) => !p)}
              >
                <span className="asm-av__advanced-title">
                  {i18n.t("AdvancedSettings") || "Advanced settings"}
                </span>
                <span className="asm-av__advanced-summary">
                  {selectedAvatarType === "custom" && sourceType === "ai" && (
                    <span className="asm-av__advanced-chip">
                      {i18n.t("Engine") || "Engine"} ·{" "}
                      {i18n.t(AI_PROVIDER_OPTIONS.find((o) => o.value === aiProvider)?.labelKey) ||
                        AI_PROVIDER_OPTIONS.find((o) => o.value === aiProvider)?.fallback ||
                        aiProvider}
                    </span>
                  )}
                  <span className="asm-av__advanced-chip">
                    {i18n.t("LipSync") || "Lip sync"} ·{" "}
                    {lipsyncMode === "auto" ? i18n.t("LipsyncAuto") || "Auto" : lipsyncProvider}
                  </span>
                  <span className="asm-av__advanced-chip">
                    {i18n.t("AvatarAspectRatio") || "En/Boy Oranı"} · {avatarAspectRatio}
                  </span>
                  <span className="asm-av__advanced-caret">
                    {advancedOpen ? <LuChevronUp /> : <LuChevronDown />}
                  </span>
                </span>
              </button>

              {advancedOpen && (
                <div className="asm-av__advanced-body">
                  {/* En/boy oranı — tüm avatar tipleri (azure / tavus / aidea) için.
                    "1:1" backend'de kareye kırpar, "16:9" native yatay oranı korur. */}
                  <div className="add-sound-modal__avatar-select-input">
                    <label className="add-sound-modal__avatar-select-label">
                      {i18n.t("AvatarAspectRatio") || "En/Boy Oranı"}
                    </label>
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {AVATAR_ASPECT_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => setAvatarAspectRatio(o.value)}
                          style={{
                            padding: "4px 10px",
                            fontSize: "11px",
                            fontWeight: "600",
                            lineHeight: "1.4",
                            border: "1px solid",
                            borderRadius: "6px",
                            cursor: "pointer",
                            background: avatarAspectRatio === o.value ? "#7c3aed" : "#f1f5f9",
                            color: avatarAspectRatio === o.value ? "#fff" : "#64748b",
                            borderColor: avatarAspectRatio === o.value ? "#7c3aed" : "#e2e8f0",
                          }}
                        >
                          {i18n.t(o.labelKey) || o.fallback}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Engine (AI sağlayıcı) + Kategori — yalnızca custom + AI üretim */}
                  {selectedAvatarType === "custom" && sourceType === "ai" && (
                    <div className="asm-av__advanced-grid">
                      <div className="add-sound-modal__avatar-select-input">
                        <label className="add-sound-modal__avatar-select-label">
                          {i18n.t("Engine") || "Engine"}
                        </label>
                        <select value={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
                          {AI_PROVIDER_OPTIONS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {i18n.t(p.labelKey) || p.fallback}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="add-sound-modal__avatar-select-input">
                        <label className="add-sound-modal__avatar-select-label">
                          {i18n.t("AvatarAiCategory") || "Category"}
                        </label>
                        <select
                          value={avatarCategory}
                          onChange={(e) => setAvatarCategory(e.target.value)}
                        >
                          {AI_CATEGORY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {i18n.t(o.labelKey) || o.fallback}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Aidea: Lipsync engine seçimi (Otomatik / Özel + sağlayıcı) */}
                  {selectedAvatarType === "stock" && selectedModel === "aidea" && (
                    <div className="add-sound-modal__avatar-select-input">
                      <label className="add-sound-modal__avatar-select-label">
                        {i18n.t("LipsyncProvider") || "Lipsync Provider"}
                      </label>
                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                        {[
                          { value: "auto", label: i18n.t("LipsyncAuto") || "Otomatik" },
                          { value: "custom", label: i18n.t("LipsyncCustom") || "Özel" },
                        ].map((m) => (
                          <button
                            key={m.value}
                            type="button"
                            onClick={() => setLipsyncMode(m.value)}
                            style={{
                              padding: "4px 10px",
                              fontSize: "11px",
                              fontWeight: "600",
                              lineHeight: "1.4",
                              border: "1px solid",
                              borderRadius: "6px",
                              cursor: "pointer",
                              background: lipsyncMode === m.value ? "#7c3aed" : "#f1f5f9",
                              color: lipsyncMode === m.value ? "#fff" : "#64748b",
                              borderColor: lipsyncMode === m.value ? "#7c3aed" : "#e2e8f0",
                            }}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                      {lipsyncMode === "custom" && (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                            marginTop: "6px",
                          }}
                        >
                          {LIPSYNC_PROVIDERS.map((p) => (
                            <button
                              key={p.value}
                              type="button"
                              onClick={() => setLipsyncProvider(p.value)}
                              style={{
                                padding: "4px 10px",
                                fontSize: "11px",
                                fontWeight: "600",
                                lineHeight: "1.4",
                                border: "1px solid",
                                borderRadius: "6px",
                                cursor: "pointer",
                                background: lipsyncProvider === p.value ? "#7c3aed" : "#f1f5f9",
                                color: lipsyncProvider === p.value ? "#fff" : "#64748b",
                                borderColor: lipsyncProvider === p.value ? "#7c3aed" : "#e2e8f0",
                              }}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Azure: dil + ses (avatarın cinsiyetine göre filtrelenmiş) */}
                  {selectedAvatarType === "stock" &&
                    selectedStockAvatar &&
                    selectedModel === "azure-video" && (
                      <div className="add-sound-modal__avatar-voice-settings">
                        <div className="add-sound-modal__avatar-select-input">
                          <label
                            className="add-sound-modal__avatar-select-label"
                            htmlFor="avatar-lang"
                          >
                            {i18n.t("AiLanguage") || "Language"}
                          </label>
                          <select
                            id="avatar-lang"
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                          >
                            <option value="tr-TR">Turkish (tr-TR)</option>
                            <option value="en-US">English (en-US)</option>
                          </select>
                        </div>
                        <div className="add-sound-modal__avatar-select-input">
                          <label
                            className="add-sound-modal__avatar-select-label"
                            htmlFor="avatar-voice"
                          >
                            {i18n.t("AiSound") || "Voice"}
                          </label>
                          {isLoadingVoices ? (
                            <div className="add-sound-modal__avatar-loading">
                              {i18n.t("FileModal.APIProviders.AIAvatars.LoadingVoices") ||
                                "Loading voices..."}
                            </div>
                          ) : (
                            <select
                              id="avatar-voice"
                              value={selectedVoiceId}
                              onChange={(e) => setSelectedVoiceId(e.target.value)}
                            >
                              {voices.length === 0 && (
                                <option value="">
                                  {i18n.t("FileModal.APIProviders.AIAvatars.NoVoicesAvailable") ||
                                    "No voices available"}
                                </option>
                              )}
                              {voices.map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Sahne sesi (keep) modunda aktif anlatıcı bilgisi — sadece gösterim */}
                  {selectedAvatarType === "stock" &&
                    selectedVoice === "keep" &&
                    (selectedModel === "aidea" || selectedModel === "tavus") && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {[
                          {
                            label: i18n.t("VoiceProvider") || "Sağlayıcı",
                            value: isLoadingTTSVoices
                              ? "…"
                              : ttsProvider === "elevenlabs"
                                ? ttsProviderLabel(ttsProvider, ttsElevenLabsModel)
                                : {
                                    azure: "Azure",
                                    gemini: "Google",
                                    google: "Google",
                                  }[ttsProvider] ||
                                  ttsProvider ||
                                  "—",
                          },
                          {
                            label: i18n.t("Voice") || "Ses",
                            value: isLoadingTTSVoices ? "…" : ttsSelectedVoice?.name || "—",
                          },
                        ].map((item) => (
                          <div
                            key={item.label}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                              flex: "1 1 140px",
                              padding: "8px 12px",
                              background: "#ffffff",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.03em",
                                color: "#9ca3af",
                              }}
                            >
                              {item.label}
                            </span>
                            <span style={{ fontSize: "13px", fontWeight: 600, color: "#1f2937" }}>
                              {item.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </div>

            {/* ── İş durumu ───────────────────────────────────────────────── */}
            {(processing || polling) && (
              <div className="add-sound-modal__narration-status">
                {jobStatus
                  ? i18n.t("FileModal.APIProviders.AIAvatars.JobStatus")
                  : i18n.t("FileModal.APIProviders.AIAvatars.PleaseWait")}
              </div>
            )}
          </div>
        )}

        <DeleteAvatarConfirmModal
          show={!!avatarToDelete}
          onClose={() => setAvatarToDelete(null)}
          onConfirm={confirmDeleteAvatar}
          avatarName={avatarToDelete?.name}
          busy={isDeletingAvatar}
        />
      </div>
    );
  }
);

AvatarNarrationTab.propTypes = {
  onElementSelected: PropTypes.func,
  onServerModal: PropTypes.func,
  navItemSelected: PropTypes.string,
  voiceProfile: PropTypes.object,
  soundList: PropTypes.array,
  boxes: PropTypes.object,
  navItems: PropTypes.object,
  containedViews: PropTypes.object,
  containedViewSelected: PropTypes.string,
  boxSelected: PropTypes.any,
  pluginToolbars: PropTypes.object,
  onBoxAdded: PropTypes.func,
  onClose: PropTypes.func,
  onViewChange: PropTypes.func,
  onProcessingChange: PropTypes.func,
  onRenderSecondsChange: PropTypes.func,
  onCanCreateChange: PropTypes.func,
  onCreditCheckReady: PropTypes.func,
  onNavItemSelected: PropTypes.func,
};

AvatarNarrationTab.displayName = "AvatarNarrationTab";

export default AvatarNarrationTab;
